import * as React from 'react';
import React__default, { createContext, useContext, useMemo, useEffect } from 'react';
import { useIntl, useFeatureToggles, useApiConfig, Currencies } from '@grandvisionhq/state';
import * as swcHelpers from '@swc/helpers';
import { gql, makeVar, useLazyQuery, useMutation, useReactiveVar } from '@grandvisionhq/graphql';
import { getAttrValue, isNumber, isString, isValidImages, getFrontImage, isPlainObject, getLocalizedValue, getLocalizedValues, DateUtils, isBoolean, getLocalizedAttrValue, isArray, useLocalStorage, useEventListener, isNotNull, isNil, formatPrice, isDefined, isFunction } from '@grandvisionhq/utils';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { Types, toast, Button, Modal, Price, MediaQuery, LineItemDeleteButton, QuantitySelector, LineItem, LineItemMedia, LineItemContent, Alert, IconButton, ProgressBar, Accordion, AccordionItem, LineItemRow, LineItemGlassesPrescription, LineItemBundleRow, Loader as Loader$1, Util, CartCounter as CartCounter$1, StickyCallToAction, LineItemAside, RadioButton, ProductTag, PriceTag } from '@grandvisionhq/common';
import { DataLayer, calcPrice as calcPrice$1, CartV2Actions } from '@grandvisionhq/analytics';
import { Text, Heading, Divider } from '@grandvisionhq/elements';
import classnames from 'classnames';
import { DeliveryIndicator } from '@grandvisionhq/shipping';
import { StockIndicator, Helpers } from '@grandvisionhq/inventory';
import { UspGroupItem } from '@grandvisionhq/content';
import { createSlotsContext } from '@grandvisionhq/slots';
import { Icon } from '@grandvisionhq/icons';
import { DiscountCodeForm } from '@grandvisionhq/forms';
import { ApplePayExpressCheckoutButtonContainer } from '@grandvisionhq/payments';
import { useLink } from '@grandvisionhq/frk-routing';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useApiClient, Products } from '@grandvisionhq/api-client';

const CartContext = React.createContext(null);
CartContext.displayName = 'CartContext';
const useCart = ()=>{
    const context = React.useContext(CartContext);
    if (!context) {
        throw new Error('No Cart available. You might still need to wrap the application with the CartProvider component.');
    }
    return context;
};

const CartSettingsContext = createContext(null);
CartSettingsContext.displayName = 'CartSettings';
const useCartSettings = ()=>{
    const context = useContext(CartSettingsContext);
    if (context === null) {
        throw new Error('Missing CartSettings provider. You have to wrap the application with the CartSettingsProvider component from the @grandvisionhq/carts-v2 package.');
    }
    return context;
};

const defaultSettings = {
    allowBackOrder: true,
    hideStockAndDeliveryOnLineItem: false,
    multiDiscountType: false,
    showSubTotal: true,
    showTotalDiscount: true,
    showCartDiscountDescriptions: true,
    isDiscountCodeEnabled: false
};
const CartSettingsProvider = ({ children , settings  })=>{
    const [cartId, setCartId] = React.useState(settings.cartId);
    const [cartHash, setCartHash] = React.useState(settings.cartHash);
    const setContext = React.useCallback(({ cartHash: newCartHash , cartId: newCartId  })=>{
        newCartId && setCartId(newCartId);
        newCartHash && setCartHash(newCartHash);
    }, []);
    const value = React.useMemo(()=>swcHelpers.objectSpread({}, defaultSettings, settings, {
            cartHash,
            cartId,
            setContext
        })
    , [
        cartHash,
        cartId,
        setContext,
        settings
    ]);
    return /*#__PURE__*/ React.createElement(CartSettingsContext.Provider, {
        value: value
    }, children);
};

const address = gql`
  fragment AddressFragment on CartAddress {
    additionalStreetInfo
    apartment
    building
    city
    company
    countryCode
    department
    email
    externalId
    firstName
    key
    lastName
    mobile
    phone
    pOBox
    postalCode
    region
    salutation
    streetName
    streetNumber
    title
  }
`;

const discount = gql`
  fragment DiscountFragment on Discount {
    id
    isActive
    key
    name {
      locale
      value
      __typename
    }
    description {
      locale
      value
    }
    type
    value {
      centAmount
      percentage
      scale
      type
      __typename
    }
    validFrom
    validUntil
    __typename
  }
`;

const legacyProduct = gql`
  fragment LegacyProductFragment on LegacyProduct {
    id
    sku
    productType
    masterData {
      current {
        variant {
          availability {
            channels {
              key
              value {
                isOnStock
                availableQuantity
              }
            }
          }
          attributes {
            name
            value
          }
          images {
            url
          }
          media {
            images {
              url
              label
            }
          }
          sku
        }
        name {
          value
          locale
        }
        slug {
          value
          locale
        }
      }
    }
  }
`;

const lineItemPrice = gql`
  fragment LineItemPriceFragment on LineItemPrice {
    total {
      discount {
        amount
        percentage
        __typename
      }
      gross
      net
      subtotal
      tax {
        amount
        portions {
          amount
          rate
          __typename
        }
        __typename
      }
      __typename
    }
    unit {
      discount {
        amount
        percentage
        __typename
      }
      gross
      net
      subtotal
      tax {
        amount
        portions {
          amount
          rate
          __typename
        }
        __typename
      }
      __typename
    }
    __typename
  }
`;

const lineItemBundle = gql`
  fragment LineItemBundleFragment on LineItemBundle {
    id
    sku
    lineItemType
    bundleLineItemsType
    bundleLineItems {
      sku
      type
      isMainBundleLineItem
      quantity
      discounts {
        ...DiscountFragment
      }
      price {
        ...LineItemPriceFragment
      }
      legacyProduct {
        ...LegacyProductFragment
      }
    }
  }
  ${legacyProduct}
  ${lineItemPrice}
  ${discount}
`;

const contactLensBundleBeneluxTypeDetails = gql`
  fragment ContactLensBundleBeneluxTypeDetailsFragment on ContactLensBundleBeneluxTypeDetails {
    prescription {
      rightLens {
        baseCurve
        diameter
        power
        axis
        cylinder
      }
      leftLens {
        baseCurve
        diameter
        power
        axis
        cylinder
      }
    }
  }
`;

const contactLensSubscriptionTypeDetails = gql`
  fragment ContactLensSubscriptionTypeDetailsFragment on ContactLensSubscriptionTypeDetails {
    billingFrequency
    deliveryFrequency
    prescriptionForSubscription {
      leftLens {
        axis
        baseCurve
        cylinder
        diameter
        power
      }
      rightLens {
        axis
        baseCurve
        cylinder
        diameter
        power
      }
    }
    subscriptionPromotion {
      code
      localizedTexts {
        locale
        value
      }
    }
  }
`;

const contactLensesTypeDetails = gql`
  fragment ContactLensesTypeDetailsFragment on ContactLensesTypeDetails {
    eyeSide
    __typename
  }
`;

const eyePrescription = gql`
  fragment EyePrescriptionFragment on SingleEyePrescription {
    baseCurve
    diameter
    power
    axis
    cylinder
    addition
    pupillaryDistance
  }
`;

const prescriptionGlassesTypeDetails = gql`
  fragment PrescriptionGlassesTypeDetailsFragment on BundlePrescriptionGlassesTypeDetails {
    newPrescription {
      leftLens {
        ...EyePrescriptionFragment
      }
      rightLens {
        ...EyePrescriptionFragment
      }
      opticianDetails {
        type
        opticianName
        postalCode
        city
        country
        address
        streetName
        additionalStreetInfo
        additionalAddressInfo
        region
      }
      expiryDate
    }
    prescriptionFile {
      fileName
      storageId
      __typename
    }
    pupillaryDistanceFile {
      fileName
      storageId
      __typename
    }
    isEmailPrescription
    isExistingCustomer
  }
  ${eyePrescription}
`;

const cartDetails = gql`
  fragment CartDetailsFragment on Cart {
    __typename
    id
    checkoutState
    customerDetails {
      email
      phone
      customerType
      dateOfBirth
    }
    invoiceDetails {
      certifiedEmail
      vatId
      certifiedRecipientCode
    }
    shippingAddress {
      ...AddressFragment
    }
    shippingMethod {
      id
      key
      freeAbove
    }
    billingAddress {
      ...AddressFragment
    }
    appliedDiscounts {
      name {
        locale
        value
        __typename
      }
      code
      requiresDiscountCode
      __typename
    }
    numberOfItems
    price {
      subtotal
      discount {
        amount
        percentage
        __typename
      }
      net
      gross
      tax {
        amount
        portions {
          rate
          amount
          __typename
        }
        __typename
      }
      shippingPrice
      paymentMethodPrice
      subscriptionPlanPrice {
        __typename
        invoicedAmount
        total
      }
      __typename
    }
    vouchers {
      code
      isActive
      name
      state
      __typename
    }
    subscriptionPlan {
      billingDate
      __typename
    }
    isAccountRequired
    additionalCartCostLineItems {
      id
      sku
      quantity
    }
    lineItems {
      id
      lineItemType
      typeDetails {
        ... on ContactLensBundleBeneluxTypeDetails {
          ...ContactLensBundleBeneluxTypeDetailsFragment
        }
        ... on ContactLensSubscriptionTypeDetails {
          ...ContactLensSubscriptionTypeDetailsFragment
        }
        ... on ContactLensesTypeDetails {
          ...ContactLensesTypeDetailsFragment
        }
        ... on BundlePrescriptionGlassesTypeDetails {
          ...PrescriptionGlassesTypeDetailsFragment
        }
      }
      isGift
      sku
      quantity
      discounts {
        ...DiscountFragment
      }
      price {
        ...LineItemPriceFragment
      }
      __typename
      ... on LineItemRegular {
        legacyProduct {
          ...LegacyProductFragment
          __typename
        }
        __typename
      }
      ... on LineItemContactLenses {
        legacyProduct {
          ...LegacyProductFragment
          __typename
        }
        __typename
      }
      ... on LineItemBundle {
        ...LineItemBundleFragment
      }
    }
  }
  ${address}
  ${contactLensBundleBeneluxTypeDetails}
  ${contactLensSubscriptionTypeDetails}
  ${contactLensesTypeDetails}
  ${prescriptionGlassesTypeDetails}
  ${legacyProduct}
  ${lineItemBundle}
  ${lineItemPrice}
  ${discount}
`;

const cartValidationErrors = gql`
  fragment CartValidationErrorsFragment on CartValidationErrors {
    cartErrors {
      defaultMessage
      validationCode
      passed
    }
    cartLineItemErrors {
      errors {
        defaultMessage
        validationCode

        passed
        ... on SolvableValidationError {
          isSolvable
          actionItem {
            lineItemId
            actioneeSKU
            action
          }
        }
      }
      itemId
    }
  }
`;

var index$7 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  cartDetails: cartDetails,
  cartValidationErrors: cartValidationErrors
});

const ADD_CART_LINE_ITEMS = gql`
  mutation AddCartLineItems($cartInput: AddCartLineItemsInput!) {
    addCartLineItems(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const ADD_CART_VOUCHER_CODES = gql`
  mutation AddCartVoucherCodes($cartInput: CartVoucherCodesInput!) {
    addCartVoucherCodes(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const CREATE_CART = gql`
  mutation CreateCart($cartInput: CreateCartInput!) {
    createCart(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
        prescriptionSignatures
      }
      cartHash
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const REMOVE_CART_VOUCHER_CODES = gql`
  mutation RemoveCartVoucherCodes($cartInput: CartVoucherCodesInput!) {
    removeCartVoucherCodes(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const REMOVE_CART_LINE_ITEMS = gql`
  mutation RemoveCartLineItems($cartInput: RemoveCartLineItemsInput!) {
    removeCartLineItems(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const UPDATE_CART_LINE_ITEMS = gql`
  mutation UpdateCartLineItems($cartInput: UpdateCartLineItemsInput!) {
    updateCartLineItems(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM = gql`
  mutation ReplaceContactLensBundleBeneluxCartLineItem(
    $cartInput: ReplaceCartContactLensBundleBeneluxLineItemInput!
  ) {
    replaceContactLensBundleBeneluxCartLineItem(cartInput: $cartInput) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
      success
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ADD_CART_LINE_ITEMS: ADD_CART_LINE_ITEMS,
  ADD_CART_VOUCHER_CODES: ADD_CART_VOUCHER_CODES,
  CREATE_CART: CREATE_CART,
  REMOVE_CART_VOUCHER_CODES: REMOVE_CART_VOUCHER_CODES,
  REMOVE_CART_LINE_ITEMS: REMOVE_CART_LINE_ITEMS,
  UPDATE_CART_LINE_ITEMS: UPDATE_CART_LINE_ITEMS,
  REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM: REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM
});

const GET_CART = gql`
  query GetCart($id: ID!) {
    getCart(id: $id) {
      cart {
        ...CartDetailsFragment
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
    }
  }
  ${cartDetails}
  ${cartValidationErrors}
`;

const GET_TOTAL_LINE_ITEMS = gql`
  query GetTotalLineItems($id: ID!) {
    getCart(id: $id) {
      cart {
        id
        numberOfItems
      }
      validationErrors {
        ...CartValidationErrorsFragment
      }
    }
  }
  ${cartValidationErrors}
`;

var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  GET_CART: GET_CART,
  GET_TOTAL_LINE_ITEMS: GET_TOTAL_LINE_ITEMS
});

var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Fragments: index$7,
  Mutations: index$6,
  Queries: index$5
});

const appendSanitizationItems = (lineItems, sanitizationItems)=>{
    return lineItems.map((lineItem)=>{
        let updatedLineItem = lineItem;
        sanitizationItems.forEach((item, i)=>{
            if (lineItem.sku === item.actioneeSKU) {
                updatedLineItem = swcHelpers.objectSpread({}, updatedLineItem, {
                    sanitizations: [
                        item
                    ]
                });
                sanitizationItems.splice(i, 1);
            }
        });
        return updatedLineItem;
    });
};

const cartCountVar = makeVar(0);

const cartIdVar = makeVar(null);

const flatten = (array)=>[].concat(...array)
;
function extractSkuQuantityPairsFromLineItems(lineItems) {
    // Flatten the result array as we can potentially receive [item, item, [bundleItem, bundleItem]]
    return flatten(lineItems.map((lineItem)=>{
        if ('sku' in lineItem) {
            return [
                {
                    sku: lineItem.sku,
                    quantity: lineItem.quantity
                }, 
            ];
        }
        // If the lineItem has no SKU, assume it's an aggregated (prescription) product
        return [
            lineItem.frame,
            lineItem.lensType,
            lineItem.lensPackage,
            lineItem.addOn
        ].map((item)=>{
            return {
                sku: (item === null || item === void 0 ? void 0 : item.sku) || '',
                quantity: 1
            };
        });
    }));
}

const getActiveVoucherCodes = (vouchers)=>{
    if (!vouchers) {
        return;
    }
    return vouchers.filter((voucher)=>voucher.isActive
    ).map((voucher)=>voucher.code
    );
};

const areSameContactLenses = (serverLineItem, draftLineItem)=>{
    var ref, ref1, ref2;
    if (draftLineItem.type !== 'contact-lenses' || ((ref = serverLineItem.typeDetails) === null || ref === void 0 ? void 0 : ref.__typename) !== 'ContactLensesTypeDetails') {
        return false;
    }
    // A Contact Lens is only considered the same product when the eyeside matches as well
    return serverLineItem.sku === draftLineItem.sku && ((ref1 = serverLineItem.typeDetails) === null || ref1 === void 0 ? void 0 : ref1.eyeSide) === ((ref2 = draftLineItem.typeDetails.eyeSide) === null || ref2 === void 0 ? void 0 : ref2.toUpperCase());
};

/**
 * Only returns true when both set a and b match exactly
 */ const isExactMatch = (a, b)=>{
    const diff = new Set(a);
    for (const elem of b){
        if (diff.has(elem)) {
            diff.delete(elem);
        } else {
            return false;
        }
    }
    return diff.size <= 0;
};
const isMissing = (value)=>typeof value === 'undefined' || value === null || typeof value === 'object' && Object.getOwnPropertyNames(value).length < 1
;
/**
 * Server and client have a different way of expressing a missing value. With
 * this util we can easily catch this situation.
 */ function areBothMissing(server, client) {
    return isMissing(server) && isMissing(client);
}

const areSameNonPrescriptionBundleLineItem = (serverLineItem, draftLineItem)=>{
    var ref;
    if (serverLineItem.__typename !== 'LineItemBundle') {
        return false;
    }
    if (((ref = serverLineItem.typeDetails) === null || ref === void 0 ? void 0 : ref.__typename) !== 'BundleNonPrescriptionBundleTypeDetails') {
        return false;
    }
    const serverSkus = new Set(serverLineItem.bundleLineItems.map((bl)=>bl.legacyProduct.sku
    ));
    const draftSkus = new Set([
        draftLineItem.sku,
        ...draftLineItem.bundleLineItems.map((b)=>b.sku
        )
    ]);
    return isExactMatch(serverSkus, draftSkus);
};

const areSameOpticianDetails = (server, draft)=>{
    if (areBothMissing(draft.opticianDetails, server.opticianDetails)) {
        // If they're both missing we consider them equal
        return true;
    } else if (!draft.opticianDetails || !server.opticianDetails) {
        // If one of them is missing we consider them to not be equal
        return false;
    }
    for (const field of Object.getOwnPropertyNames(draft.opticianDetails)){
        const serverValue = server.opticianDetails[field];
        const draftValue = draft.opticianDetails[field];
        /**
     * If server and client both express in their own way that
     * the value is missing, we consider them to be equal.
     */ if (areBothMissing(serverValue, draftValue)) {
            continue;
        }
        if (serverValue !== draftValue) {
            return false;
        }
    }
    return true;
};

const opticalParametersOnServer = [
    'baseCurve',
    'diameter',
    'power',
    'axis',
    'cylinder',
    'addition',
    'pupillaryDistance', 
];
const areSamePrescriptionParameters = (server, draft)=>{
    const serverMap = new Map(Object.entries(server).filter(([k])=>k !== '__typename'
    ));
    for (const attributeName of opticalParametersOnServer){
        const serverValue = serverMap.get(attributeName);
        const draftValue = draft[attributeName];
        /**
     * If server and client both express in their own way that
     * the value is missing, we consider them to be equal.
     */ if (areBothMissing(serverValue, draftValue)) {
            serverMap.delete(attributeName);
            continue;
        }
        if (serverValue !== draftValue) {
            return false;
        }
        serverMap.delete(attributeName);
    }
    return serverMap.size <= 0;
};

const isSamePrescription = (server, draft)=>{
    if (server.isEmailPrescription) {
        return draft.type === 'email-prescription';
    } else if (server.isExistingCustomer) {
        return draft.type === 'existing-customer';
    } else if (server.prescriptionFile && server.pupillaryDistanceFile) {
        if (draft.type !== 'prescription-file') {
            return false;
        }
        return draft.details.prescriptionFile.storageId === server.prescriptionFile.storageId && draft.details.pupillaryDistanceFile.storageId === server.pupillaryDistanceFile.storageId;
    }
    if (draft.type !== 'new-prescription' || !server.newPrescription) {
        return false;
    }
    const { leftLens , rightLens , expiryDate , opticianDetails  } = server.newPrescription;
    return areSamePrescriptionParameters(leftLens, draft.details.leftLens) && areSamePrescriptionParameters(rightLens, draft.details.rightLens) && areSameOpticianDetails({
        expiryDate,
        opticianDetails
    }, draft.details);
};

const areSamePrescriptionGlassesLineItem = (serverLineItem, draftLineItem)=>{
    var ref;
    if (serverLineItem.__typename !== 'LineItemBundle') {
        return false;
    }
    if (((ref = serverLineItem.typeDetails) === null || ref === void 0 ? void 0 : ref.__typename) !== 'BundlePrescriptionGlassesTypeDetails') {
        return false;
    }
    const serverSkus = new Set(serverLineItem.bundleLineItems.map((bl)=>bl.legacyProduct.sku
    ));
    const draftSkus = new Set([
        draftLineItem.sku,
        ...draftLineItem.bundleLineItems.map((b)=>b.sku
        )
    ]);
    return isExactMatch(serverSkus, draftSkus) && isSamePrescription(serverLineItem.typeDetails, draftLineItem.prescription);
};

const areSameRegularProduct = (serverLineItem, draftLineItem)=>{
    if (serverLineItem.__typename !== 'LineItemRegular') {
        return false;
    }
    return serverLineItem.sku === draftLineItem.sku;
};

const sameProduct = (serverLineItem, draft)=>{
    switch(draft.type){
        case 'contact-lenses-bundle-benelux':
        case 'contact-lenses-subscription':
            return serverLineItem.sku === draft.sku;
        case 'regular':
            return areSameRegularProduct(serverLineItem, draft);
        case 'non-prescription-bundle':
            return areSameNonPrescriptionBundleLineItem(serverLineItem, draft);
        case 'prescription-glasses':
            // A LineItemBundle is considered the same product only when all SKUs match exactly
            return areSamePrescriptionGlassesLineItem(serverLineItem, draft);
        case 'contact-lenses':
            // CL drafts should already be matched by the check above
            return areSameContactLenses(serverLineItem, draft);
    }
};
const getAddedLineItems = (lineItemsInCart, newLineItems)=>{
    return lineItemsInCart.reduce((m, cartLineItem)=>{
        const matchingCartLineItem = newLineItems.find(sameProduct.bind(null, cartLineItem));
        if (!matchingCartLineItem) {
            /**
         * We only want to keep cart line items we can match to a draft line
         * item that was just added.
         */ return m;
        }
        var ref;
        const quantity = (ref = matchingCartLineItem === null || matchingCartLineItem === void 0 ? void 0 : matchingCartLineItem.quantity) !== null && ref !== void 0 ? ref : 1;
        if (cartLineItem.quantity === quantity) {
            /**
         * No need to recalculate pricing if the quantities are the same.
         */ return [
                ...m,
                cartLineItem
            ];
        }
        /**
       * We need quantity and total pricing based on the products that were
       * just added, excluding products that already existed in the cart.
       */ return [
            ...m,
            swcHelpers.objectSpread({}, cartLineItem, {
                quantity,
                price: getTotalPricePerAddedQuantity(cartLineItem.price, quantity)
            }), 
        ];
    }, []);
};
// Overwrite total pricing based on unit price * quantity
const getTotalPricePerAddedQuantity = (price, quantity)=>swcHelpers.objectSpread({}, price, {
        total: swcHelpers.objectSpread({}, price.total, {
            gross: price.unit.gross * quantity,
            net: price.unit.net * quantity,
            subtotal: price.unit.subtotal * quantity,
            discount: swcHelpers.objectSpread({}, price.total.discount, {
                amount: price.unit.discount.amount * quantity
            }),
            tax: swcHelpers.objectSpread({}, price.total.tax, {
                amount: price.unit.tax.amount * quantity,
                portions: price.unit.tax.portions.map((portion, index)=>swcHelpers.objectSpread({}, portion, {
                        amount: price.unit.tax.portions[index].amount * quantity
                    })
                )
            })
        })
    })
;

const getAddress = (address)=>{
    if (!address) {
        return undefined;
    }
    const { apartment , city , countryCode , firstName , lastName , streetName , streetNumber , additionalStreetInfo , postalCode , region ,  } = address;
    return {
        city,
        countryCode,
        firstName,
        lastName,
        streetName,
        streetNumber: streetNumber !== null && streetNumber !== void 0 ? streetNumber : '',
        apartment: apartment !== null && apartment !== void 0 ? apartment : '',
        postalCode: postalCode !== null && postalCode !== void 0 ? postalCode : '',
        additionalStreetInfo: additionalStreetInfo !== null && additionalStreetInfo !== void 0 ? additionalStreetInfo : '',
        region: region !== null && region !== void 0 ? region : ''
    };
};

const getAttribute = (variant, attrName)=>{
    const attrValue = getAttrValue(variant, attrName);
    if (isNumber(attrValue) || isString(attrValue)) {
        return String(attrValue);
    }
    return '';
};

const numOrDefault = (v, d)=>typeof v === 'number' && !isNaN(v) ? v : d
;
const getCalculatedMaximumQuantity = (maximumQuantity, availableQuantity)=>{
    const max = numOrDefault(maximumQuantity, Infinity);
    return Math.min(max, numOrDefault(availableQuantity, max));
};

var _paymentMethodPrice;
const getCartPrices = (prices)=>({
        discountPrice: prices.discount.amount,
        paymentMethodPrice: (_paymentMethodPrice = prices.paymentMethodPrice) !== null && _paymentMethodPrice !== void 0 ? _paymentMethodPrice : undefined,
        subTotalPrice: prices.subtotal,
        shippingPrice: prices.shippingPrice,
        totalPrice: prices.gross
    })
;

const contactLensOpticalParameters = new Set([
    'baseCurve',
    'diameter',
    'axis',
    'power',
    'cylinder',
    'addition', 
]);
const getContactLensSpecs = (attributes)=>attributes.filter((attribute)=>contactLensOpticalParameters.has(attribute.name)
    ).map(({ name , value  })=>({
            name,
            value
        })
    ) // filter out __typename
;
const TYPE_DETAILS_EYE_SIDE = {
    left: 'LEFT',
    right: 'RIGHT',
    both: 'BOTH'
};

const getCustomerDetails = (details)=>{
    if (!details) {
        return undefined;
    }
    const { email , phone  } = details;
    return {
        email,
        phone: phone !== null && phone !== void 0 ? phone : ''
    };
};

const getLocalizedString = (locale, localizedStrings)=>{
    var ref;
    if (!localizedStrings) {
        return '';
    }
    let value = (ref = localizedStrings.find((string)=>string.locale === locale
    )) === null || ref === void 0 ? void 0 : ref.value;
    // Business rule, localized version unavailable, try 'en' fallback
    if (value === undefined) {
        var ref1;
        value = (ref1 = localizedStrings.find((string)=>string.locale.substr(0, 2) === 'en'
        )) === null || ref1 === void 0 ? void 0 : ref1.value;
    }
    return value || '';
};

const getDiscountDescriptions = (appliedDiscounts, locale)=>{
    // Business rule: filter out discount that require a discount code
    const filteredDiscounts = appliedDiscounts.filter((discount)=>!discount.requiresDiscountCode
    );
    return filteredDiscounts.map((discount)=>getLocalizedString(locale, discount.name)
    );
};

const isBundle = (lineItemRaw)=>lineItemRaw.__typename === 'LineItemBundle'
;
const getDisplayType = (lineItemRaw, intl)=>{
    let productType;
    if (isBundle(lineItemRaw)) {
        productType = lineItemRaw.bundleLineItemsType;
    } else {
        productType = lineItemRaw.legacyProduct.productType;
    }
    const camelCasedProductType = productType.toLowerCase().replace(/(-|_)[a-z]/g, (match)=>{
        return match[1].toUpperCase();
    });
    return intl.getLabel(`cart.lineItem.displayType.${camelCasedProductType}`);
};

const getLineItemAvailability = (availability)=>{
    if (!(availability === null || availability === void 0 ? void 0 : availability.channels)) {
        return undefined;
    }
    const channels = availability.channels.reduce((accumulator, currentValue)=>{
        return swcHelpers.objectSpread({}, accumulator, {
            [currentValue.key]: {
                isOnStock: currentValue.value.isOnStock,
                availableQuantity: currentValue.value.availableQuantity
            }
        });
    }, {});
    return {
        channels
    };
};

const getLineItemDiscount = (discounts, price, locale)=>{
    const productDiscount = discounts === null || discounts === void 0 ? void 0 : discounts.find((discount)=>discount.type === 'ProductDiscount'
    );
    var ref, ref1;
    return {
        validFrom: (ref = productDiscount === null || productDiscount === void 0 ? void 0 : productDiscount.validFrom) !== null && ref !== void 0 ? ref : undefined,
        validUntil: (ref1 = productDiscount === null || productDiscount === void 0 ? void 0 : productDiscount.validUntil) !== null && ref1 !== void 0 ? ref1 : undefined,
        absoluteValue: (price === null || price === void 0 ? void 0 : price.total.discount.amount) || 0,
        relativeValue: price ? Number((price.total.discount.percentage * 100).toFixed(2)) : 0,
        description: getLocalizedString(locale, productDiscount === null || productDiscount === void 0 ? void 0 : productDiscount.description)
    };
};

const getLineItemImageUrl = (variant, staticsUrl, isBynderImagesEnabled)=>{
    var ref, ref1;
    if (isBynderImagesEnabled) {
        var ref2, ref3;
        const images = isValidImages((ref2 = variant.media) === null || ref2 === void 0 ? void 0 : ref2.images) ? (ref3 = variant.media) === null || ref3 === void 0 ? void 0 : ref3.images : undefined;
        const imageUrl = getFrontImage(images).url;
        return `${imageUrl}?w=275&bg=FFFFFF&fm=jpeg&auto=format`;
    }
    if (variant === null || variant === void 0 ? void 0 : (ref = variant.images) === null || ref === void 0 ? void 0 : (ref1 = ref[0]) === null || ref1 === void 0 ? void 0 : ref1.url) {
        var ref4;
        return `${staticsUrl}${variant === null || variant === void 0 ? void 0 : (ref4 = variant.images) === null || ref4 === void 0 ? void 0 : ref4[0].url}?w=275&bg=FFFFFF&fm=jpeg&auto=format`;
    }
    return `${staticsUrl}`;
};

const getLineItemName = (product, intl)=>{
    if (product.productType === 'sunglasses') {
        return String(getAttrValue(product.masterData.current.variant, 'brand'));
    }
    return getLocalizedString(intl.locale, product.masterData.current.name);
};

const getLineItemPrice = (priceObj)=>{
    const total = priceObj.total;
    const unit = priceObj.unit;
    return {
        total: {
            discount: total.discount.amount,
            original: total.subtotal,
            gross: total.gross,
            net: total.net,
            taxAmount: total.tax.amount
        },
        unit: {
            discount: unit.discount.amount,
            original: unit.subtotal,
            gross: unit.gross,
            net: unit.net,
            taxAmount: unit.tax.amount
        }
    };
};

const getLocalizedAttribute = (variant, attrName, locale, seperator = ', ')=>{
    const attrValue = getAttrValue(variant, attrName);
    if (isPlainObject(attrValue)) {
        var _label;
        /* Handle two different types of attribute values

        value: {
          key: 'aviator',
          label: {
            'en': 'Aviator',
        }

        },
        value: {
          en: 'Gold,Violet',
        },
      */ const value = (_label = attrValue.label) !== null && _label !== void 0 ? _label : attrValue;
        return getLocalizedValue(value, locale);
    }
    if (Array.isArray(attrValue)) {
        var ref;
        var ref1;
        return (ref1 = (ref = getLocalizedValues(attrValue, locale)) === null || ref === void 0 ? void 0 : ref.join(seperator)) !== null && ref1 !== void 0 ? ref1 : '';
    }
};

var BillingFrequency;
(function(BillingFrequency) {
    BillingFrequency["MONTHLY"] = "MONTHLY";
    BillingFrequency["QUARTERLY"] = "QUARTERLY";
})(BillingFrequency || (BillingFrequency = {}));
var BundleLineItemProductType;
(function(BundleLineItemProductType) {
    BundleLineItemProductType["LENS"] = "LENS";
    BundleLineItemProductType["SOLUTION"] = "SOLUTION";
})(BundleLineItemProductType || (BundleLineItemProductType = {}));
var BundleLineItemType;
(function(BundleLineItemType) {
    BundleLineItemType["COMPOSITE"] = "COMPOSITE";
    BundleLineItemType["CONTACT_LENSES_BENELUX"] = "CONTACT_LENSES_BENELUX";
    BundleLineItemType["CONTACT_LENSES_SUBSCRIPTION"] = "CONTACT_LENSES_SUBSCRIPTION";
    BundleLineItemType["NON_PRESCRIPTION_BUNDLE"] = "NON_PRESCRIPTION_BUNDLE";
    BundleLineItemType["PRESCRIPTION_GLASSES"] = "PRESCRIPTION_GLASSES";
})(BundleLineItemType || (BundleLineItemType = {}));
var CartCustomerType;
(function(CartCustomerType) {
    CartCustomerType["B2B"] = "B2B";
    CartCustomerType["B2C"] = "B2C";
})(CartCustomerType || (CartCustomerType = {}));
var ChannelType;
(function(ChannelType) {
    ChannelType["ONLINE"] = "ONLINE";
    ChannelType["STORE"] = "STORE";
})(ChannelType || (ChannelType = {}));
var CheckoutState;
(function(CheckoutState) {
    CheckoutState["Finalized"] = "Finalized";
    CheckoutState["InProgress"] = "InProgress";
    CheckoutState["ReadyToOrder"] = "ReadyToOrder";
})(CheckoutState || (CheckoutState = {}));
var DeliveryFrequency;
(function(DeliveryFrequency) {
    DeliveryFrequency["MONTHLY"] = "MONTHLY";
    DeliveryFrequency["QUARTERLY"] = "QUARTERLY";
})(DeliveryFrequency || (DeliveryFrequency = {}));
var DiscountType;
(function(DiscountType) {
    DiscountType["ABSOLUTE"] = "ABSOLUTE";
    DiscountType["CartDiscount"] = "CartDiscount";
    DiscountType["ProductDiscount"] = "ProductDiscount";
    DiscountType["RELATIVE"] = "RELATIVE";
})(DiscountType || (DiscountType = {}));
var DiscountValueType;
(function(DiscountValueType) {
    DiscountValueType["ABSOLUTE"] = "ABSOLUTE";
    DiscountValueType["GIFT"] = "GIFT";
    DiscountValueType["RELATIVE"] = "RELATIVE";
})(DiscountValueType || (DiscountValueType = {}));
var EyeSide;
(function(EyeSide) {
    EyeSide["BOTH"] = "BOTH";
    EyeSide["LEFT"] = "LEFT";
    EyeSide["RIGHT"] = "RIGHT";
})(EyeSide || (EyeSide = {}));
var LineItemType;
(function(LineItemType) {
    LineItemType["BUNDLE"] = "BUNDLE";
    LineItemType["CONTACTLENSES"] = "CONTACTLENSES";
    LineItemType["REGULAR"] = "REGULAR";
})(LineItemType || (LineItemType = {}));
var SanitizationAction$1;
(function(SanitizationAction) {
    SanitizationAction["KeepExisting"] = "KeepExisting";
    SanitizationAction["Replace"] = "Replace";
})(SanitizationAction$1 || (SanitizationAction$1 = {}));
var ValidationCode;
(function(ValidationCode) {
    ValidationCode["CONTAINS_MUTUALLY_EXCLUSIVE_PRODUCT_TYPES"] = "CONTAINS_MUTUALLY_EXCLUSIVE_PRODUCT_TYPES";
    ValidationCode["HAS_VALID_OPTICIAN_DETAILS"] = "HAS_VALID_OPTICIAN_DETAILS";
    ValidationCode["HAS_VALID_PRESCRIPTION_DATA_RANGES"] = "HAS_VALID_PRESCRIPTION_DATA_RANGES";
    ValidationCode["IS_IN_STOCK"] = "IS_IN_STOCK";
    ValidationCode["IS_VALID_AMOUNT_OF_MASTER_VARIANTS"] = "IS_VALID_AMOUNT_OF_MASTER_VARIANTS";
    ValidationCode["IS_VALID_CART_BY_PRESCRIPTION"] = "IS_VALID_CART_BY_PRESCRIPTION";
    ValidationCode["IS_VALID_EXPIRY_DATE"] = "IS_VALID_EXPIRY_DATE";
    ValidationCode["IS_VALID_QUANTITY"] = "IS_VALID_QUANTITY";
    ValidationCode["MAX_QUANTITY_PER_LINE_ITEM_TYPE"] = "MAX_QUANTITY_PER_LINE_ITEM_TYPE";
    ValidationCode["MAX_QUANTITY_PER_PRODUCT_TYPE"] = "MAX_QUANTITY_PER_PRODUCT_TYPE";
    ValidationCode["MAX_VARIANTS_PER_PRODUCT_TYPE"] = "MAX_VARIANTS_PER_PRODUCT_TYPE";
    ValidationCode["ONE_LENS_TYPE_PER_EYE"] = "ONE_LENS_TYPE_PER_EYE";
    ValidationCode["PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT"] = "PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT";
    ValidationCode["PRODUCTS_ARE_PUBLISHED"] = "PRODUCTS_ARE_PUBLISHED";
    ValidationCode["REQUIRED_PRODUCT_TYPES_ARE_IN_BUNDLE"] = "REQUIRED_PRODUCT_TYPES_ARE_IN_BUNDLE";
    ValidationCode["VALID_CART_BY_PRODUCT_TYPES"] = "VALID_CART_BY_PRODUCT_TYPES";
    ValidationCode["VALID_RELATIONS_BUNDLE_PRODUCTS"] = "VALID_RELATIONS_BUNDLE_PRODUCTS";
})(ValidationCode || (ValidationCode = {}));
var VoucherState;
(function(VoucherState) {
    VoucherState["ApplicationStoppedByPreviousDiscount"] = "ApplicationStoppedByPreviousDiscount";
    VoucherState["DoesNotMatchCart"] = "DoesNotMatchCart";
    VoucherState["MatchesCart"] = "MatchesCart";
    VoucherState["MaxApplicationReached"] = "MaxApplicationReached";
    VoucherState["NotActive"] = "NotActive";
    VoucherState["NotValid"] = "NotValid";
})(VoucherState || (VoucherState = {}));

const upperFirst = (value)=>{
    return value[0].toUpperCase() + value.slice(1);
};
const mapToProductType = (lineItemRaw)=>{
    //TODO: Fix it properly with ticket
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch(lineItemRaw.bundleLineItemsType){
        case BundleLineItemType.CONTACT_LENSES_BENELUX:
        case BundleLineItemType.CONTACT_LENSES_SUBSCRIPTION:
            return 'Contact-lenses';
        case BundleLineItemType.NON_PRESCRIPTION_BUNDLE:
        case BundleLineItemType.PRESCRIPTION_GLASSES:
            const mainLineItem = lineItemRaw.bundleLineItems.find((l)=>l.isMainBundleLineItem
            );
            const productType = mainLineItem === null || mainLineItem === void 0 ? void 0 : mainLineItem.legacyProduct.productType;
            if (!productType) {
                return null;
            }
            const capitalized = upperFirst(productType);
            if (!CommerceToolsApi.isProductTypeName(capitalized)) {
                return null;
            }
            return capitalized;
    }
    return null;
};
const getProductUrl = (formatProductUrl, lineItemRaw, slug, locale)=>{
    if (!slug) {
        return undefined;
    }
    if (lineItemRaw.__typename === 'LineItemBundle') {
        const productType = mapToProductType(lineItemRaw);
        if (!productType) {
            return undefined;
        }
        return formatProductUrl({
            productType,
            sku: lineItemRaw.sku,
            slug: {
                [locale]: slug
            }
        });
    }
    return formatProductUrl({
        productType: upperFirst(lineItemRaw.legacyProduct.productType),
        sku: lineItemRaw.sku,
        slug: {
            [locale]: slug
        }
    });
};

const getShippingMethod = (shippingMethod)=>{
    return {
        id: shippingMethod.id,
        key: shippingMethod.key,
        freeAbove: shippingMethod.freeAbove
    };
};

const isSolvableValidationError = (validationError)=>validationError.__typename === 'SolvableValidationError'
;
const getSolvableValidationErrors = (validationInput)=>{
    if (!validationInput || !validationInput.cartLineItemErrors) {
        return [];
    }
    /** The backend returns a hypothetical cart including the to-be-added lineItem with mockID and it's errors. We don't want to take action on them. */ const lineItemValidationErrors = validationInput.cartLineItemErrors.filter((error)=>error.itemId !== 'mockID'
    );
    if (lineItemValidationErrors) {
        return reduceErrorsToSingleArray(lineItemValidationErrors);
    }
    return [];
};
const reduceErrorsToSingleArray = (lineItemValidationErrors)=>{
    const errors = lineItemValidationErrors.reduce((accumulator, currentValue)=>{
        const resolvableValidationError = currentValue.errors.map((error)=>{
            if (isSolvableValidationError(error)) {
                const { actionItem , defaultMessage , isSolvable , passed , validationCode  } = error;
                return {
                    actionItem,
                    defaultMessage,
                    isSolvable,
                    passed,
                    validationCode
                };
            }
        });
        return [
            ...accumulator,
            ...resolvableValidationError
        ];
    }, []);
    return errors.filter((error)=>!!error
    );
};

const getSubscriptionDetails = (cart)=>{
    const cartContainsContactLensesSubscriptionLineItems = cart.lineItems.filter((item)=>item.lineItemType === LineItemType.BUNDLE
    ).some((item)=>item.bundleLineItemsType === BundleLineItemType.CONTACT_LENSES_SUBSCRIPTION
    );
    if (!cartContainsContactLensesSubscriptionLineItems || !cart.subscriptionPlan || !cart.price.subscriptionPlanPrice) {
        return undefined;
    }
    const billingDate = new Date(cart.subscriptionPlan.billingDate);
    const subscriptionDetails = {
        billingDate: DateUtils.toLocalDate(billingDate),
        firstPayment: cart.price.subscriptionPlanPrice.invoicedAmount,
        recurringPayment: cart.price.subscriptionPlanPrice.total
    };
    return subscriptionDetails;
};

const validationErrorLabelMap = {
    CONTAINS_MUTUALLY_EXCLUSIVE_PRODUCT_TYPES: 'validations.containsMutuallyExclusiveProductTypes',
    HAS_VALID_OPTICIAN_DETAILS: 'validations.prescriptionGlassesInvalidBundleCombination',
    HAS_VALID_PRESCRIPTION_DATA_RANGES: 'validations.prescriptionGlassesInvalidBundleCombination',
    IS_IN_STOCK: 'validations.unavailableProductsInCart',
    IS_VALID_AMOUNT_OF_MASTER_VARIANTS: 'validations.maxMasterVariantQuantityExceeded',
    IS_VALID_CART_BY_PRESCRIPTION: 'validations.quantityExceededPrescriptionExpiryDate',
    IS_VALID_EXPIRY_DATE: 'validations.quantityExceededPrescriptionExpiryDate',
    IS_VALID_QUANTITY: 'validations.maxQuantitySkuExceeded',
    MAX_QUANTITY_PER_LINE_ITEM_TYPE: 'validations.maxVariantsPerProductTypeQuantityExceeded',
    MAX_QUANTITY_PER_PRODUCT_TYPE: 'validations.maxVariantsPerProductTypeQuantityExceeded',
    MAX_VARIANTS_PER_PRODUCT_TYPE: 'validations.maxVariantsPerProductTypeQuantityExceeded',
    ONE_LENS_TYPE_PER_EYE: 'validations.oneLensTypePerEye',
    PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT: 'validations.unavailableProductsInCart',
    PRODUCTS_ARE_PUBLISHED: 'validations.unavailableProductsInCart',
    REQUIRED_PRODUCT_TYPES_ARE_IN_BUNDLE: 'validations.prescriptionGlassesInvalidBundleCombination',
    VALID_CART_BY_PRODUCT_TYPES: 'validations.prescriptionGlassesInvalidBundleCombination',
    VALID_RELATIONS_BUNDLE_PRODUCTS: 'validations.prescriptionGlassesInvalidBundleCombination'
};
const getValidationErrorCodes = (validationInput)=>{
    if (!validationInput) {
        return [];
    }
    const lineItemValidationErrors = validationInput.cartLineItemErrors;
    if (lineItemValidationErrors) {
        return getLineItemValidationErrorCodes(lineItemValidationErrors);
    }
    return [];
};
const getLineItemValidationErrorCodes = (lineItemValidationErrors)=>{
    const codes = lineItemValidationErrors.reduce((accumulator, currentValue)=>{
        const validationCode = currentValue.errors.map((error)=>error.validationCode
        );
        return [
            ...accumulator,
            ...validationCode
        ];
    }, []);
    const uniqueValidationCodes = [
        ...new Set(codes)
    ];
    return uniqueValidationCodes.map((code)=>validationErrorLabelMap[code]
    );
};

const recoverableErrorCodes = [
    validationErrorLabelMap.IS_IN_STOCK,
    validationErrorLabelMap.PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT, 
];
// Filter out all error codes that are considered "recoverable" from the
// validation errors fragment and return an array of the remaining error codes
const getUnrecoverableValidationErrors = (validationErrors)=>{
    const validationErrorCodes = getValidationErrorCodes(validationErrors);
    return validationErrorCodes.filter((code)=>!recoverableErrorCodes.includes(code)
    );
};

const isContactLensesSubscriptionLineItem = (lineItem)=>lineItem.type === 'contact-lenses-subscription'
;

const getAllowBackOrder = (variant)=>{
    const allowBackOrder = getAttrValue(variant, 'allowBackOrder');
    return isBoolean(allowBackOrder) ? allowBackOrder : undefined;
};

const getSharedProps = (lineItemRaw, product, intl)=>{
    const variant = product.masterData.current.variant;
    return {
        allowBackOrder: getAllowBackOrder(variant),
        displayType: getDisplayType(lineItemRaw, intl),
        availability: getLineItemAvailability(variant.availability),
        currency: intl.currency,
        discount: getLineItemDiscount(lineItemRaw.discounts, lineItemRaw.price, intl.locale),
        id: lineItemRaw.id,
        imageAlt: getLineItemName(product, intl),
        isGift: lineItemRaw.isGift,
        name: getLineItemName(product, intl),
        price: getLineItemPrice(lineItemRaw.price),
        productType: product.productType,
        quantity: lineItemRaw.quantity
    };
};

const getContactLensSpecifications = (specs)=>{
    var ref, ref1, ref2, ref3, ref4;
    return [
        {
            name: 'baseCurve',
            value: specs === null || specs === void 0 ? void 0 : (ref = specs.baseCurve) === null || ref === void 0 ? void 0 : ref.toString()
        },
        {
            name: 'diameter',
            value: specs === null || specs === void 0 ? void 0 : (ref1 = specs.diameter) === null || ref1 === void 0 ? void 0 : ref1.toString()
        },
        {
            name: 'axis',
            value: specs === null || specs === void 0 ? void 0 : (ref2 = specs.axis) === null || ref2 === void 0 ? void 0 : ref2.toString()
        },
        {
            name: 'power',
            value: specs === null || specs === void 0 ? void 0 : (ref3 = specs.power) === null || ref3 === void 0 ? void 0 : ref3.toString()
        },
        {
            name: 'cylinder',
            value: specs === null || specs === void 0 ? void 0 : (ref4 = specs.cylinder) === null || ref4 === void 0 ? void 0 : ref4.toString()
        }, 
    ].filter((spec)=>Boolean(spec.value)
    );
};

const getLegacyProductByType = (lineItems, type)=>{
    var ref;
    if (!type) {
        var ref1;
        return (ref1 = lineItems.find((lineItem)=>lineItem.isMainBundleLineItem
        )) === null || ref1 === void 0 ? void 0 : ref1.legacyProduct;
    }
    return (ref = lineItems.find((lineItem)=>lineItem.type === type
    )) === null || ref === void 0 ? void 0 : ref.legacyProduct;
};

const getSolutionProperties = (solutionProduct, intl, settings)=>{
    return {
        solutionSku: solutionProduct.sku,
        solutionName: getLineItemName(solutionProduct, intl),
        solutionImageUrl: getLineItemImageUrl(solutionProduct.masterData.current.variant, settings.staticsUrl, settings.isBynderImagesEnabled)
    };
};

const mapContactLensesBundleBeneluxLineItem$1 = (lineItemRaw, settings, intl)=>{
    const contactLensesProduct = getLegacyProductByType(lineItemRaw.bundleLineItems, BundleLineItemProductType.LENS);
    const solutionsProduct = getLegacyProductByType(lineItemRaw.bundleLineItems, BundleLineItemProductType.SOLUTION);
    const mainBundleProduct = getLegacyProductByType(lineItemRaw.bundleLineItems);
    if (!contactLensesProduct || !mainBundleProduct) {
        console.error('This bundle does not contain Contact Lenses or a mainBundleProduct');
        return null;
    }
    const contactLensVariant = contactLensesProduct.masterData.current.variant;
    const mainProductVariant = mainBundleProduct.masterData.current.variant;
    const shared = getSharedProps(lineItemRaw, mainBundleProduct, intl);
    const slug = getAttribute(mainProductVariant, 'parentSlug');
    const typeDetails = lineItemRaw.typeDetails;
    const { leftLens , rightLens  } = typeDetails.prescription;
    const solutionProperties = solutionsProduct ? getSolutionProperties(solutionsProduct, intl, settings) : null;
    return swcHelpers.objectSpread({}, shared, {
        type: 'contact-lenses-bundle-benelux',
        sku: lineItemRaw.sku,
        imageSku: getAttribute(mainProductVariant, 'imageSku'),
        productSlug: slug,
        productId: contactLensesProduct.id,
        productUrl: getProductUrl(settings.formatProductUrl, lineItemRaw, slug, intl.locale),
        brand: {
            name: getAttribute(contactLensVariant, 'brand'),
            model: getAttribute(contactLensVariant, 'brandModel'),
            modelCode: getAttribute(contactLensVariant, 'brandModelCode')
        },
        imageUrl: settings.isBynderImagesEnabled ? getLineItemImageUrl(mainProductVariant, settings.staticsUrl, true) : getLineItemImageUrl(contactLensVariant, settings.staticsUrl),
        lensType: getLocalizedAttribute(mainProductVariant, 'lensType', intl.locale) || '',
        leftEyeProduct: {
            name: getLineItemName(contactLensesProduct, intl),
            specs: getContactLensSpecifications(leftLens)
        },
        rightEyeProduct: {
            name: getLineItemName(contactLensesProduct, intl),
            specs: getContactLensSpecifications(rightLens)
        }
    }, solutionProperties);
};

const billingFrequencyLabelMap = {
    MONTHLY: 'cart.subscription.billingFrequency.monthly',
    QUARTERLY: 'cart.subscription.billingFrequency.quarterly'
};
const deliveryFrequencyLabelMap = {
    MONTHLY: 'cart.subscription.deliveryFrequency.monthly',
    QUARTERLY: 'cart.subscription.deliveryFrequency.quarterly'
};
const getSubscriptionFrequencyLabel = (enumValue, type)=>{
    switch(type){
        case 'billingFrequency':
            return billingFrequencyLabelMap[enumValue];
        case 'deliveryFrequency':
            return deliveryFrequencyLabelMap[enumValue];
    }
};

var SanitizationAction;
(function(SanitizationAction) {
    SanitizationAction["Replace"] = 'replace';
    SanitizationAction["KeepExisting"] = 'keep existing';
})(SanitizationAction || (SanitizationAction = {}));

var SubscriptionType;
(function(SubscriptionType) {
    SubscriptionType[/**
   * Monthly payment, monthly delivery
   */ "MONTHLY"] = "MONTHLY";
    SubscriptionType[/**
   * Quarterly payment, quarterly delivery
   */ "QUARTERLY"] = "QUARTERLY";
    SubscriptionType[/**
   * Monthly payment, quarterly delivery
   * This type has a first payment of three months
   */ "MIXED_MONTHLY"] = "MIXED_MONTHLY";
    SubscriptionType[/**
   * Quarterly payment, monthly delivery.
   */ "MIXED_QUARTERLY"] = "MIXED_QUARTERLY";
})(SubscriptionType || (SubscriptionType = {}));

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get SanitizationAction () { return SanitizationAction; },
  get SubscriptionType () { return SubscriptionType; }
});

const getSubscriptionType$1 = (typeDetails)=>{
    if (typeDetails.billingFrequency.valueOf() === typeDetails.deliveryFrequency.valueOf()) {
        if (typeDetails.billingFrequency === BillingFrequency.MONTHLY) {
            return SubscriptionType.MONTHLY;
        }
        return SubscriptionType.QUARTERLY;
    }
    if (typeDetails.billingFrequency === BillingFrequency.MONTHLY) {
        return SubscriptionType.MIXED_MONTHLY;
    }
    return SubscriptionType.MIXED_QUARTERLY;
};

const mapContactLensesSubscription = (lineItemRaw, settings, intl)=>{
    const solutionsProduct = getLegacyProductByType(lineItemRaw.bundleLineItems, BundleLineItemProductType.SOLUTION);
    const mainBundleProduct = getLegacyProductByType(lineItemRaw.bundleLineItems, BundleLineItemProductType.LENS);
    if (!mainBundleProduct) {
        console.error('This ContactLensesSubscriptionLineItem does not have a mainBundleLineItem');
        return null;
    }
    const mainBundleProductVariant = mainBundleProduct.masterData.current.variant;
    const shared = getSharedProps(lineItemRaw, mainBundleProduct, intl);
    const slug = getLocalizedString(intl.locale, mainBundleProduct.masterData.current.slug);
    const typeDetails = lineItemRaw.typeDetails;
    const { leftLens , rightLens  } = typeDetails.prescriptionForSubscription;
    const solutionProperties = solutionsProduct ? getSolutionProperties(solutionsProduct, intl, settings) : null;
    const billingFrequency = getSubscriptionFrequencyLabel(typeDetails.billingFrequency, 'billingFrequency');
    const deliveryFrequency = getSubscriptionFrequencyLabel(typeDetails.deliveryFrequency, 'deliveryFrequency');
    const mapLensProductDetails = (lensProduct)=>{
        if (lensProduct === null) {
            return undefined;
        }
        return {
            name: getLineItemName(mainBundleProduct, intl),
            specs: getContactLensSpecifications(lensProduct)
        };
    };
    return swcHelpers.objectSpread({}, shared, {
        type: 'contact-lenses-subscription',
        sku: lineItemRaw.sku,
        imageSku: getAttribute(mainBundleProductVariant, 'imageSku'),
        productSlug: slug,
        productId: mainBundleProduct.id,
        productUrl: getProductUrl(settings.formatProductUrl, lineItemRaw, slug, intl.locale),
        brand: {
            name: getAttribute(mainBundleProductVariant, 'brand'),
            model: getAttribute(mainBundleProductVariant, 'brandModel'),
            modelCode: getAttribute(mainBundleProductVariant, 'brandModelCode')
        },
        imageUrl: getLineItemImageUrl(mainBundleProductVariant, settings.staticsUrl, settings.isBynderImagesEnabled),
        lensType: getLocalizedAttribute(mainBundleProductVariant, 'lensType', intl.locale) || '',
        leftEyeProduct: mapLensProductDetails(leftLens),
        rightEyeProduct: mapLensProductDetails(rightLens),
        billingFrequency: intl.getLabel(billingFrequency),
        deliveryFrequency: intl.getLabel(deliveryFrequency),
        packageQuantity: getAttribute(mainBundleProductVariant, 'packageQuantity'),
        subscriptionType: getSubscriptionType$1(typeDetails)
    }, solutionProperties);
};

function getBundledLineItemProductVariant(bundleLineItem) {
    return bundleLineItem.legacyProduct.masterData.current.variant;
}

/**
 * We're doing a localeCompore to catch some casing differences we've encountered
 * in the past when receiving product data from the backend.
 */ function findBundledLineItem(lineItems, productType) {
    return lineItems.find((lineItem)=>lineItem.legacyProduct.productType.localeCompare(productType, 'en', {
            sensitivity: 'base',
            usage: 'search'
        }) === 0
    );
}

function getBundledProductInfo(bundleLineItem, intl) {
    const { name  } = bundleLineItem.legacyProduct.masterData.current;
    const variant = getBundledLineItemProductVariant(bundleLineItem);
    const variationAttribute = getAttribute(variant, 'variationAttribute');
    const variationAttributeValue = getLocalizedAttrValue(variant, variationAttribute, intl.locale);
    const bundleTitle = getLocalizedString(intl.locale, name);
    return swcHelpers.objectSpread({
        sku: variant.sku,
        title: !!variationAttributeValue ? `${bundleTitle} - ${variationAttributeValue}` : bundleTitle
    }, bundleLineItem.price ? {
        price: getLineItemPrice(bundleLineItem.price).unit,
        discount: getLineItemDiscount(bundleLineItem.discounts, bundleLineItem.price, intl.locale)
    } : {});
}

function getBundledFrameInfo(lineItemRaw, bundleLineItem, intl) {
    const variant = getBundledLineItemProductVariant(bundleLineItem);
    const brandName = getAttribute(variant, 'brand');
    const model = getAttribute(variant, 'brandModel');
    const modelCode = getAttribute(variant, 'brandModelCode');
    const modelColorCode = getAttribute(variant, 'brandModelColorCode');
    const gender = getLocalizedAttribute(variant, 'gender', intl.locale);
    const frameColor = getLocalizedAttribute(variant, 'frameColor', intl.locale);
    const frameShape = getLocalizedAttribute(variant, 'frameShape', intl.locale);
    const frameMaterial = getLocalizedAttribute(variant, 'frameMaterial', intl.locale);
    const title = [
        ...new Set([
            brandName,
            model,
            modelCode,
            modelColorCode
        ].filter(Boolean))
    ].join(' ');
    return swcHelpers.objectSpread({}, getBundledProductInfo(bundleLineItem, intl), {
        title,
        productId: lineItemRaw.id,
        brand: {
            name: brandName,
            model,
            modelCode
        },
        gender,
        frameColor,
        frameShape,
        frameMaterial
    });
}

const PrescriptionType = Types.PrescriptionType;

/**
 * getLensDetails
 *
 * Removes all null values and __typename from the lens object
 */ const getLensDetails = (lens)=>Object.entries(lens).reduce((acc, [name, value])=>{
        if (value !== null && name !== '__typename') {
            acc[name] = value;
        }
        return acc;
    }, {})
;

function getPrescription(typeDetails) {
    if (typeDetails.isExistingCustomer) {
        return {
            type: PrescriptionType.EXISTING_CUSTOMER
        };
    }
    if (typeDetails.newPrescription) {
        return {
            type: PrescriptionType.NEW_PRESCRIPTION,
            rightLens: getLensDetails(typeDetails.newPrescription.rightLens),
            leftLens: getLensDetails(typeDetails.newPrescription.leftLens)
        };
    }
    if (typeDetails.prescriptionFile && typeDetails.pupillaryDistanceFile) {
        return {
            type: PrescriptionType.PRESCRIPTION_FILE,
            prescriptionFile: typeDetails.prescriptionFile.fileName,
            pupillaryDistanceFile: typeDetails.pupillaryDistanceFile.fileName
        };
    }
    return {
        type: PrescriptionType.EMAIL_PRESCRIPTION
    };
}

function getLineItemCartDiscountDescription(lineItemRaw, locale) {
    const { discounts  } = lineItemRaw;
    if (!discounts || discounts.length === 0) {
        return;
    }
    const cartDiscounts = discounts === null || discounts === void 0 ? void 0 : discounts.filter((discount)=>discount.type === 'CartDiscount'
    );
    if (!cartDiscounts.length) {
        return;
    }
    return getLocalizedString(locale, cartDiscounts[0].description);
}

const mapNonPrescriptionBundleLineItem$1 = (lineItemRaw, settings, intl)=>{
    const { locale  } = intl;
    const mainBundleLineItem = lineItemRaw.bundleLineItems.find((bundleLineItem)=>bundleLineItem.isMainBundleLineItem
    );
    if (!mainBundleLineItem) {
        console.error(`None of the bundleLineItems have isMainBundleLineItem in LineItemBundle with id ${lineItemRaw.id}`);
        return null;
    }
    // There are more bundled items in bundleLineItems than just the configurationBundle (aka offering)
    // but only the configurationBundle is needed for the view in cart and checkout
    const configurationBundleItem = lineItemRaw.bundleLineItems.find((item)=>item.legacyProduct.productType === 'configurationbundle'
    );
    if (!configurationBundleItem) {
        console.error(`None of the bundleLineItems is a configurationbundle in LineItemBundle with id ${lineItemRaw.id}`);
        return null;
    }
    const shared = getSharedProps(lineItemRaw, mainBundleLineItem.legacyProduct, intl);
    const variant = getBundledLineItemProductVariant(mainBundleLineItem);
    const slug = getLocalizedString(locale, mainBundleLineItem.legacyProduct.masterData.current.slug);
    const offering = getBundledProductInfo(configurationBundleItem, intl);
    const mainBundleItemPrice = mainBundleLineItem.price ? getLineItemPrice(mainBundleLineItem.price).unit : undefined;
    return swcHelpers.objectSpread({}, shared, {
        type: 'non-prescription-bundle',
        imageUrl: getLineItemImageUrl(variant, settings.staticsUrl, settings.isBynderImagesEnabled),
        name: getLocalizedString(locale, mainBundleLineItem.legacyProduct.masterData.current.name),
        productUrl: getProductUrl(settings.formatProductUrl, lineItemRaw, slug, locale),
        productId: lineItemRaw.id,
        sku: lineItemRaw.sku,
        mainBundleItemPrice,
        offering,
        brand: {
            name: getAttribute(variant, 'brand'),
            model: getAttribute(variant, 'brandModel'),
            modelCode: getAttribute(variant, 'brandModelCode'),
            modelColorCode: getAttribute(variant, 'brandModelColorCode')
        }
    });
};

const mapPrescriptionGlassesLineItem$1 = (lineItemRaw, settings, intl)=>{
    var ref, ref1, ref2, ref3;
    const mainBundleLineItem = lineItemRaw.bundleLineItems.find((bundleLineItem)=>bundleLineItem.isMainBundleLineItem
    );
    if (!mainBundleLineItem) {
        console.error(`None of the bundleLineItems have isMainBundleLineItem in LineItemBundle with id ${lineItemRaw.id}`);
        return null;
    }
    const lensTypeBundleLineItem = findBundledLineItem(lineItemRaw.bundleLineItems, 'LensTypes');
    const lensPackageBundleLineItem = findBundledLineItem(lineItemRaw.bundleLineItems, 'PrescriptionGlassPackages');
    if (!lensTypeBundleLineItem || !lensPackageBundleLineItem) {
        console.error(`Either lensTypeBundleLineItem or lensPackageBundleLineItem are missing from LineItemBundle with id ${lineItemRaw.id}`);
        return null;
    }
    const prescriptionTypeDetails = lineItemRaw.typeDetails;
    const addOnBundleLineItem = findBundledLineItem(lineItemRaw.bundleLineItems, 'PrescriptionGlassPackageOptions');
    const shared = getSharedProps(lineItemRaw, mainBundleLineItem.legacyProduct, intl);
    // Assumes the frame is the main bundled line item
    const frame = getBundledFrameInfo(lineItemRaw, mainBundleLineItem, intl);
    const lensType = getBundledProductInfo(lensTypeBundleLineItem, intl);
    const lensPackage = getBundledProductInfo(lensPackageBundleLineItem, intl);
    const addOn = !!addOnBundleLineItem ? getBundledProductInfo(addOnBundleLineItem, intl) : undefined;
    // Product discounts
    const frameDiscount = ((ref = frame.price) === null || ref === void 0 ? void 0 : ref.discount) || 0;
    const lensTypeDiscount = ((ref1 = lensType.price) === null || ref1 === void 0 ? void 0 : ref1.discount) || 0;
    const lensPackageDiscount = ((ref2 = lensPackage.price) === null || ref2 === void 0 ? void 0 : ref2.discount) || 0;
    const addOnDiscount = (addOn === null || addOn === void 0 ? void 0 : (ref3 = addOn.price) === null || ref3 === void 0 ? void 0 : ref3.discount) || 0;
    const totalProductDiscount = shared.quantity * (frameDiscount + lensTypeDiscount + lensPackageDiscount + addOnDiscount);
    // Total discount (cart discount + (all product discounts * quantity))
    const totalDiscount = shared.price.total.discount;
    const slug = getLocalizedString(intl.locale, mainBundleLineItem.legacyProduct.masterData.current.slug);
    const variant = getBundledLineItemProductVariant(mainBundleLineItem);
    return swcHelpers.objectSpread({}, shared, {
        type: 'prescription-glasses',
        name: frame.title,
        imageAlt: frame.title,
        frame,
        mainBundleItemPrice: frame === null || frame === void 0 ? void 0 : frame.price,
        imageUrl: getLineItemImageUrl(variant, settings.staticsUrl, settings.isBynderImagesEnabled),
        lensType,
        lensPackage,
        addOn,
        productUrl: getProductUrl(settings.formatProductUrl, lineItemRaw, slug, intl.locale),
        prescription: getPrescription(prescriptionTypeDetails)
    }, !!totalDiscount ? {
        cartDiscount: {
            amount: totalDiscount - totalProductDiscount,
            description: getLineItemCartDiscountDescription(lineItemRaw, intl.locale)
        }
    } : {});
};

// Maps the server model to client view model (getCart queries to view cart UI)
const mapBundleProduct = (lineItemRaw, settings, intl)=>{
    switch(lineItemRaw.bundleLineItemsType){
        case BundleLineItemType.CONTACT_LENSES_BENELUX:
            return mapContactLensesBundleBeneluxLineItem$1(lineItemRaw, settings, intl);
        case BundleLineItemType.CONTACT_LENSES_SUBSCRIPTION:
            return mapContactLensesSubscription(lineItemRaw, settings, intl);
        case BundleLineItemType.PRESCRIPTION_GLASSES:
            return mapPrescriptionGlassesLineItem$1(lineItemRaw, settings, intl);
        case BundleLineItemType.NON_PRESCRIPTION_BUNDLE:
            return mapNonPrescriptionBundleLineItem$1(lineItemRaw, settings, intl);
        default:
            console.error(`Encountered unsupported bundle line item type "${lineItemRaw.bundleLineItemsType}" with SKU: ${lineItemRaw.sku}`);
            return null;
    }
};

const getEyeSide = (typeDetails)=>{
    if (typeDetails && 'eyeSide' in typeDetails && typeDetails.eyeSide) {
        return typeDetails.eyeSide.toLowerCase();
    }
    return;
};

const getLocalizedAttributes = (variant, attrName, locale)=>{
    const attrValues = getAttrValue(variant, attrName);
    if (isPlainObject(attrValues)) {
        return [
            getLocalizedValue(attrValues, locale)
        ];
    }
    if (isArray(attrValues)) {
        return attrValues.map((attrValue)=>{
            return getLocalizedValue(attrValue, locale);
        });
    }
    return [];
};

const mapRegularProduct = (lineItemRaw, settings, intl)=>{
    const product = lineItemRaw.legacyProduct;
    const variant = product.masterData.current.variant;
    const shared = getSharedProps(lineItemRaw, product, intl);
    const slug = getLocalizedString(intl.locale, product.masterData.current.slug);
    const imageUrl = getLineItemImageUrl(variant, settings.staticsUrl, settings.isBynderImagesEnabled);
    const brand = {
        name: getAttribute(variant, 'brand'),
        model: getAttribute(variant, 'brandModel'),
        modelCode: getAttribute(variant, 'brandModelCode')
    };
    const productAttributes = {
        sku: lineItemRaw.sku,
        productSlug: slug,
        productId: product.id,
        productUrl: getProductUrl(settings.formatProductUrl, lineItemRaw, slug, intl.locale),
        brand
    };
    switch(product.productType.toLowerCase()){
        case 'frames':
            return swcHelpers.objectSpread({
                type: 'frames'
            }, shared, productAttributes, {
                imageUrl: getLineItemImageUrl(variant, settings.staticsUrl, settings.isBynderImagesEnabled),
                subtitle: intl.getLabel('products.frames.frameOnly.description')
            });
        case 'solutions':
            return swcHelpers.objectSpread({
                type: 'solutions'
            }, shared, productAttributes, {
                imageUrl,
                solutionType: getAttribute(variant, 'solutionType'),
                solutionVolume: getAttribute(variant, 'volume')
            });
        case 'contact-lenses':
            return swcHelpers.objectSpread({
                type: 'contact-lenses'
            }, shared, productAttributes, {
                imageUrl,
                colorLens: getAttribute(variant, 'color'),
                eyeSide: getEyeSide(lineItemRaw.typeDetails),
                lensType: getLocalizedAttribute(variant, 'lensType', intl.locale) || '',
                material: getLocalizedAttribute(variant, 'material', intl.locale) || '',
                packageQuantity: getAttribute(variant, 'packageQuantity'),
                replacementFrequency: getLocalizedAttribute(variant, 'replacementFrequency', intl.locale),
                sapArticleNumber: getAttribute(variant, 'sapArticleNumber'),
                specs: getContactLensSpecs(variant.attributes)
            });
        case 'sunglasses':
            return swcHelpers.objectSpread({
                type: 'sunglasses'
            }, shared, productAttributes, {
                imageUrl,
                imageAlt: `${brand.name} ${brand.model}`,
                subtitle: `${brand.model} ${brand.modelCode && brand.modelCode !== brand.model ? brand.modelCode : ''}`,
                frame: {
                    frameShape: getLocalizedAttribute(variant, 'frameShape', intl.locale),
                    frameMaterial: getLocalizedAttributes(variant, 'frameMaterial', intl.locale),
                    frameColor: getLocalizedAttributes(variant, 'frameColor', intl.locale)
                },
                gender: getLocalizedAttributes(variant, 'gender', intl.locale)
            });
        case 'ready-readers':
            return swcHelpers.objectSpread({
                type: 'ready-readers'
            }, shared, productAttributes, {
                gender: getLocalizedAttributes(variant, 'gender', intl.locale),
                imageUrl: getLineItemImageUrl(variant, settings.staticsUrl, settings.isBynderImagesEnabled),
                power: getAttribute(variant, 'power')
            });
        case 'accessories':
            return swcHelpers.objectSpread({
                type: 'accessories'
            }, shared, productAttributes, {
                gender: getLocalizedAttributes(variant, 'gender', intl.locale),
                material: getLocalizedAttribute(variant, 'material', intl.locale) || '',
                accessoryType: getLocalizedAttribute(variant, 'accessoryType', intl.locale) || '',
                accessoryColor: getLocalizedAttributes(variant, 'accessoryColor', intl.locale),
                imageUrl: getLineItemImageUrl(variant, settings.staticsUrl, settings.isBynderImagesEnabled)
            });
        default:
            console.log(`Encountered unsupported productType '${product.productType}' with sku '${variant.sku}'`);
            return null;
    }
};
// Maps the server model to client view model
const lineItemDataMapper = (lineItemsRaw, settings, intl)=>{
    const lineItems = lineItemsRaw.map((lineItemRaw)=>{
        switch(lineItemRaw.__typename){
            case 'LineItemBundle':
                return mapBundleProduct(lineItemRaw, settings, intl);
            case 'LineItemContactLenses':
            case 'LineItemRegular':
                return mapRegularProduct(lineItemRaw, settings, intl);
        }
    });
    return lineItems.filter((lineItem)=>Boolean(lineItem)
    );
};

const hasLineItemTypes = (lineItems, producTypes)=>{
    return lineItems.some((item)=>{
        return producTypes === null || producTypes === void 0 ? void 0 : producTypes.includes(item.type);
    });
};

const createContainsHelper = (lineItems)=>{
    return {
        get contactLenses () {
            return hasLineItemTypes(lineItems, [
                'contact-lenses'
            ]);
        },
        get contactLensesSubscription () {
            return hasLineItemTypes(lineItems, [
                'contact-lenses-subscription'
            ]);
        },
        get prescriptionGlasses () {
            return hasLineItemTypes(lineItems, [
                'prescription-glasses'
            ]);
        },
        get solutions () {
            return hasLineItemTypes(lineItems, [
                'solutions'
            ]);
        },
        get sunglasses () {
            return hasLineItemTypes(lineItems, [
                'sunglasses'
            ]);
        },
        get subscription () {
            return hasLineItemTypes(lineItems, [
                'contact-lenses-subscription',
                'contact-lenses-bundle-benelux', 
            ]);
        }
    };
};

const CART_STORAGE_KEY = 'stored_cart';
const initialState = {
    cartId: null,
    cartHash: null,
    cartCount: 0
};
const useCartStorage = ()=>{
    const [data, setData] = useLocalStorage(CART_STORAGE_KEY, initialState);
    React.useDebugValue(data);
    /**
   *  Gql reactive variable is used to always keep latest cartId available
   *  and can be shared with components
   *  Problem :
   *  When component/hook consumes useCartStorage and indirectly updated cartId then latest cartId
   *  is not returned to consumer as it is not direct source of change.
   *  For e.g
   *  createOrder saga is called from StepPaymentContainer and if order fails replicateOrder saga is called.
   *  replicateOrder saga dispatches 'cartChange' event which results in loading new cart using useGetCart hook.
   *  useGetCart hook updates cartId in localstorage via useCartStorage but updated cartId is only available to useGetCart hook.
   *  All other consumers of useCartStorage will have old cartId as there was no re-render.
   *
   *  Solution:
   *  `cartIdVar` will always have latest cart Id, It can be accessed getter signature or via hook
   *   `
   *   import { useReactiveVar } from '@grandvisionhq/graphql'
   *   const cartId = useReactiveVar(cartIdVar)
   *   `
   *   OR
   *   `cartIdVar()`
   *
   */ React.useEffect(()=>{
        cartIdVar(data.cartId);
    }, [
        data.cartId
    ]);
    const setCartId = React.useCallback((cartId)=>{
        cartIdVar(cartId);
        setData((state)=>swcHelpers.objectSpread({}, state, {
                cartId
            })
        );
    }, [
        setData
    ]);
    const setCartHash = React.useCallback((cartHash)=>{
        setData((state)=>swcHelpers.objectSpread({}, state, {
                cartHash
            })
        );
    }, [
        setData
    ]);
    const setCartCount = React.useCallback((newCount)=>{
        if (cartCountVar() !== newCount) {
            cartCountVar(newCount);
        }
        return setData((state)=>{
            if (state.cartCount === newCount) {
                return state;
            }
            return swcHelpers.objectSpread({}, state, {
                cartCount: newCount
            });
        });
    }, [
        setData
    ]);
    const reset = ()=>{
        setData(initialState);
        cartCountVar(0);
    };
    return [
        data,
        {
            setCartId,
            setCartHash,
            setCartCount,
            reset
        }
    ];
};

const useGetCart = ()=>{
    const settings = useCartSettings();
    const [cart, { setCartId , setCartHash , reset  }] = useCartStorage();
    const cartIdRef = React.useRef(cart.cartId);
    const { cartId , cartHash , setContext  } = settings;
    // This is needed to prefill the cart with the customer logic in the back-office flow.
    if (cartId && cartHash && (cart.cartId !== cartId || cart.cartHash !== cartHash)) {
        setCartId(cartId);
        setCartHash(cartHash);
        cartIdRef.current = cartId;
    }
    const [getCart, { called , loading , data: data1 , error , refetch  }] = useLazyQuery(GET_CART, {
        onCompleted: (data)=>{
            const unsolvableErrorCodes = getUnrecoverableValidationErrors(data.getCart.validationErrors);
            if (unsolvableErrorCodes.length) {
                // If there are unsolvable validation errors, reset the cart
                reset();
            }
        },
        onError: console.error,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        context: {
            forwardHeaders: {
                'cart-hash': cart.cartHash
            }
        }
    });
    const refetchCallback = React.useCallback(()=>{
        if (cartIdRef.current) {
            // Refetch is undefined if the initial run has not been executed
            refetch ? refetch({
                id: cartIdRef.current
            }) : getCart({
                variables: {
                    id: cartIdRef.current
                }
            });
        }
    }, [
        getCart,
        refetch
    ]);
    React.useEffect(()=>{
        if (cartIdRef.current) {
            getCart({
                variables: {
                    id: cartIdRef.current
                }
            });
        }
    }, [
        getCart
    ]);
    const fetchByCartId = React.useCallback((cartIdParam, cartHashParam)=>{
        // Set cartId & carthash in localstorage
        setCartId(cartIdParam);
        setCartHash(cartHashParam || null);
        // update react context with latest cartId & carthash
        setContext && setContext({
            cartId: cartIdParam,
            cartHash: cartHashParam || undefined
        });
        // fetch cart based on latest cartId & carthash without relying on localstorage or context
        if (cartIdParam) {
            refetch ? refetch({
                id: cartIdParam
            }) : getCart(swcHelpers.objectSpread({
                variables: {
                    id: cartIdParam
                }
            }, cartHashParam && {
                context: {
                    forwardHeaders: {
                        'cart-hash': cartHashParam
                    }
                }
            }));
        }
    }, [
        setCartId,
        setCartHash,
        setContext,
        refetch,
        getCart
    ]);
    return {
        called,
        loading,
        data: !!cart.cartId ? data1 : null,
        error,
        refetch: refetchCallback,
        fetchByCartId
    };
};

/**
 * @description Not all of the validations codes are meant/supported to be displayed
 *    as unavailable in cart so far.
 *  When a new type is supported, this array should be updated.
 */ const UnavailableLineItemCodes = [
    ValidationCode.PRODUCTS_ARE_NOT_EXCLUDED_FROM_CHECKOUT,
    ValidationCode.IS_IN_STOCK, 
];
function getValidationErrorsByLineItem(validationInput) {
    if (!validationInput) {
        return undefined;
    }
    const lineItemValidationErrors = validationInput.cartLineItemErrors;
    if (!lineItemValidationErrors) {
        return undefined;
    }
    const unavailableLineItems = {};
    for (const lineItem of lineItemValidationErrors){
        const codes = lineItem.errors.map((error)=>error.validationCode
        ).filter((code)=>UnavailableLineItemCodes.includes(code)
        );
        if (codes.length > 0) {
            unavailableLineItems[lineItem.itemId] = codes;
        }
    }
    return Object.keys(unavailableLineItems).length > 0 ? unavailableLineItems : undefined;
}

const CartProvider = ({ children  })=>{
    var ref3, ref1;
    const { called , refetch , loading , data , error  } = useGetCart();
    const settings = useCartSettings();
    const intl = useIntl();
    const cart1 = React.useMemo(()=>{
        var ref, ref2;
        if (data === null) {
            return null;
        }
        if (!data) {
            return undefined;
        }
        const cart = data === null || data === void 0 ? void 0 : (ref = data.getCart) === null || ref === void 0 ? void 0 : ref.cart;
        const lineItemsRaw = cart.lineItems;
        const lineItems = lineItemDataMapper(lineItemsRaw, settings, intl);
        return {
            id: cart.id,
            numberOfItems: cart.numberOfItems,
            lineItems,
            validationErrors: getValidationErrorsByLineItem(data === null || data === void 0 ? void 0 : (ref2 = data.getCart) === null || ref2 === void 0 ? void 0 : ref2.validationErrors),
            prices: getCartPrices(cart.price),
            discountDescriptions: getDiscountDescriptions(cart.appliedDiscounts, intl.locale),
            voucherCodes: getActiveVoucherCodes(cart.vouchers),
            subscription: getSubscriptionDetails(cart),
            isAccountRequired: !!cart.isAccountRequired,
            billingAddress: getAddress(cart.billingAddress),
            shippingAddress: getAddress(cart.shippingAddress),
            shippingMethod: getShippingMethod(cart.shippingMethod),
            customerDetails: getCustomerDetails(cart.customerDetails),
            checkoutState: cart.checkoutState,
            contains: createContainsHelper(lineItems)
        };
    // we need data?.getCart.cart as a dependency, data doesn't change on cart updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        data,
        data === null || data === void 0 ? void 0 : (ref3 = data.getCart) === null || ref3 === void 0 ? void 0 : ref3.cart,
        data === null || data === void 0 ? void 0 : (ref1 = data.getCart) === null || ref1 === void 0 ? void 0 : ref1.validationErrors,
        intl,
        settings
    ]);
    const value = React.useMemo(()=>({
            cart: cart1,
            refetch,
            loading,
            error,
            called
        })
    , [
        cart1,
        refetch,
        called,
        loading,
        error, 
    ]);
    if (typeof children === 'function') {
        return /*#__PURE__*/ React.createElement(CartContext.Provider, {
            value: value
        }, children(value));
    }
    return /*#__PURE__*/ React.createElement(CartContext.Provider, {
        value: value
    }, children);
};

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  useCart: useCart,
  CartContext: CartContext,
  CartProvider: CartProvider,
  useCartSettings: useCartSettings,
  CartSettingsProvider: CartSettingsProvider
});

const convertAttributeToLowerCase = (attribute)=>{
    if (!attribute) {
        return '';
    }
    return Array.isArray(attribute) ? attribute.join().toLowerCase() : attribute.toLowerCase();
};
const calcPrice = (centAmount)=>{
    const price = !isNaN(centAmount) ? (centAmount / 100).toFixed(2).toString() : '0.00';
    if (price === '0') {
        return '0.00';
    }
    return price;
};
const createPriceSpecification = (lineItem)=>{
    const { unit  } = lineItem.price;
    return {
        '@type': 'CompoundPriceSpecification',
        name: 'Price',
        price: String(calcPrice(unit.gross)),
        priceCurrency: lineItem.currency,
        validFrom: lineItem.discount.validFrom,
        validThrough: lineItem.discount.validUntil,
        valueAddedTaxIncluded: true,
        priceComponent: [
            {
                '@type': 'UnitPriceSpecification',
                name: 'Price excluding VAT',
                price: String(calcPrice(unit.net)),
                valueAddedTaxIncluded: false
            },
            {
                '@type': 'UnitPriceSpecification',
                name: 'VAT',
                price: String(calcPrice(unit.taxAmount)),
                valueAddedTaxIncluded: true
            }, 
        ]
    };
};
const createOfferObject = (lineItem)=>{
    return {
        '@type': 'Offer',
        includesObject: {
            '@type': 'TypeAndQuantityNode',
            amountOfThisGood: lineItem.quantity || 1
        },
        priceSpecification: createPriceSpecification(lineItem)
    };
};
const createProductObject = (lineItem)=>{
    let url = window.location && window.location.origin || '';
    if (lineItem.productUrl) {
        url += lineItem.productUrl;
    }
    if (lineItem.type === 'prescription-glasses') {
        const { frame , name  } = lineItem;
        const { brand , productId , sku  } = frame;
        return {
            '@type': 'Product',
            '@id': `${url}#product`,
            category: 'Optical',
            url,
            brand: brand.name,
            name,
            sku,
            gtin13: sku,
            productID: productId,
            offers: [
                createOfferObject(lineItem)
            ]
        };
    }
    const { name , sku , productId , type , brand  } = lineItem;
    const frame = lineItem.type === 'sunglasses' ? lineItem.frame : null;
    return {
        '@type': 'Product',
        '@id': `${url}#product`,
        url,
        model: convertAttributeToLowerCase(frame === null || frame === void 0 ? void 0 : frame.frameShape),
        name,
        productID: productId,
        gtin13: sku,
        category: type,
        brand: brand.name,
        material: convertAttributeToLowerCase(frame === null || frame === void 0 ? void 0 : frame.frameMaterial),
        color: convertAttributeToLowerCase(frame === null || frame === void 0 ? void 0 : frame.frameColor),
        offers: [
            createOfferObject(lineItem)
        ]
    };
};

const getSubscriptionType = (lineItem)=>{
    return lineItem.type === 'contact-lenses-bundle-benelux' ? DataLayer.DataLayerTypes.PurchaseType.Subscription : DataLayer.DataLayerTypes.PurchaseType.OneTime;
};
const getNameFromBrand = (brand)=>{
    const name = [
        ...new Set([
            brand.name,
            brand.model,
            brand.modelCode
        ].filter(Boolean))
    ].join(' ').trim();
    return name;
};
const getSharedAttributes = (lineItem, lineItemType, quantity = 1)=>{
    const { brand , sku , price  } = lineItem;
    return {
        name: getNameFromBrand(brand),
        id: sku,
        price: calcPrice$1(price.unit.gross),
        dimension143: getSubscriptionType(lineItem),
        dimension144: lineItemType,
        dimension145: 'true',
        metric140: calcPrice$1(price.unit.original),
        metric141: calcPrice$1(price.unit.discount),
        metric142: calcPrice$1(price.unit.taxAmount),
        quantity
    };
};
const getSunglassesAttributes = (lineItem, quantity)=>{
    var ref, ref1, ref2;
    const { brand , frame , gender , sku  } = lineItem;
    const genderValue = gender && gender.map((value)=>value.toLowerCase()
    ).join();
    return swcHelpers.objectSpread({}, getSharedAttributes(lineItem, DataLayer.DataLayerTypes.LineItemType.Single, quantity), {
        category: DataLayer.DataLayerTypes.Category.Sunglasses,
        brand: brand.name || '',
        variant: (frame === null || frame === void 0 ? void 0 : (ref = frame.frameShape) === null || ref === void 0 ? void 0 : ref.toLowerCase()) || '',
        dimension16: genderValue || '',
        dimension140: sku,
        dimension146: (frame === null || frame === void 0 ? void 0 : (ref1 = frame.frameMaterial) === null || ref1 === void 0 ? void 0 : ref1.join().toLowerCase()) || '',
        dimension147: (frame === null || frame === void 0 ? void 0 : (ref2 = frame.frameColor) === null || ref2 === void 0 ? void 0 : ref2.join().toLowerCase()) || ''
    });
};
const getReadyReadersAttributes = (lineItem, quantity = 1)=>{
    var ref, ref3, ref4;
    const { brand , frame , gender , sku  } = lineItem;
    const genderValue = gender && gender.map((value)=>value.toLowerCase()
    ).join();
    return swcHelpers.objectSpread({}, getSharedAttributes(lineItem, DataLayer.DataLayerTypes.LineItemType.Single, quantity), {
        category: DataLayer.DataLayerTypes.Category.ReadyReaders,
        brand: brand.name || '',
        variant: (frame === null || frame === void 0 ? void 0 : (ref = frame.frameShape) === null || ref === void 0 ? void 0 : ref.toLowerCase()) || '',
        dimension16: genderValue || '',
        dimension140: sku,
        dimension146: (frame === null || frame === void 0 ? void 0 : (ref3 = frame.frameMaterial) === null || ref3 === void 0 ? void 0 : ref3.join().toLowerCase()) || '',
        dimension147: (frame === null || frame === void 0 ? void 0 : (ref4 = frame.frameColor) === null || ref4 === void 0 ? void 0 : ref4.join().toLowerCase()) || ''
    });
};
const getAccessoriesLineItem = (lineItem, quantity = 1)=>{
    const { brand , accessoryType , accessoryColor , material , gender , sku  } = lineItem;
    const genderValue = gender && gender.map((value)=>value.toLowerCase()
    ).join();
    return swcHelpers.objectSpread({}, getSharedAttributes(lineItem, DataLayer.DataLayerTypes.LineItemType.Single, quantity), {
        category: DataLayer.DataLayerTypes.Category.Accessories,
        brand: brand.name || '',
        variant: accessoryType || '',
        dimension16: genderValue || '',
        dimension140: sku,
        dimension146: material || '',
        dimension147: (accessoryColor === null || accessoryColor === void 0 ? void 0 : accessoryColor.join().toLowerCase()) || ''
    });
};
const getContactLensesAttributes = (lineItem, quantity)=>{
    var ref;
    return swcHelpers.objectSpread({}, getSharedAttributes(lineItem, DataLayer.DataLayerTypes.LineItemType.Single, quantity), {
        name: lineItem.name,
        category: DataLayer.DataLayerTypes.Category.ContactLenses,
        brand: lineItem.brand.name || '',
        variant: lineItem.lensType || '',
        dimension140: lineItem.sku,
        dimension146: lineItem.material || '',
        dimension148: ((ref = lineItem.replacementFrequency) === null || ref === void 0 ? void 0 : ref.toLowerCase()) || ''
    });
};
const getSolutionsAttributes = (lineItem, quantity)=>swcHelpers.objectSpread({}, getSharedAttributes(lineItem, DataLayer.DataLayerTypes.LineItemType.Single, quantity), {
        name: lineItem.name,
        category: DataLayer.DataLayerTypes.Category.Solutions,
        brand: lineItem.brand.name || '',
        variant: lineItem.solutionVolume + 'ml',
        dimension140: lineItem.sku,
        dimension146: lineItem.solutionType
    })
;
const getContactLensesBundleBeneluxAttributes = (lineItem, quantity)=>swcHelpers.objectSpread({}, getSharedAttributes(lineItem, DataLayer.DataLayerTypes.LineItemType.Bundle, quantity), {
        category: DataLayer.DataLayerTypes.Category.ContactLenses,
        variant: lineItem.lensType
    })
;
const getPrescriptionGlassesAttributes = (lineItem, quantity = 1)=>{
    var ref;
    const { frame , price , productType  } = lineItem;
    const { brand  } = frame;
    const category = productType === 'sunglasses' ? DataLayer.DataLayerTypes.Category.Sunglasses : DataLayer.DataLayerTypes.Category.Optical;
    const name = getNameFromBrand(brand);
    return {
        category,
        name,
        brand: frame.brand.name || '',
        id: frame.sku,
        price: calcPrice$1(price.unit.gross),
        variant: ((ref = frame.frameShape) === null || ref === void 0 ? void 0 : ref.toLowerCase()) || '',
        dimension16: frame.gender || '',
        dimension140: frame.sku,
        dimension143: getSubscriptionType(lineItem),
        dimension144: DataLayer.DataLayerTypes.LineItemType.Bundle,
        dimension145: 'true',
        dimension146: frame.frameMaterial || '',
        dimension147: frame.frameColor || '',
        metric140: calcPrice$1(price.unit.original),
        metric141: calcPrice$1(price.unit.discount),
        metric142: calcPrice$1(price.unit.taxAmount),
        quantity
    };
};
/* eslint @typescript-eslint/switch-exhaustiveness-check: "warn" */ const mapClientModelToProductObject = (lineItem, quantity)=>{
    switch(lineItem.type){
        case 'sunglasses':
            return getSunglassesAttributes(lineItem, quantity);
        case 'contact-lenses':
            return getContactLensesAttributes(lineItem, quantity);
        case 'solutions':
            return getSolutionsAttributes(lineItem, quantity);
        case 'contact-lenses-bundle-benelux':
            return getContactLensesBundleBeneluxAttributes(lineItem, quantity);
        case 'prescription-glasses':
            return getPrescriptionGlassesAttributes(lineItem, quantity);
        case 'ready-readers':
            return getReadyReadersAttributes(lineItem, quantity);
        case 'accessories':
            return getAccessoriesLineItem(lineItem, quantity);
    }
};

const mapLineItemToDataLayerEvent = (eventType, lineItems, cartId)=>{
    const currency = lineItems[0].currency;
    //TODO: Fix it properly with ticket
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch(eventType){
        case 'add':
            return {
                event: 'gvAddToCart',
                atfEcommerce: {
                    ecommerce: {
                        currencyCode: currency,
                        cart: {
                            id: cartId
                        },
                        add: {
                            products: lineItems.map((lineItem)=>mapClientModelToProductObject(lineItem, lineItem.quantity)
                            )
                        }
                    }
                }
            };
        case 'remove':
            return {
                event: 'gvRemoveFromCart',
                atfEcommerce: {
                    ecommerce: {
                        currencyCode: currency,
                        cart: {
                            id: cartId
                        },
                        remove: {
                            products: lineItems.map((lineItem)=>mapClientModelToProductObject(lineItem, lineItem.quantity)
                            )
                        }
                    }
                }
            };
    }
};

const errorsMapping = {
    CART_IS_EMPTY: 'errors.cartIsEmpty',
    MISSING_CART_ID: 'errors.missingCartID',
    SOMETHING_WENT_WRONG: 'errors.somethingWentWrong',
    CART_NOT_FOUND: 'errors.cartNotFound'
};

const mapContactLensesLineItem = ({ sku , quantity , typeDetails  })=>{
    const { eyeSide , prescriptionExpiryDate , externalOptician  } = typeDetails;
    return {
        sku,
        quantity: quantity !== null && quantity !== void 0 ? quantity : 1,
        lineItemType: LineItemType.CONTACTLENSES,
        typeDetails: {
            eyeSide: eyeSide === 'left' ? EyeSide.LEFT : EyeSide.RIGHT,
            prescriptionExpiryDate,
            externalOptician
        }
    };
};

const mapContactLensesBundleBeneluxLineItem = ({ sku , quantity , typeDetails  })=>{
    const { leftLensBaseCurve , leftLensDiameter , leftLensPower , leftLensAxis , leftLensCylinder ,  } = typeDetails;
    const { rightLensBaseCurve , rightLensDiameter , rightLensPower , rightLensAxis , rightLensCylinder ,  } = typeDetails;
    return {
        sku,
        quantity: quantity !== null && quantity !== void 0 ? quantity : 1,
        lineItemType: LineItemType.BUNDLE,
        bundleLineItemsType: BundleLineItemType.CONTACT_LENSES_BENELUX,
        typeDetails: {
            prescription: {
                rightLens: {
                    baseCurve: rightLensBaseCurve,
                    diameter: rightLensDiameter,
                    power: rightLensPower,
                    axis: rightLensAxis,
                    cylinder: rightLensCylinder
                },
                leftLens: {
                    baseCurve: leftLensBaseCurve,
                    diameter: leftLensDiameter,
                    power: leftLensPower,
                    axis: leftLensAxis,
                    cylinder: leftLensCylinder
                }
            }
        }
    };
};

const MAIN_LINE_ITEM_INDEX = 0;
const mapContactLensesSubscriptionLineItem = ({ bundleLineItems , quantity , typeDetails  })=>{
    if (!bundleLineItems.every((item)=>Boolean(item.ratePlanId)
    )) {
        console.error(`All of the bundleLineItems must have a ratePlanId defined.`);
        return null;
    }
    var _quantity;
    return {
        sku: bundleLineItems[MAIN_LINE_ITEM_INDEX].sku,
        quantity: quantity !== null && quantity !== void 0 ? quantity : 1,
        lineItemType: LineItemType.BUNDLE,
        bundleLineItemsType: BundleLineItemType.CONTACT_LENSES_SUBSCRIPTION,
        bundleLineItems: bundleLineItems.map((item, index)=>swcHelpers.objectSpread({}, item, {
                type: BundleLineItemProductType[item.type.toUpperCase()],
                quantity: (_quantity = item.quantity) !== null && _quantity !== void 0 ? _quantity : 1,
                isMainBundleLineItem: index === MAIN_LINE_ITEM_INDEX
            })
        ),
        typeDetails: swcHelpers.objectSpread({}, typeDetails, {
            billingFrequency: BillingFrequency[typeDetails.billingFrequency.toUpperCase()],
            deliveryFrequency: DeliveryFrequency[typeDetails.deliveryFrequency.toUpperCase()]
        })
    };
};

const mapNonPrescriptionBundleLineItem = ({ sku , quantity , bundleLineItems  })=>{
    const mainBundleLineItem = {
        sku,
        quantity: 1,
        isMainBundleLineItem: true
    };
    var _quantity;
    return {
        sku,
        quantity: quantity !== null && quantity !== void 0 ? quantity : 1,
        lineItemType: LineItemType.BUNDLE,
        bundleLineItemsType: BundleLineItemType.NON_PRESCRIPTION_BUNDLE,
        bundleLineItems: [
            mainBundleLineItem,
            ...bundleLineItems.map((item)=>swcHelpers.objectSpread({}, item, {
                    quantity: (_quantity = item.quantity) !== null && _quantity !== void 0 ? _quantity : 1,
                    isMainBundleLineItem: false
                })
            ), 
        ]
    };
};

function getPrescriptionGlassesDetails(prescription) {
    switch(prescription.type){
        case 'email-prescription':
            return {
                isEmailPrescription: true
            };
        case 'existing-customer':
            return {
                isExistingCustomer: true
            };
        case 'new-prescription':
            return {
                newPrescription: swcHelpers.objectSpread({}, prescription.details)
            };
        case 'prescription-file':
            {
                const { prescriptionFile , pupillaryDistanceFile  } = prescription.details;
                return {
                    prescriptionFile: {
                        fileName: prescriptionFile.fileName,
                        storageToken: prescriptionFile.storageToken
                    },
                    pupillaryDistanceFile: {
                        fileName: pupillaryDistanceFile.fileName,
                        storageToken: pupillaryDistanceFile.storageToken
                    }
                };
            }
    }
}
const mapPrescriptionGlassesLineItem = ({ sku , quantity , bundleLineItems , prescription  })=>{
    const mainBundleLineItem = {
        sku,
        quantity: 1,
        isMainBundleLineItem: true
    };
    var _quantity;
    return {
        sku,
        quantity: quantity !== null && quantity !== void 0 ? quantity : 1,
        lineItemType: LineItemType.BUNDLE,
        bundleLineItemsType: BundleLineItemType.PRESCRIPTION_GLASSES,
        bundleLineItems: [
            mainBundleLineItem,
            ...bundleLineItems.map((item)=>swcHelpers.objectSpread({}, item, {
                    quantity: (_quantity = item.quantity) !== null && _quantity !== void 0 ? _quantity : 1,
                    isMainBundleLineItem: false
                })
            ), 
        ],
        typeDetails: getPrescriptionGlassesDetails(prescription)
    };
};

const mapRegularLineItem = ({ sku , quantity  })=>({
        sku,
        quantity: quantity !== null && quantity !== void 0 ? quantity : 1,
        lineItemType: LineItemType.REGULAR
    })
;

// Map line items to the server model
const mapDraftLineItemToServer = (lineItemDraft)=>{
    switch(lineItemDraft.type){
        case 'contact-lenses':
            return mapContactLensesLineItem(lineItemDraft);
        case 'contact-lenses-bundle-benelux':
            return mapContactLensesBundleBeneluxLineItem(lineItemDraft);
        case 'contact-lenses-subscription':
            return mapContactLensesSubscriptionLineItem(lineItemDraft);
        case 'prescription-glasses':
            return mapPrescriptionGlassesLineItem(lineItemDraft);
        case 'non-prescription-bundle':
            return mapNonPrescriptionBundleLineItem(lineItemDraft);
        case 'regular':
            return mapRegularLineItem(lineItemDraft);
    }
};

const miniCartEventType = 'mini-cart';
const dispatchMiniCartEvent = (type, lineItems)=>{
    if (window) {
        window.dispatchEvent(new CustomEvent(miniCartEventType, {
            detail: {
                type,
                lineItems
            }
        }));
    }
};
const useMiniCartListener = (handler)=>{
    return useEventListener(miniCartEventType, handler);
};

const useAddToCart = ()=>{
    const intl = useIntl();
    const { locale , currency , country  } = intl;
    const settings = useCartSettings();
    const [cart, { setCartId , setCartCount , setCartHash  }] = useCartStorage();
    const [createCart, createCartMutation] = useMutation(CREATE_CART);
    const [addCartLineItems, addCartLineItemsMutation] = useMutation(ADD_CART_LINE_ITEMS);
    const handleOnCompleted = (addedLineItemDrafts, data, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent)=>{
        let result;
        if ('addCartLineItems' in data) {
            result = data.addCartLineItems;
        }
        if ('createCart' in data) {
            result = data.createCart;
        }
        if (result.validationErrors) {
            const solvableValidationErrors = getSolvableValidationErrors(result.validationErrors);
            if (solvableValidationErrors.length > 0 && onSolvableValidationError) {
                onSolvableValidationError(solvableValidationErrors);
                return;
            }
            const validationErrors = getValidationErrorCodes(result.validationErrors);
            onValidationError && onValidationError(validationErrors);
            if (!result.success) {
                return;
            }
        }
        if (!result.cart) {
            return onError(errorsMapping.CART_IS_EMPTY);
        }
        const { cartHash  } = result;
        const { id , numberOfItems , lineItems  } = result.cart;
        setCartId(id);
        setCartCount(numberOfItems);
        if (cartHash) {
            setCartHash(cartHash);
        }
        const addedLineItems = getAddedLineItems(lineItems, addedLineItemDrafts);
        const addedLineItemsMapped = lineItemDataMapper(addedLineItems, settings, intl);
        const SdProductObjects = addedLineItemsMapped.map((lineItem)=>createProductObject(lineItem)
        );
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.add, addedLineItemsMapped, id);
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
        pushRemoveLineItemDataLayerEvent && pushRemoveLineItemDataLayerEvent();
        dispatchMiniCartEvent('added', addedLineItemsMapped);
        CartV2Actions.addToCartSdAction(SdProductObjects);
        onSuccess && onSuccess(addedLineItemsMapped);
    };
    const addToCart = React__default.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(lineItemDrafts, onSuccess, onValidationError, onError = ()=>undefined
        , onSolvableValidationError, sanitizationItems, pushRemoveLineItemDataLayerEvent, options = {}) {
            let lineItems = lineItemDrafts.map(mapDraftLineItemToServer).filter(isNotNull);
            if (!lineItems.length) {
                return onError(errorsMapping.SOMETHING_WENT_WRONG);
            }
            if (sanitizationItems) {
                lineItems = appendSanitizationItems(lineItems, sanitizationItems);
            }
            /**
       * On the PDP, there will be multiple instances of this hook (useAddToCart)
       * running. This is causing a mismatch in the StoredCart data value that is returned
       * from localStorage. Pending rethinking how to fix this issue properly, we'll
       * let this hook itself get the StoredCart value (containing cartId) from localStorage
       * instead of relying on the useCartStorage hook (which might return an old StoredCart value).
       */ let localCartData = null;
            try {
                const localStorageCartData = window.localStorage.getItem(CART_STORAGE_KEY);
                localCartData = isNotNull(localStorageCartData) ? JSON.parse(localStorageCartData) : null;
            } catch (err) {
                console.error(`Failed retrieving cart data from localStorage. Error: ${err}`);
            }
            const { prescriptionSignatures  } = options;
            try {
                const { data , errors  } = yield isNotNull(localCartData) && isNotNull(localCartData.cartId) ? addCartLineItems({
                    variables: {
                        cartInput: {
                            cartId: localCartData.cartId,
                            lineItems
                        }
                    }
                }) : createCart({
                    variables: {
                        cartInput: {
                            locale,
                            currency,
                            country,
                            lineItems,
                            prescriptionSignatures
                        }
                    }
                });
                if (errors) {
                    onError(errorsMapping.SOMETHING_WENT_WRONG);
                }
                if (data) {
                    handleOnCompleted(lineItemDrafts, data, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent);
                }
            } catch (error) {
                onError(errorsMapping.SOMETHING_WENT_WRONG);
            }
        });
        return function(lineItemDrafts, onSuccess, onValidationError) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart
    ]);
    const { called , loading , error , data: data1  } = cart.cartId ? addCartLineItemsMutation : createCartMutation;
    let newCart;
    if (data1 && 'createCart' in data1) {
        newCart = data1.createCart.cart;
    }
    if (data1 && 'addCartLineItems' in data1) {
        newCart = data1.addCartLineItems.cart;
    }
    return [
        addToCart,
        {
            called,
            loading,
            error,
            cart: newCart
        }
    ];
};

// When a voucher code is added to the cart but it does not actually apply to any lineItems
// then it will show up in the `vouchers` array but _not_ in the `appliedDiscounts`.
// This function returns all of the vouchers that are currently not applicable to any lineItems
const getUnappliedVoucherCodes = (vouchers, appliedDiscounts)=>{
    return vouchers === null || vouchers === void 0 ? void 0 : vouchers.filter((voucher)=>!appliedDiscounts.some(({ code  })=>code === voucher.code
        )
    );
};

const useRemoveFromCart = (lineItem, onSuccess)=>{
    const [{ cartId  }, { setCartCount  }] = useCartStorage();
    const [removeCartItems, removeCartLineItemsMutation] = useMutation(REMOVE_CART_LINE_ITEMS, {
        onCompleted: ()=>{
            const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.remove, [
                lineItem
            ], cartId || '');
            dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
            CartV2Actions.removeLineItemsSdAction([
                createProductObject(lineItem)
            ]);
            onSuccess && onSuccess();
        }
    });
    const removeFromCart = React__default.useCallback(()=>{
        if (isNil(cartId)) {
            console.error('No cart ID available');
            return;
        }
        removeCartItems({
            variables: {
                cartInput: {
                    cartId,
                    lineItems: [
                        {
                            id: lineItem.id
                        }, 
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.removeCartLineItems.success === true) {
                    var ref;
                    const newCart = data.removeCartLineItems.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: newCart
                    });
                    if (newCart === null || newCart === void 0 ? void 0 : (ref = newCart.vouchers) === null || ref === void 0 ? void 0 : ref.length) {
                        const unappliedVouchers = getUnappliedVoucherCodes(newCart === null || newCart === void 0 ? void 0 : newCart.vouchers, newCart === null || newCart === void 0 ? void 0 : newCart.appliedDiscounts);
                        if (unappliedVouchers === null || unappliedVouchers === void 0 ? void 0 : unappliedVouchers.length) {
                            toast.failed('cart.validations.discountcode.doesnotmatchcart');
                        }
                    }
                    setCartCount((newCart === null || newCart === void 0 ? void 0 : newCart.numberOfItems) || 0);
                }
            }
        });
    }, [
        lineItem
    ]);
    const { called , loading , error  } = removeCartLineItemsMutation;
    return [
        removeFromCart,
        {
            called,
            loading,
            error
        }
    ];
};

const getSanitizationPayloads = (actionItem)=>{
    const { action , actioneeSKU , lineItemId  } = actionItem;
    return {
        action,
        actioneeSKU,
        lineItemId
    };
};
const getConflictingLineItems = (sanitizationPayloads, cart)=>{
    if (!cart || !sanitizationPayloads) {
        return null;
    }
    const conflictingLineItemIds = sanitizationPayloads.map((payload)=>payload.lineItemId
    );
    return cart.lineItems.filter((lineItem)=>conflictingLineItemIds.find((id)=>id === lineItem.id
        )
    );
};
const useSanitization = ()=>{
    const [isModalOpen, setIsModalOpen] = React__default.useState(false);
    const [validation, setValidation] = React__default.useState(null);
    const [sanitizationPayloads1, setSanitizationPayloads] = React__default.useState(null);
    const { cart  } = useCart();
    const conflictingLineItems = React__default.useRef(null);
    const data = {
        sanitizationPayloads: sanitizationPayloads1,
        isModalOpen,
        validation
    };
    const onSolvableValidationError = React__default.useCallback((solvableValidationErrors)=>{
        if (solvableValidationErrors.length > 0) {
            const actions = solvableValidationErrors.map((error)=>error.actionItem.action
            );
            /** When all sanitization actions are the same, confirm will resolve all validation errors at once */ if (solvableValidationErrors.length > 1 && actions.every((val, _i, arr)=>val === arr[0]
            )) {
                const sanitizationPayloads = solvableValidationErrors.map((error)=>getSanitizationPayloads(error.actionItem)
                );
                conflictingLineItems.current = getConflictingLineItems(sanitizationPayloads, cart);
                setValidation({
                    validationError: solvableValidationErrors[0],
                    singleValidation: false
                });
                setSanitizationPayloads(sanitizationPayloads);
            } else {
                const sanitizationPayloads = [
                    getSanitizationPayloads(solvableValidationErrors[0].actionItem), 
                ];
                conflictingLineItems.current = getConflictingLineItems(sanitizationPayloads, cart);
                setValidation({
                    validationError: solvableValidationErrors[0],
                    singleValidation: true
                });
                setSanitizationPayloads(sanitizationPayloads);
            }
            setIsModalOpen(true);
        }
    }, [
        cart
    ]);
    const pushRemoveLineItemDataLayerEvent = React__default.useCallback(()=>{
        const lineItems = conflictingLineItems.current;
        if (!lineItems) {
            return;
        }
        const removeFromCartEvent = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.remove, lineItems, (cart === null || cart === void 0 ? void 0 : cart.id) || '');
        removeFromCartEvent && DataLayer.pushEcommerceEvent(removeFromCartEvent);
    }, [
        cart === null || cart === void 0 ? void 0 : cart.id
    ]);
    const reset = React__default.useCallback(()=>{
        setIsModalOpen(false);
        setValidation(null);
        setSanitizationPayloads(null);
        conflictingLineItems.current = null;
    }, [
        setIsModalOpen,
        setValidation,
        setSanitizationPayloads
    ]);
    return [
        data,
        {
            onSolvableValidationError,
            pushRemoveLineItemDataLayerEvent,
            reset
        }, 
    ];
};

const useUpdateLineItemQuantity = (lineItem)=>{
    const [{ cartId  }, { setCartCount  }] = useCartStorage();
    const [quantityDiff, setQuantityDiff] = React.useState(0);
    const handleValidationErrors = (validationErrors)=>{
        validationErrors.forEach((validationError)=>toast.failed(`cart.${validationError}`)
        );
    };
    const [updateCartItems, updateQuantityMutation] = useMutation(UPDATE_CART_LINE_ITEMS, {
        onCompleted: ()=>{
            if (quantityDiff === 0) {
                return;
            }
            const id = cartId || '';
            const eventType = quantityDiff > 0 ? DataLayer.DataLayerTypes.EventType.add : DataLayer.DataLayerTypes.EventType.remove;
            const dataLayerEvents = mapLineItemToDataLayerEvent(eventType, [
                swcHelpers.objectSpread({}, lineItem, {
                    quantity: Math.abs(quantityDiff)
                })
            ], id);
            dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
            CartV2Actions.updateQuantitySdAction([
                {
                    object: createProductObject(lineItem),
                    updateType: quantityDiff > 0 ? 'increase' : 'decrease'
                }, 
            ]);
        },
        refetchQueries: [
            'GetCart'
        ]
    });
    const updateItemQuantity = React.useCallback((updatedQuantity)=>{
        if (isNil(cartId)) {
            console.error('No cart ID available');
            return;
        }
        setQuantityDiff(updatedQuantity - lineItem.quantity);
        updateCartItems({
            variables: {
                cartInput: {
                    cartId,
                    lineItems: [
                        {
                            id: lineItem.id,
                            quantity: updatedQuantity
                        }, 
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.updateCartLineItems.success === true) {
                    const newCart = data.updateCartLineItems.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: newCart
                    });
                    setCartCount((newCart === null || newCart === void 0 ? void 0 : newCart.numberOfItems) || 0);
                }
                if (data && !data.updateCartLineItems.success) {
                    const validationErrors = getValidationErrorCodes(data.updateCartLineItems.validationErrors);
                    handleValidationErrors(validationErrors);
                }
            }
        });
    }, [
        cartId,
        lineItem,
        setCartCount,
        updateCartItems
    ]);
    const { called , loading , error  } = updateQuantityMutation;
    return [
        updateItemQuantity,
        {
            called,
            loading,
            error
        }
    ];
};

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  useAddToCart: useAddToCart,
  useCartStorage: useCartStorage,
  useGetCart: useGetCart,
  useRemoveFromCart: useRemoveFromCart,
  useSanitization: useSanitization,
  useUpdateLineItemQuantity: useUpdateLineItemQuantity
});

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createProductObject: createProductObject,
  mapClientModelToProductObject: mapClientModelToProductObject,
  mapLineItemToDataLayerEvent: mapLineItemToDataLayerEvent
});

const AddToCart$1 = (props)=>/*#__PURE__*/ React.createElement(Button, swcHelpers.extends({
        size: "custom",
        "data-t": "add-to-cart",
        className: "add-to-cart"
    }, props))
;

const ActionButtons$1 = ({ handleOnAction , handleOnCancel , isDisabled , labels ,  })=>/*#__PURE__*/ React.createElement("div", {
        className: 'confirmation-modal__action-buttons'
    }, /*#__PURE__*/ React.createElement(Button, {
        "aria-label": labels.cancelButton,
        caption: labels.cancelButton,
        className: 'confirmation-modal__action-button',
        onClick: handleOnCancel,
        size: "custom",
        variant: "outline"
    }), /*#__PURE__*/ React.createElement(Button, {
        "aria-label": labels.actionButton,
        caption: labels.actionButton,
        className: 'confirmation-modal__action-button',
        onClick: handleOnAction,
        disabled: isDisabled,
        size: "custom"
    }))
;

const RequiredActionMessage = ({ information , action  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Text, null, information), /*#__PURE__*/ React.createElement(Text, null, action))
;

const getConfirmationModalLabels = (getFormattedLabel, getLabel, validation, lineItems)=>{
    var ref;
    let title, sanitizationAction;
    const { action , lineItemId  } = validation.validationError.actionItem;
    const actionKey = action.toLowerCase();
    const validationErrorCode = validationErrorLabelMap[validation.validationError.validationCode];
    const conflictingProductType = ((ref = lineItems === null || lineItems === void 0 ? void 0 : lineItems.find((lineItem)=>lineItem.id === lineItemId
    )) === null || ref === void 0 ? void 0 : ref.displayType) || '';
    if (validation.singleValidation && conflictingProductType) {
        title = getLabel(`cart.sanitizationAction.${actionKey}.title`);
        sanitizationAction = getFormattedLabel(`cart.sanitizationAction.actionDescription`, {
            labelData: {
                action: actionKey,
                productType: conflictingProductType
            }
        });
    } else {
        title = getLabel(`cart.sanitizationAction.${actionKey}.title.plural`);
        sanitizationAction = getFormattedLabel(`cart.sanitizationAction.actionDescription.generic`, {
            labelData: {
                action: actionKey
            }
        });
    }
    return {
        title,
        errorMessage: getLabel(`cart.${validationErrorCode}`),
        sanitizationAction,
        actionButton: getLabel(`cart.sanitizationAction.confirm`),
        cancelButton: getLabel(`cart.sanitizationAction.cancel`)
    };
};

const ConfirmationModalContainer = ({ lineItems , handleOnAction , handleOnCancel , isLoading , isOpen , onModalClose , validation  })=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    const labels = React.useMemo(()=>getConfirmationModalLabels(getFormattedLabel, getLabel, validation, lineItems)
    , [
        getFormattedLabel,
        getLabel,
        validation,
        lineItems
    ]);
    const content = React.useMemo(()=>/*#__PURE__*/ React.createElement(RequiredActionMessage, {
            information: labels.errorMessage,
            action: labels.sanitizationAction
        })
    , [
        labels
    ]);
    const footer = React.useMemo(()=>/*#__PURE__*/ React.createElement(ActionButtons$1, {
            labels: labels,
            handleOnAction: handleOnAction,
            handleOnCancel: handleOnCancel,
            isDisabled: isLoading
        })
    , [
        handleOnAction,
        handleOnCancel,
        isLoading,
        labels
    ]);
    return /*#__PURE__*/ React.createElement(Modal, {
        children: content,
        className: "confirmation-modal",
        footer: footer,
        isOpen: isOpen,
        showIcon: true,
        title: labels.title,
        onModalClose: onModalClose
    });
};

var AddToCartContainer = (_param)=>{
    var { lineItemDrafts , onSuccess  } = _param, buttonProps = swcHelpers.objectWithoutProperties(_param, [
        "lineItemDrafts",
        "onSuccess"
    ]);
    const [addToCart, { loading  }] = useAddToCart();
    const { cart  } = useCart();
    const [{ isModalOpen , sanitizationPayloads , validation  }, { onSolvableValidationError , pushRemoveLineItemDataLayerEvent , reset  }, ] = useSanitization();
    const onValidationError = React.useCallback((validationCodes)=>{
        validationCodes.forEach((code)=>toast.failed(`cart.${code}`)
        );
    }, []);
    const onError = React.useCallback((errorCode)=>{
        toast.failed(`cart.${errorCode}`);
    }, []);
    const handleSuccess = React.useCallback((addedLineItems)=>{
        onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(addedLineItems);
        reset();
    }, [
        onSuccess,
        reset
    ]);
    const handleOnClick = React.useCallback(()=>{
        addToCart(lineItemDrafts, handleSuccess, onValidationError, onError, onSolvableValidationError, sanitizationPayloads, pushRemoveLineItemDataLayerEvent);
    }, [
        addToCart,
        lineItemDrafts,
        handleSuccess,
        onError,
        onValidationError,
        onSolvableValidationError,
        sanitizationPayloads, 
    ]);
    if (!lineItemDrafts.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, !isNil(validation) && /*#__PURE__*/ React.createElement(ConfirmationModalContainer, {
        lineItems: cart === null || cart === void 0 ? void 0 : cart.lineItems,
        handleOnAction: handleOnClick,
        handleOnCancel: reset,
        isLoading: loading,
        isOpen: isModalOpen,
        onModalClose: reset,
        validation: validation
    }), /*#__PURE__*/ React.createElement(AddToCart$1, swcHelpers.extends({
        onClick: handleOnClick,
        disabled: loading || buttonProps.disabled
    }, buttonProps)));
};

const AddToCart = (props)=>/*#__PURE__*/ React.createElement(AddToCartContainer, swcHelpers.extends({}, props))
;

const EmptyCart$1 = ({ labels , onClick  })=>/*#__PURE__*/ React__default.createElement("div", {
        className: "cart",
        "data-t": "empty-cart"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "large"
    }, labels.cartTitle), /*#__PURE__*/ React__default.createElement("div", {
        className: "cart__empty-message"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        component: "h2",
        variant: "small"
    }, labels.emptyCartTitle), /*#__PURE__*/ React__default.createElement(Text, {
        variant: "body"
    }, labels.emptyCartMessage), /*#__PURE__*/ React__default.createElement(Button, {
        className: "cart__button cart__empty-message-button",
        color: "primary",
        caption: labels.buttonCaption,
        onClick: onClick,
        "aria-label": labels.buttonCaption
    })))
;

const goBack = ()=>{
    if (document.referrer === window.location.href) {
        window.location.href = '/';
    }
    window.location.host === document.referrer.split('/')[2] ? window.history.back() : window.location.href = '/';
};

const EmptyCartContainer = ({ onClick =goBack  })=>{
    const intl = useIntl();
    const labels = useMemo(()=>({
            cartTitle: intl.getLabel('cart.title'),
            emptyCartTitle: intl.getLabel('cart.emptyCart.title'),
            emptyCartMessage: intl.getLabel('cart.emptyCart.message'),
            buttonCaption: intl.getLabel('cart.actions.goBack')
        })
    , [
        intl.locale
    ]);
    return /*#__PURE__*/ React__default.createElement(EmptyCart$1, {
        labels: labels,
        onClick: onClick
    });
};

const EmptyCart = (props)=>/*#__PURE__*/ React.createElement(EmptyCartContainer, swcHelpers.extends({}, props))
;

const ContactLensMetaData = ({ colorLens , replacementFrequency , packageQuantity , labels  })=>/*#__PURE__*/ React.createElement("div", {
        className: "line-item__contact-lens-meta"
    }, colorLens && /*#__PURE__*/ React.createElement("span", {
        className: "line-item__contact-lens-meta-value"
    }, colorLens), replacementFrequency && /*#__PURE__*/ React.createElement("span", {
        className: "line-item__contact-lens-meta-value"
    }, replacementFrequency), packageQuantity && /*#__PURE__*/ React.createElement("span", {
        className: "line-item__contact-lens-meta-value"
    }, `${packageQuantity} ${labels.productsUnitContactLenses} ${labels.productsPerBox}`))
;

const ContactLensSpecs = ({ specs , labels , isTabletLandscapeUp  })=>/*#__PURE__*/ React.createElement("div", {
        className: "line-item__spec-box"
    }, specs.map((spec)=>/*#__PURE__*/ React.createElement("span", {
            className: "line-item__spec",
            key: spec.name
        }, /*#__PURE__*/ React.createElement("span", {
            className: "line-item__spec-label"
        }, isTabletLandscapeUp ? labels[spec.name] : labels[`${spec.name}Abbr`]), /*#__PURE__*/ React.createElement("span", {
            className: "line-item__spec-value"
        }, spec.value, " "))
    ))
;

const Discount = ({ absoluteValue , relativeValue , currency , labels , className  })=>{
    const hasAbsoluteValue = absoluteValue > 0;
    const hasRelativeValue = (relativeValue !== null && relativeValue !== void 0 ? relativeValue : 0) > 0;
    if (!hasAbsoluteValue && !hasRelativeValue) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('line-item__discount', className)
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__discount-text"
    }, labels.discount, "\xa0"), hasRelativeValue ? /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__discount-value"
    }, relativeValue, "%") : hasAbsoluteValue && /*#__PURE__*/ React__default.createElement(Price, {
        value: absoluteValue,
        currency: currency,
        className: "line-item__discount-price",
        type: "discount"
    }));
};

const Prices = ({ className , currency , discount , labels , orientation ='vertical' , originalPrice , totalPrice  })=>/*#__PURE__*/ React__default.createElement("div", {
        className: classnames('line-item__prices', {
            [`line-item__prices--${orientation}`]: discount > 0
        }, className)
    }, originalPrice !== 0 ? /*#__PURE__*/ React__default.createElement(Price, {
        value: originalPrice,
        currency: currency,
        className: classnames({
            'line-item__prices-discount-amount': discount > 0
        }),
        type: discount > 0 ? 'strikethrough' : 'default'
    }) : labels.freeProduct, discount > 0 && /*#__PURE__*/ React__default.createElement(Price, {
        value: totalPrice,
        currency: currency,
        type: "discount",
        size: "custom"
    }))
;

const SolutionSpecs = ({ type , volume , labels  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, type, ", "), /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, volume, " ", labels.unit))
;

const StockAndDelivery = ({ readOnly , availableQuantity , availability , deliveryResolver , allowBackOrder , hideStockAndDeliveryOnLineItem  })=>!readOnly && !hideStockAndDeliveryOnLineItem ? /*#__PURE__*/ React.createElement("div", {
        className: "line-item__stock-and-delivery"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__delivery"
    }, /*#__PURE__*/ React.createElement(DeliveryIndicator, {
        view: "short",
        availability: availability ? [
            availability
        ] : [],
        deliveryResolver: deliveryResolver
    })), /*#__PURE__*/ React.createElement("div", {
        className: "line-item__stock"
    }, /*#__PURE__*/ React.createElement(StockIndicator, {
        allowBackOrder: allowBackOrder,
        stockCount: availableQuantity,
        // Channel availability is not saved on lineItems and can't be added
        // to the backend without major changes. Since it's required to calculate
        // the stock indication, we assume in the cart that its value is ONLINE,
        // because IN_STORE_ONLY items are never orderable. In the future this logic
        // will be replaced by a GraphQL query.
        // Channel availability is not saved on lineItems and can't be added
        // to the backend without major changes. Since it's required to calculate
        // the stock indication, we assume in the cart that its value is ONLINE,
        // because IN_STORE_ONLY items are never orderable. In the future this logic
        // will be replaced by a GraphQL query.
        channelAvailability: "ONLINE"
    }))) : null
;

const ContactLensSpecsContainer = ({ colorLens , replacementFrequency , packageQuantity , specs  })=>{
    const { getLabel  } = useIntl();
    const isTabletLandscapeUp = MediaQuery.useBreakpoint() === MediaQuery.BreakpointName.TABLET_LANDSCAPE;
    const labels = React__default.useMemo(()=>({
            productsUnitContactLenses: getLabel('products.unit.contactLenses'),
            productsPerBox: getLabel('products.perBox'),
            baseCurve: getLabel(`products.contactLenses.prescription.baseCurve`),
            baseCurveAbbr: getLabel(`products.contactLenses.prescription.baseCurve.abbr`),
            diameter: getLabel(`products.contactLenses.prescription.diameter`),
            diameterAbbr: getLabel(`products.contactLenses.prescription.diameter.abbr`),
            axis: getLabel(`products.contactLenses.prescription.axis`),
            axisAbbr: getLabel(`products.contactLenses.prescription.axis.abbr`),
            power: getLabel(`products.contactLenses.prescription.power`),
            powerAbbr: getLabel(`products.contactLenses.prescription.power.abbr`),
            cylinder: getLabel(`products.contactLenses.prescription.cylinder`),
            cylinderAbbr: getLabel(`products.contactLenses.prescription.cylinder.abbr`),
            addition: getLabel(`products.contactLenses.prescription.addition`),
            additionAbbr: getLabel(`products.contactLenses.prescription.addition.abbr`)
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(ContactLensMetaData, {
        colorLens: colorLens,
        replacementFrequency: replacementFrequency,
        packageQuantity: packageQuantity,
        labels: labels
    }), /*#__PURE__*/ React__default.createElement(ContactLensSpecs, {
        specs: specs,
        labels: labels,
        isTabletLandscapeUp: isTabletLandscapeUp
    }));
};

const DiscountContainer = ({ discount  })=>{
    const { currency , getLabel , locale  } = useIntl();
    const settings = useCartSettings();
    const labels = React.useMemo(()=>({
            discount: getLabel('cart.item.discount')
        })
    , [
        locale,
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(Discount, {
        currency: currency,
        absoluteValue: discount.absoluteValue,
        relativeValue: settings.multiDiscountType ? discount.relativeValue : undefined,
        labels: labels
    });
};

const PricesContainer = ({ className , discount , orientation , originalPrice , totalPrice  })=>{
    const { currency , locale , getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            freeProduct: getLabel('products.freeProduct')
        })
    , [
        locale,
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(Prices, {
        className: className,
        currency: currency,
        discount: discount,
        orientation: orientation,
        originalPrice: originalPrice,
        totalPrice: totalPrice,
        labels: labels
    });
};

const RemoveLineItemContainer = ({ lineItem  })=>{
    const [removeFromCart] = useRemoveFromCart(lineItem);
    const handleOnClick = React__default.useCallback(()=>{
        removeFromCart();
    }, [
        lineItem
    ]);
    return /*#__PURE__*/ React__default.createElement(LineItemDeleteButton, {
        onClick: handleOnClick
    });
};

const SolutionSpecsContainer = ({ type , volume  })=>{
    const { getLabel , locale  } = useIntl();
    const labels = React.useMemo(()=>({
            unit: getLabel('products.unit.metric.liter.milli.abbr')
        })
    , [
        locale,
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(SolutionSpecs, {
        labels: labels,
        type: type,
        volume: volume
    });
};

const StockAndDeliveryContainer = ({ lineItem  })=>{
    const settings = useCartSettings();
    const deliveryResolver = settings.deliveryResolverFactory && settings.deliveryResolverFactory([
        lineItem
    ]);
    const availableQuantity = Helpers.getAvailableQuantity(lineItem.availability);
    return /*#__PURE__*/ React.createElement(StockAndDelivery, {
        hideStockAndDeliveryOnLineItem: settings.hideStockAndDeliveryOnLineItem,
        readOnly: settings.readOnly,
        availableQuantity: availableQuantity,
        availability: lineItem.availability,
        deliveryResolver: deliveryResolver,
        allowBackOrder: isBoolean(lineItem.allowBackOrder) ? lineItem.allowBackOrder : settings.allowBackOrder
    });
};

const UpdateLineItemContainer = ({ lineItem , availableQuantity , disabled ,  })=>{
    const { minimumQuantity , maximumQuantity  } = useCartSettings();
    const [updateItemQuantity] = useUpdateLineItemQuantity(lineItem);
    const handleDecreaseQuantity = React.useCallback(()=>{
        updateItemQuantity(lineItem.quantity - 1);
    }, [
        lineItem.quantity,
        updateItemQuantity
    ]);
    const handleIncreaseQuantity = React.useCallback(()=>{
        updateItemQuantity(lineItem.quantity + 1);
    }, [
        lineItem.quantity,
        updateItemQuantity
    ]);
    return /*#__PURE__*/ React.createElement(QuantitySelector, {
        onDecreaseQuantity: handleDecreaseQuantity,
        onIncreaseQuantity: handleIncreaseQuantity,
        minimumQuantity: minimumQuantity,
        maximumQuantity: getCalculatedMaximumQuantity(maximumQuantity, availableQuantity),
        quantity: lineItem.quantity,
        isDisabled: disabled,
        "data-t": "quantity-selector"
    });
};

const UnavailableLineItem = ({ lineItem , lineItemMediaIcon  })=>{
    const { availability , imageUrl , isGift , price , productUrl , name , subtitle , imageAlt  } = lineItem;
    return /*#__PURE__*/ React__default.createElement(LineItem, {
        className: "line-item line-item--unavailable-line-item"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__media"
    }, lineItemMediaIcon && /*#__PURE__*/ React__default.createElement(LineItemMedia, swcHelpers.extends({
        type: "icon"
    }, lineItemMediaIcon)), /*#__PURE__*/ React__default.createElement(LineItemMedia, {
        className: "line-item__image",
        type: "image",
        href: productUrl,
        imageUrl: imageUrl,
        imageAlt: imageAlt
    })), /*#__PURE__*/ React__default.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__description"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "line-item__title"
    }, name), subtitle && /*#__PURE__*/ React__default.createElement(Text, {
        component: "span",
        variant: "body"
    }, subtitle)), /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity-and-price"
    }, !isGift && /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity-selector"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__delete-btn"
    }, /*#__PURE__*/ React__default.createElement(RemoveLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React__default.createElement(UpdateLineItemContainer, {
        lineItem: lineItem,
        availableQuantity: Helpers.getAvailableQuantity(availability),
        disabled: true
    }))), /*#__PURE__*/ React__default.createElement(PricesContainer, {
        discount: price.total.discount,
        originalPrice: price.total.original,
        totalPrice: price.total.gross
    }))));
};

const UnavailableLineItemsContainer = ({ unavailableLineItems , labels  })=>{
    return /*#__PURE__*/ React.createElement(React.Fragment, null, !!(unavailableLineItems === null || unavailableLineItems === void 0 ? void 0 : unavailableLineItems.length) && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        component: "h2",
        className: "cart__unavailable-title"
    }, labels.unavailableTitle), unavailableLineItems.map((lineItem)=>/*#__PURE__*/ React.createElement(UnavailableLineItem, {
            key: lineItem.id,
            lineItem: lineItem
        })
    )));
};

const DiscountDescriptions$1 = ({ descriptions , labels  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__discount-descriptions"
    }, /*#__PURE__*/ React.createElement(Alert, {
        type: "info",
        title: labels.promotionTitle,
        message: descriptions,
        className: "cart-totals__alert"
    }))
;

const DiscountDescriptionsContainer = ()=>{
    const settings = useCartSettings();
    const { cart  } = useCart();
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            promotionTitle: getLabel('cart.cart.promotion.title')
        })
    , [
        getLabel
    ]);
    if (!settings.showCartDiscountDescriptions || !cart || !(cart.discountDescriptions.length > 0)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(DiscountDescriptions$1, {
        descriptions: cart.discountDescriptions,
        labels: labels
    });
};

const DiscountDescriptions = ()=>/*#__PURE__*/ React.createElement(DiscountDescriptionsContainer, null)
;

const [CartSlotsProvider, useCartSlots] = createSlotsContext({
    displayName: 'CartSlotsContext'
});

const RemoveVoucherCode = ({ voucherCode , onRemove , 'data-t': dataT  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__row",
        key: voucherCode
    }, /*#__PURE__*/ React.createElement("span", {
        className: "cart-totals__label"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "cart__applied-discount"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "cart__applied-discount-code"
    }, voucherCode), /*#__PURE__*/ React.createElement(IconButton, {
        className: "cart__applied-discount-button",
        onClick: onRemove,
        "data-t": dataT
    }, /*#__PURE__*/ React.createElement(Icon, {
        className: "cart__applied-discount-icon",
        name: "close",
        width: 12,
        height: 12
    })))), /*#__PURE__*/ React.createElement("span", {
        className: "cart-totals__value"
    }));
};

var RemoveVoucherCodeContainer = (_param)=>{
    var { voucherCode  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "voucherCode"
    ]);
    const [{ cartId  }] = useCartStorage();
    const [removeCartVoucherCodes] = useMutation(REMOVE_CART_VOUCHER_CODES);
    const handleRemove = React.useCallback(()=>{
        if (!cartId || !voucherCode) {
            return;
        }
        removeCartVoucherCodes({
            variables: {
                cartInput: {
                    cartId,
                    voucherCodes: [
                        voucherCode
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.removeCartVoucherCodes.success === true) {
                    const newCart = data.removeCartVoucherCodes.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: newCart
                    });
                }
            }
        });
    }, [
        cartId,
        removeCartVoucherCodes,
        voucherCode
    ]);
    if (isNil(cartId) || !voucherCode) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(RemoveVoucherCode, swcHelpers.extends({
        onRemove: handleRemove,
        voucherCode: voucherCode
    }, rest));
};

const VoucherCodeFormContainer = ({ inputId , className , 'data-t': dataT ,  })=>{
    const [{ cartId  }] = useCartStorage();
    const { isOmniVoucherEnabled  } = useFeatureToggles();
    const { getLabel  } = useIntl();
    const handleError = React.useCallback(()=>{
        toast.failed('cart.validations.discountcode.notvalid');
    }, []);
    const [addCartVoucherCodes] = useMutation(ADD_CART_VOUCHER_CODES, {
        onError: handleError
    });
    const handleSubmit = React.useCallback((inputValue)=>{
        if (!cartId) {
            return;
        }
        addCartVoucherCodes({
            variables: {
                cartInput: {
                    cartId,
                    voucherCodes: [
                        inputValue
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.addCartVoucherCodes.success === true) {
                    var ref, ref1;
                    const newCart = data.addCartVoucherCodes.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: newCart
                    });
                    const voucherStates = newCart === null || newCart === void 0 ? void 0 : (ref = newCart.vouchers) === null || ref === void 0 ? void 0 : ref.map(({ state  })=>state
                    );
                    // First check if any voucher codes are not in the state of "MatchesCart"
                    // according to: https://docs.commercetools.com/api/projects/carts#discountcodestate
                    // Show a warning toast for all voucher states other than MatchesCart
                    if (voucherStates === null || voucherStates === void 0 ? void 0 : voucherStates.some((state)=>state != 'MatchesCart'
                    )) {
                        var ref2;
                        newCart === null || newCart === void 0 ? void 0 : (ref2 = newCart.vouchers) === null || ref2 === void 0 ? void 0 : ref2.forEach(({ state  })=>{
                            if (state !== 'MatchesCart') {
                                toast.failed(`cart.validations.discountcode.${state.toLowerCase()}`);
                            }
                        });
                        return;
                    }
                    // Check if some vouchers are not applicable to the cart and show toast message
                    if (newCart === null || newCart === void 0 ? void 0 : (ref1 = newCart.vouchers) === null || ref1 === void 0 ? void 0 : ref1.length) {
                        const unappliedVouchers = getUnappliedVoucherCodes(newCart === null || newCart === void 0 ? void 0 : newCart.vouchers, newCart === null || newCart === void 0 ? void 0 : newCart.appliedDiscounts);
                        if (unappliedVouchers === null || unappliedVouchers === void 0 ? void 0 : unappliedVouchers.length) {
                            toast.failed(`cart.validations.discountcode.doesnotmatchcart`);
                            return;
                        }
                    }
                    // If no problems fire success toast
                    toast.success('cart.validations.discountcode.matchescart');
                }
            }
        });
    }, [
        cartId,
        addCartVoucherCodes
    ]);
    if (isNil(cartId)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(DiscountCodeForm, {
        content: {
            inputField: {
                inputId: inputId || 'discount-code',
                type: 'text'
            }
        },
        className: className,
        onSubmit: handleSubmit,
        placeholderLabel: getLabel('cart.discountForm.placeholder'),
        buttonCaptionLabel: getLabel('cart.discountForm.cta'),
        "data-t": dataT,
        showVoucherScanner: isOmniVoucherEnabled
    });
};

const CartDiscount = ({ label , price , currency  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__row"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__label"
    }, /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, label)), /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__value cart-totals__value--discount"
    }, /*#__PURE__*/ React.createElement(Price, {
        value: price,
        currency: currency,
        type: "discount"
    })))
;

const MainTotal = ({ label , price , currency  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__row"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__label"
    }, /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head",
        className: "cart-totals__total-price"
    }, label)), /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__value"
    }, /*#__PURE__*/ React.createElement(Price, {
        value: price,
        currency: currency,
        className: "cart-totals__total-price"
    })))
;

const PaymentMethodPrice = ({ label , price , currency  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__row"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__label"
    }, /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, label)), /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__value"
    }, /*#__PURE__*/ React.createElement(Price, {
        value: price,
        currency: currency
    })))
;

const Shipping = ({ price , labels , currency  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__row"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__label"
    }, /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, labels.delivery)), /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__value"
    }, price > 0 ? /*#__PURE__*/ React.createElement(Price, {
        value: price,
        currency: currency
    }) : /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, labels.freeDelivery)))
;

const SubscriptionExplainer = ({ information  })=>/*#__PURE__*/ React.createElement(Text, {
        className: "cart-totals__subscription-explainer"
    }, information)
;

const SubscriptionHeader = ({ header  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__subscription-header"
    }, /*#__PURE__*/ React.createElement(Divider, {
        theme: "light"
    }), /*#__PURE__*/ React.createElement(Text, {
        className: "cart-totals__subscription-header-text",
        component: "span",
        variant: "body-head"
    }, header))
;

const Subtotal = ({ label , price , currency  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__row"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__label"
    }, /*#__PURE__*/ React.createElement(Text, {
        component: "span"
    }, label)), /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__value"
    }, /*#__PURE__*/ React.createElement(Price, {
        value: price,
        currency: currency
    })))
;

const ShippingThreshold = ({ price , freeShippingThreshold , labels  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__shipping-threshold"
    }, /*#__PURE__*/ React.createElement(ProgressBar, {
        value: price,
        max: freeShippingThreshold
    }), /*#__PURE__*/ React.createElement("div", {
        className: "cart-totals__shipping-threshold--text"
    }, price < freeShippingThreshold ? labels.freeShippingRemainder : labels.freeShippingReached));
};

const containsOnlyContactLensesSubscriptionLineItems = (lineItems)=>{
    return lineItems.every((lineItem)=>lineItem.type === 'contact-lenses-subscription'
    );
};

const getSubscriptionLabels = (subscriptionType, labels)=>{
    switch(subscriptionType){
        case SubscriptionType.MONTHLY:
            return {
                totals: labels.totalPerMonth,
                explainer: labels.subscriptionExplainer.monthly
            };
        case SubscriptionType.QUARTERLY:
            return {
                totals: labels.totalPerThreeMonths,
                explainer: labels.subscriptionExplainer.quarterly
            };
        case SubscriptionType.MIXED_MONTHLY:
            return {
                totals: labels.totalPerMonth,
                explainer: labels.subscriptionExplainer.mixedMonthly
            };
        case SubscriptionType.MIXED_QUARTERLY:
            return {
                totals: labels.totalPerThreeMonths,
                explainer: labels.subscriptionExplainer.mixedQuarterly
            };
    }
};

const SubscriptionContainer = ({ lineItems , subscription , hideSubscriptionExplainer  })=>{
    const { currency , getLabel , getFormattedLabel  } = useIntl();
    const contactLensesSubscriptionLineItem = lineItems.find((lineItem)=>lineItem.type === 'contact-lenses-subscription'
    );
    const labels = React.useMemo(()=>({
            totalPerMonth: getLabel('cart.subscription.totalPerMonth'),
            totalPerThreeMonths: getLabel('cart.subscription.totalPerThreeMonths'),
            firstPayment: getLabel('cart.subscription.firstPayment'),
            subscriptionExplainer: {
                monthly: getFormattedLabel('cart.subscription.explainerMonthly', {
                    labelData: {
                        billingDate: subscription.billingDate
                    }
                }),
                quarterly: getFormattedLabel('cart.subscription.explainerQuarterly', {
                    labelData: {
                        billingDate: subscription.billingDate
                    }
                }),
                mixedMonthly: getFormattedLabel('cart.subscription.explainerMixedMonthly', {
                    labelData: {
                        billingDate: subscription.billingDate
                    }
                }),
                mixedQuarterly: getFormattedLabel('cart.subscription.explainerMixedQuarterly', {
                    labelData: {
                        billingDate: subscription.billingDate
                    }
                })
            }
        })
    , [
        getLabel,
        getFormattedLabel,
        subscription.billingDate
    ]);
    if (!contactLensesSubscriptionLineItem || !isContactLensesSubscriptionLineItem(contactLensesSubscriptionLineItem)) {
        return null;
    }
    const hasFirstPayment = contactLensesSubscriptionLineItem.subscriptionType === SubscriptionType.MIXED_MONTHLY;
    const { totals , explainer  } = getSubscriptionLabels(contactLensesSubscriptionLineItem.subscriptionType, labels);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, hasFirstPayment && /*#__PURE__*/ React.createElement(Subtotal, {
        label: labels.firstPayment,
        price: subscription.firstPayment,
        currency: currency
    }), /*#__PURE__*/ React.createElement(MainTotal, {
        label: totals,
        price: subscription.recurringPayment,
        currency: currency
    }), !hideSubscriptionExplainer && /*#__PURE__*/ React.createElement(SubscriptionExplainer, {
        information: explainer
    }));
};

const Mixed = ({ className , currency , labels , lineItems , prices , settings , subscription , voucherCodes , hideSubscriptionExplainer  })=>{
    const { Slots  } = useCartSlots();
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('cart-totals', className)
    }, settings.showSubTotal && /*#__PURE__*/ React.createElement(Subtotal, {
        label: labels.subTotal,
        price: prices.subTotalPrice,
        currency: currency
    }), settings.showTotalDiscount && prices.discountPrice !== 0 && /*#__PURE__*/ React.createElement(CartDiscount, {
        label: labels.totalDiscount,
        price: -prices.discountPrice,
        currency: currency
    }), voucherCodes === null || voucherCodes === void 0 ? void 0 : voucherCodes.map((code)=>/*#__PURE__*/ React.createElement(RemoveVoucherCodeContainer, {
            key: code,
            voucherCode: code
        })
    ), prices.paymentMethodPrice && /*#__PURE__*/ React.createElement(PaymentMethodPrice, {
        label: labels.paymentMethodPrice,
        price: prices.paymentMethodPrice,
        currency: currency
    }), /*#__PURE__*/ React.createElement(Shipping, {
        labels: labels,
        price: prices.shippingPrice,
        currency: currency
    }), /*#__PURE__*/ React.createElement(MainTotal, {
        label: labels.todaysTotal,
        price: prices.totalPrice,
        currency: currency
    }), Slots.AfterTotals ? /*#__PURE__*/ React.createElement(Slots.AfterTotals, null) : null, /*#__PURE__*/ React.createElement(SubscriptionHeader, {
        header: labels.subscriptionHeader
    }), /*#__PURE__*/ React.createElement(SubscriptionContainer, {
        lineItems: lineItems,
        subscription: subscription,
        hideSubscriptionExplainer: hideSubscriptionExplainer
    }));
};

const Regular = ({ className , currency , labels , prices , settings , voucherCodes , freeShippingThreshold , isMiniCart , discountedPrice  })=>{
    const { Slots  } = useCartSlots();
    const showShippingThreshold = (!settings.readOnly || isMiniCart) && !isNil(freeShippingThreshold) && !isNaN(freeShippingThreshold) && freeShippingThreshold > 0 ? true : false;
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('cart-totals', className)
    }, showShippingThreshold && /*#__PURE__*/ React.createElement(ShippingThreshold, {
        price: discountedPrice || 0,
        labels: labels,
        freeShippingThreshold: freeShippingThreshold || 0
    }), settings.showSubTotal && /*#__PURE__*/ React.createElement(Subtotal, {
        label: labels.subTotal,
        price: prices.subTotalPrice,
        currency: currency
    }), settings.showTotalDiscount && prices.discountPrice !== 0 && /*#__PURE__*/ React.createElement(CartDiscount, {
        label: labels.totalDiscount,
        price: -prices.discountPrice,
        currency: currency
    }), voucherCodes && voucherCodes.map((code)=>/*#__PURE__*/ React.createElement(RemoveVoucherCodeContainer, {
            key: code,
            voucherCode: code
        })
    ), prices.paymentMethodPrice && /*#__PURE__*/ React.createElement(PaymentMethodPrice, {
        label: labels.paymentMethodPrice,
        price: prices.paymentMethodPrice,
        currency: currency
    }), /*#__PURE__*/ React.createElement(Shipping, {
        labels: labels,
        price: prices.shippingPrice,
        currency: currency
    }), /*#__PURE__*/ React.createElement(MainTotal, {
        label: labels.total,
        price: prices.totalPrice,
        currency: currency
    }), Slots.AfterTotals ? /*#__PURE__*/ React.createElement(Slots.AfterTotals, null) : null);
};

const Subscription = ({ className , lineItems , hideSubscriptionExplainer , subscription , voucherCodes  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('cart-totals', className)
    }, voucherCodes && voucherCodes.map((code)=>/*#__PURE__*/ React.createElement(RemoveVoucherCodeContainer, {
            key: code,
            voucherCode: code
        })
    ), /*#__PURE__*/ React.createElement(SubscriptionContainer, {
        lineItems: lineItems,
        subscription: subscription,
        hideSubscriptionExplainer: hideSubscriptionExplainer
    }))
;

const TotalsContainer = ({ showSubTotal , hideSubscriptionExplainer , showTotalDiscount , className , isMiniCart  })=>{
    const settings = useCartSettings();
    const { cart  } = useCart();
    const { locale , currency , getLabel , getFormattedLabel  } = useIntl();
    const discountedPrice = (cart === null || cart === void 0 ? void 0 : cart.prices.discountPrice) ? cart.prices.subTotalPrice - cart.prices.discountPrice : (cart === null || cart === void 0 ? void 0 : cart.prices.subTotalPrice) || 0;
    const freeShippingRemainder = (cart === null || cart === void 0 ? void 0 : cart.shippingMethod.freeAbove) ? cart.shippingMethod.freeAbove - discountedPrice : undefined;
    const formattedPrice = formatPrice({
        locale,
        value: freeShippingRemainder,
        currency,
        isCentAmount: true
    });
    const labels = React.useMemo(()=>({
            delivery: getLabel('cart.delivery'),
            freeDelivery: getLabel('cart.delivery.forFree'),
            subTotal: getLabel('cart.value.subTotal'),
            total: getLabel('cart.value.total'),
            todaysTotal: getLabel('cart.value.todaysTotal'),
            totalDiscount: getLabel('cart.value.totalDiscount'),
            paymentMethodPrice: getLabel('cart.value.paymentMethod'),
            subscriptionHeader: getLabel('cart.subscription.header'),
            freeShippingRemainder: getFormattedLabel('cart.delivery.freeShippingRemainder', {
                parseHTML: true,
                labelData: {
                    price: `<span className="price">${formattedPrice}</span>`
                }
            }),
            freeShippingReached: getLabel('cart.delivery.freeShippingReached')
        })
    , [
        getLabel,
        getFormattedLabel,
        formattedPrice
    ]);
    if (!cart) {
        return null;
    }
    const cartSettings = swcHelpers.objectSpread({}, settings, {
        showSubTotal: !isNil(showSubTotal) ? showSubTotal : settings.showSubTotal,
        showTotalDiscount: !isNil(showTotalDiscount) ? showTotalDiscount : settings.showTotalDiscount
    });
    if (cart.subscription && containsOnlyContactLensesSubscriptionLineItems(cart.lineItems)) {
        return /*#__PURE__*/ React.createElement(Subscription, {
            className: className,
            voucherCodes: cart.voucherCodes,
            subscription: cart.subscription,
            lineItems: cart.lineItems,
            hideSubscriptionExplainer: hideSubscriptionExplainer
        });
    }
    if (cart.subscription) {
        return /*#__PURE__*/ React.createElement(Mixed, {
            className: className,
            currency: currency,
            labels: labels,
            lineItems: cart.lineItems,
            prices: cart.prices,
            settings: cartSettings,
            hideSubscriptionExplainer: hideSubscriptionExplainer,
            subscription: cart.subscription,
            voucherCodes: cart.voucherCodes
        });
    }
    return /*#__PURE__*/ React.createElement(Regular, {
        className: className,
        currency: currency,
        labels: labels,
        prices: cart.prices,
        settings: cartSettings,
        voucherCodes: cart.voucherCodes,
        freeShippingThreshold: cart.shippingMethod.freeAbove,
        discountedPrice: discountedPrice,
        isMiniCart: isMiniCart
    });
};

const Totals = (props)=>/*#__PURE__*/ React.createElement(TotalsContainer, swcHelpers.extends({}, props))
;

// This component returns the regular "go to checkout" button if there are no
// unavailable line items. If there are unavailable line items, it removes
// them from the cart before navigating to checkout.
const FixCartButtonContainer = ({ unavailableLineItems , labels  })=>{
    const { onClick: navigate  } = useLink({
        route: 'checkout'
    }) || {};
    const [{ cartId  }, { setCartCount  }] = useCartStorage();
    const unavailableItemIds = unavailableLineItems === null || unavailableLineItems === void 0 ? void 0 : unavailableLineItems.map(({ id  })=>({
            id
        })
    );
    const hasUnavailableItems = unavailableItemIds === null || unavailableItemIds === void 0 ? void 0 : unavailableItemIds.length;
    const [removeCartItems, { loading  }] = useMutation(REMOVE_CART_LINE_ITEMS, {
        onCompleted: ()=>{
            navigate === null || navigate === void 0 ? void 0 : navigate();
        },
        update (cache, { data  }) {
            if (data && data.removeCartLineItems.success === true) {
                const newCart = data.removeCartLineItems.cart;
                cache.writeQuery({
                    query: GET_CART,
                    variables: {
                        id: cartId
                    },
                    data: {
                        getCart: {
                            cart: newCart
                        }
                    }
                });
                setCartCount((newCart === null || newCart === void 0 ? void 0 : newCart.numberOfItems) || 0);
                window.scrollTo(0, 0);
            }
        }
    });
    const removeAndNavigate = React.useCallback((e)=>{
        if (hasUnavailableItems && cartId) {
            e.preventDefault();
            removeCartItems({
                variables: {
                    cartInput: {
                        cartId,
                        lineItems: unavailableItemIds
                    }
                }
            });
        }
    }, [
        removeCartItems,
        cartId,
        unavailableItemIds,
        hasUnavailableItems
    ]);
    return /*#__PURE__*/ React.createElement(Button, {
        isLoading: loading,
        className: "cart-navigation__button",
        "data-t": "cart-go-to-checkout",
        caption: labels.goToCheckout,
        href: "/checkout",
        onClick: hasUnavailableItems ? removeAndNavigate : undefined
    });
};

const Navigation = ({ labels , unavailableLineItems  })=>{
    const [{ cartId  }] = useCartStorage();
    return /*#__PURE__*/ React.createElement("div", {
        className: "cart-navigation"
    }, /*#__PURE__*/ React.createElement(Button, {
        className: "cart-navigation__button",
        "data-t": "cart-go-back",
        caption: labels.goBack,
        variant: "outline",
        href: "/"
    }), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-button-container"
    }, !(unavailableLineItems === null || unavailableLineItems === void 0 ? void 0 : unavailableLineItems.length) && cartId && /*#__PURE__*/ React.createElement(ApplePayExpressCheckoutButtonContainer, {
        cartId: cartId
    }), /*#__PURE__*/ React.createElement(FixCartButtonContainer, {
        labels: labels,
        unavailableLineItems: unavailableLineItems
    })));
};

const NavigationContainer = (props)=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            goBack: getLabel('cart.actions.goBack'),
            goToCheckout: getLabel('cart.actions.goToCheckout')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(Navigation, swcHelpers.extends({}, props, {
        labels: labels
    }));
};

const Footer = ({ readOnly , summaryLabel , formTitleLabel , policyItems , unavailableLineItems  })=>/*#__PURE__*/ React.createElement("div", {
        className: "cart-footer"
    }, policyItems && /*#__PURE__*/ React.createElement("div", {
        className: "usp-group__container mobile-hidden"
    }, policyItems.map((item)=>/*#__PURE__*/ React.createElement(UspGroupItem, {
            item: item,
            key: item.text
        })
    )), !readOnly && /*#__PURE__*/ React.createElement("div", {
        className: "cart-footer__aside"
    }, /*#__PURE__*/ React.createElement(DiscountDescriptions, null)), /*#__PURE__*/ React.createElement("div", {
        className: "cart-footer__main"
    }, /*#__PURE__*/ React.createElement(Heading, {
        className: "cart-footer__heading",
        variant: "small"
    }, summaryLabel), !readOnly && /*#__PURE__*/ React.createElement(Accordion, {
        className: "cart-footer__acc"
    }, /*#__PURE__*/ React.createElement(AccordionItem, {
        id: "discount-code",
        className: "cart-footer__acc__item",
        label: formTitleLabel,
        headingLevel: "h4"
    }, /*#__PURE__*/ React.createElement(VoucherCodeFormContainer, {
        className: "cart-footer__acc__form",
        inputId: "discount-code"
    }))), /*#__PURE__*/ React.createElement(Totals, null)), !readOnly && /*#__PURE__*/ React.createElement("div", {
        className: "cart-footer__navigation"
    }, /*#__PURE__*/ React.createElement(NavigationContainer, {
        unavailableLineItems: unavailableLineItems
    })), policyItems && /*#__PURE__*/ React.createElement("div", {
        className: "usp-group__container desktop-hidden"
    }, policyItems.map((item)=>/*#__PURE__*/ React.createElement(UspGroupItem, {
            item: item,
            key: item.text
        })
    )))
;

const FooterContainer = ({ readOnly , policyItems , unavailableLineItems  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement(Footer, {
        summaryLabel: getLabel('cart.discount.summary'),
        formTitleLabel: getLabel('cart.discountForm.title'),
        readOnly: readOnly,
        policyItems: policyItems,
        unavailableLineItems: unavailableLineItems
    });
};

const BundleLineItem = ({ settings , lineItem , bundleItems , mainBundleItemPrice , cartDiscount , prescription , isLineItemClickable =true  })=>{
    const { getLabel  } = useIntl();
    const isPhone = MediaQuery.useIsPhone();
    const { currency , name , price: lineItemPrice , productUrl , imageUrl , quantity  } = lineItem;
    const totalPrice = lineItemPrice.total;
    const totalDiscount = lineItemPrice.total.discount;
    const { readOnly  } = settings;
    const labels = React.useMemo(()=>({
            totalDiscount: getLabel('cart.item.discount'),
            quantity: getLabel('cart.item.quantity')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item bundle-line-item"
    }, !isPhone && /*#__PURE__*/ React.createElement("div", {
        className: "line-item__media"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "bundle-line-item__media",
        type: "image",
        href: isLineItemClickable ? productUrl : undefined,
        imageUrl: imageUrl,
        imageAlt: name
    })), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "bundle-line-item__content"
    }, /*#__PURE__*/ React.createElement(LineItemRow, null, /*#__PURE__*/ React.createElement(LineItemRow.Item, {
        className: "bundle-line-item__title-wrapper"
    }, isPhone && /*#__PURE__*/ React.createElement("div", {
        className: "line-item__media"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        type: "image",
        imageUrl: imageUrl,
        imageAlt: name,
        href: isLineItemClickable ? productUrl : undefined,
        className: "bundle-line-item__media"
    })), /*#__PURE__*/ React.createElement(Heading, {
        className: "bundle-line-item__title",
        variant: "smallest",
        component: "h3"
    }, name)), /*#__PURE__*/ React.createElement("div", {
        className: "bundle-line-item__frame-price-wrapper"
    }, mainBundleItemPrice && /*#__PURE__*/ React.createElement(PricesContainer, {
        discount: mainBundleItemPrice.discount,
        originalPrice: mainBundleItemPrice.original,
        totalPrice: mainBundleItemPrice.gross,
        orientation: isPhone ? 'vertical' : 'horizontal'
    }))), prescription && /*#__PURE__*/ React.createElement(LineItemGlassesPrescription, {
        className: "bundle-line-item__prescription",
        prescription: prescription
    }), bundleItems.map((bundle, idx)=>/*#__PURE__*/ React.createElement(LineItemBundleRow, swcHelpers.extends({
            key: idx
        }, bundle))
    ), readOnly && /*#__PURE__*/ React.createElement(LineItemRow, null, /*#__PURE__*/ React.createElement(LineItemRow.Item, null, labels.quantity, ": ", quantity)), cartDiscount && cartDiscount.amount > 0 && /*#__PURE__*/ React.createElement(LineItemRow, null, /*#__PURE__*/ React.createElement(LineItemRow.Item, {
        className: "bundle-line-item__cart-discount"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "bundle-line-item__cart-discount-text"
    }, cartDiscount.description)), /*#__PURE__*/ React.createElement(Price, {
        value: -cartDiscount.amount,
        currency: currency,
        type: "discount"
    })), /*#__PURE__*/ React.createElement(LineItemRow, null, /*#__PURE__*/ React.createElement(LineItemRow.Item, {
        className: "bundle-line-item__total-price-wrapper"
    }, !readOnly && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "bundle-line-item__delete-btn"
    }, /*#__PURE__*/ React.createElement(RemoveLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React.createElement(UpdateLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React.createElement(PricesContainer, {
        className: "bundle-line-item__total-price",
        discount: 0,
        originalPrice: totalPrice.gross,
        totalPrice: 0
    }))), /*#__PURE__*/ React.createElement(LineItemRow, null, /*#__PURE__*/ React.createElement("span", {
        className: "bundle-line-item__total-discount"
    }, totalDiscount > 0 && /*#__PURE__*/ React.createElement(Discount, {
        absoluteValue: totalDiscount,
        currency: currency,
        labels: {
            discount: labels.totalDiscount
        }
    })))));
};

const ContactLensesBundleBenelux = ({ lineItem , settings , labels , isLineItemClickable =true  })=>{
    const { readOnly  } = settings;
    const { discount , imageUrl , isGift , price , name , lensType , solutionImageUrl , solutionName , leftEyeProduct , rightEyeProduct , productUrl ,  } = lineItem;
    const { iconLeftEye , iconRightEye , monthlySubscription  } = labels;
    return /*#__PURE__*/ React.createElement("div", {
        className: "line-item--contact-lenses-bundle-benelux"
    }, /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-bundle-benelux-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "image",
        href: isLineItemClickable ? productUrl : undefined,
        imageUrl: imageUrl,
        imageAlt: name
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__description"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "line-item__title"
    }, name), /*#__PURE__*/ React.createElement(Text, {
        component: "span",
        variant: "body"
    }, lensType), readOnly && /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, monthlySubscription)), /*#__PURE__*/ React.createElement("div", {
        className: "line-item__quantity-and-price"
    }, !readOnly && !isGift && /*#__PURE__*/ React.createElement("div", {
        className: "line-item__quantity"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__quantity-selector"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__delete-btn"
    }, /*#__PURE__*/ React.createElement(RemoveLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, monthlySubscription))), /*#__PURE__*/ React.createElement(PricesContainer, {
        discount: price.total.discount,
        originalPrice: price.total.original,
        totalPrice: price.total.gross
    })), /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs-and-stock"
    }), /*#__PURE__*/ React.createElement(DiscountContainer, {
        discount: discount
    }))), /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-bundle-benelux-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "icon",
        iconName: "left-eye",
        iconLabel: iconLeftEye
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs-and-stock"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs"
    }, /*#__PURE__*/ React.createElement(Text, {
        component: "span",
        variant: "body-head"
    }, leftEyeProduct.name), /*#__PURE__*/ React.createElement(ContactLensSpecsContainer, {
        specs: leftEyeProduct.specs
    }))))), /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-bundle-benelux-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "icon",
        iconName: "right-eye",
        iconLabel: iconRightEye
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs-and-stock"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs"
    }, /*#__PURE__*/ React.createElement(Text, {
        component: "span",
        variant: "body-head"
    }, rightEyeProduct.name), /*#__PURE__*/ React.createElement(ContactLensSpecsContainer, {
        specs: rightEyeProduct.specs
    }))))), solutionName && /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-bundle-benelux-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "image",
        imageUrl: solutionImageUrl,
        imageAlt: solutionName
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, solutionName))));
};

const ContactLensesSubscription = ({ lineItem , settings , labels , isLineItemClickable =true  })=>{
    const { readOnly  } = settings;
    const { discount , imageUrl , isGift , price , name , lensType , solutionImageUrl , solutionName , leftEyeProduct , rightEyeProduct , productUrl , packageQuantity , billingFrequency , deliveryFrequency ,  } = lineItem;
    const { iconLeftEye , iconRightEye , deliveredEvery , perBox  } = labels;
    const BillingDeliveryFrequency = /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, `${billingFrequency}`), /*#__PURE__*/ React.createElement(Text, {
        variant: "body"
    }, `${deliveredEvery} ${deliveryFrequency}`));
    return /*#__PURE__*/ React.createElement("div", {
        className: "line-item--contact-lenses-subscription"
    }, /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-subscription-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "image",
        href: isLineItemClickable ? productUrl : undefined,
        imageUrl: imageUrl,
        imageAlt: name
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__description"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "line-item__title"
    }, name), /*#__PURE__*/ React.createElement(Text, {
        component: "span",
        variant: "body"
    }, `${lensType}, ${packageQuantity} ${perBox}`), readOnly && BillingDeliveryFrequency), /*#__PURE__*/ React.createElement("div", {
        className: "line-item__quantity-and-price"
    }, !readOnly && !isGift && /*#__PURE__*/ React.createElement("div", {
        className: "line-item__quantity"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__quantity-selector"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__delete-btn"
    }, /*#__PURE__*/ React.createElement(RemoveLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React.createElement("div", {
        className: "line-item__frequency"
    }, BillingDeliveryFrequency))), /*#__PURE__*/ React.createElement(PricesContainer, {
        discount: price.total.discount,
        originalPrice: price.total.original,
        totalPrice: price.total.gross
    })), /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs-and-stock"
    }), /*#__PURE__*/ React.createElement(DiscountContainer, {
        discount: discount
    }))), leftEyeProduct && /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-subscription-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "icon",
        iconName: "left-eye",
        iconLabel: iconLeftEye
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs-and-stock"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs"
    }, /*#__PURE__*/ React.createElement(ContactLensSpecsContainer, {
        specs: leftEyeProduct.specs
    }))))), rightEyeProduct && /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-subscription-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "icon",
        iconName: "right-eye",
        iconLabel: iconRightEye
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs-and-stock"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "line-item__specs"
    }, /*#__PURE__*/ React.createElement(ContactLensSpecsContainer, {
        specs: rightEyeProduct.specs
    }))))), solutionName && /*#__PURE__*/ React.createElement(LineItem, {
        className: "line-item line-item--contact-lenses-subscription-item"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "image",
        imageUrl: solutionImageUrl,
        imageAlt: solutionName
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, solutionName))));
};

var ref;
const getBundleItemProps = (bundledProduct, currency, title, priceIsZeroLabel)=>{
    var ref4, ref1, ref2, ref3;
    return {
        currency,
        title,
        body: bundledProduct.title,
        priceIsZeroLabel,
        originalPrice: (ref = (ref4 = bundledProduct.price) === null || ref4 === void 0 ? void 0 : ref4.original) !== null && ref !== void 0 ? ref : 0,
        discountedPrice: ((ref1 = bundledProduct.price) === null || ref1 === void 0 ? void 0 : ref1.discount) ? (ref2 = bundledProduct.price) === null || ref2 === void 0 ? void 0 : ref2.gross : undefined,
        discountText: (ref3 = bundledProduct.discount) === null || ref3 === void 0 ? void 0 : ref3.description
    };
};

var NonPrescriptionBundleLineItem = (_param)=>{
    var { lineItem  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "lineItem"
    ]);
    const { offering , currency , mainBundleItemPrice  } = lineItem;
    const bundleItem = getBundleItemProps(offering, currency);
    return /*#__PURE__*/ React__default.createElement(BundleLineItem, swcHelpers.extends({}, rest, {
        lineItem: lineItem,
        bundleItems: [
            bundleItem
        ],
        mainBundleItemPrice: mainBundleItemPrice
    }));
};

var PrescriptionGlassesLineItem = (_param)=>{
    var { lineItem  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "lineItem"
    ]);
    const { getLabel  } = useIntl();
    const { addOn , currency , lensPackage , lensType , prescription , cartDiscount , mainBundleItemPrice ,  } = lineItem;
    const labels = React.useMemo(()=>({
            lensTypeTitle: getLabel(`cart.lineItem.glasses.bundleItem.LensTypes`),
            lensPackageTitle: getLabel(`cart.lineItem.glasses.bundleItem.PrescriptionGlassPackages`),
            addOnTitle: getLabel(`cart.lineItem.glasses.bundleItem.PrescriptionGlassPackageOptions`)
        })
    , [
        getLabel
    ]);
    const addOnConfig = addOn ? getBundleItemProps(addOn, currency, labels.addOnTitle) : undefined;
    const bundleItems = [
        getBundleItemProps(lensType, currency, labels.lensTypeTitle),
        getBundleItemProps(lensPackage, currency, labels.lensPackageTitle), 
    ];
    if (addOnConfig) bundleItems.push(addOnConfig);
    return /*#__PURE__*/ React.createElement(BundleLineItem, swcHelpers.extends({}, rest, {
        lineItem: lineItem,
        bundleItems: bundleItems,
        prescription: prescription,
        cartDiscount: cartDiscount,
        mainBundleItemPrice: mainBundleItemPrice
    }));
};

const RegularLineItem = ({ lineItem , settings , labels , specsChild , quotationChild , lineItemMediaIcon , isLineItemClickable =true  })=>{
    const { readOnly  } = settings;
    const { availability , discount , imageUrl , isGift , price , productUrl , quantity , name , subtitle , imageAlt ,  } = lineItem;
    return /*#__PURE__*/ React__default.createElement(LineItem, {
        className: "line-item line-item--regular-line-item"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__media"
    }, lineItemMediaIcon && /*#__PURE__*/ React__default.createElement(LineItemMedia, swcHelpers.extends({
        type: "icon"
    }, lineItemMediaIcon)), /*#__PURE__*/ React__default.createElement(LineItemMedia, {
        className: "line-item__image",
        type: "image",
        href: isLineItemClickable ? productUrl : undefined,
        imageUrl: imageUrl,
        imageAlt: imageAlt
    })), /*#__PURE__*/ React__default.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__description"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "line-item__title"
    }, name), subtitle && /*#__PURE__*/ React__default.createElement(Text, {
        component: "span",
        variant: "body"
    }, subtitle)), /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity-and-price"
    }, !readOnly && !isGift && /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity-selector"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__delete-btn"
    }, /*#__PURE__*/ React__default.createElement(RemoveLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React__default.createElement(UpdateLineItemContainer, {
        lineItem: lineItem,
        availableQuantity: Helpers.getAvailableQuantity(availability)
    }))), /*#__PURE__*/ React__default.createElement(PricesContainer, {
        discount: price.total.discount,
        originalPrice: price.total.original,
        totalPrice: price.total.gross
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__specs-and-stock"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__specs"
    }, specsChild), /*#__PURE__*/ React__default.createElement(StockAndDeliveryContainer, {
        lineItem: lineItem
    }), readOnly && /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__count"
    }, labels.quantity, ": ", quantity)), /*#__PURE__*/ React__default.createElement(DiscountContainer, {
        discount: discount
    }), quotationChild));
};

const ReadyReaders = ({ lineItem , settings , labels , isLineItemClickable =true  })=>{
    const { readOnly  } = settings;
    const { availability , brand , discount , imageUrl , isGift , price , productUrl , quantity , name , power ,  } = lineItem;
    return /*#__PURE__*/ React__default.createElement(LineItem, {
        className: "line-item"
    }, /*#__PURE__*/ React__default.createElement(LineItemMedia, {
        className: "line-item__media",
        type: "image",
        href: isLineItemClickable ? productUrl : undefined,
        imageUrl: imageUrl,
        imageAlt: `${brand.name} ${brand.model}`
    }), /*#__PURE__*/ React__default.createElement(LineItemContent, {
        className: "line-item__content"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__description"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "line-item__title"
    }, name), /*#__PURE__*/ React__default.createElement(Text, {
        component: "span",
        variant: "body"
    }, brand.model, (brand === null || brand === void 0 ? void 0 : brand.modelCode) !== brand.model ? ` ${brand.modelCode}` : null), /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__spec-box"
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__spec",
        key: labels.power
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__spec-label"
    }, labels.power), /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__spec-value"
    }, power, " ")))), /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity-and-price"
    }, !readOnly && !isGift && /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__quantity-selector"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__delete-btn"
    }, /*#__PURE__*/ React__default.createElement(RemoveLineItemContainer, {
        lineItem: lineItem
    })), /*#__PURE__*/ React__default.createElement(UpdateLineItemContainer, {
        lineItem: lineItem,
        availableQuantity: Helpers.getAvailableQuantity(availability)
    }))), /*#__PURE__*/ React__default.createElement(PricesContainer, {
        discount: price.total.discount,
        originalPrice: price.total.original,
        totalPrice: price.total.gross
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__specs-and-stock"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "line-item__specs"
    }), /*#__PURE__*/ React__default.createElement(StockAndDeliveryContainer, {
        lineItem: lineItem
    }), readOnly && /*#__PURE__*/ React__default.createElement("span", {
        className: "line-item__count"
    }, labels.quantity, ": ", quantity)), /*#__PURE__*/ React__default.createElement(DiscountContainer, {
        discount: discount
    })));
};

const LineItemsContainer$1 = ({ lineItems  })=>{
    const settings = useCartSettings();
    const { getLabel  } = useIntl();
    const { Slots  } = useCartSlots();
    const [isLineItemClickable, setIsLineItemClickable] = React.useState(true);
    React.useEffect(()=>{
        if (typeof window !== 'undefined') {
            const { pathname  } = window.location;
            /**
       *  Make the lineItem images NON clickable, if the user is in the checkout flow
       *  This will prevent user from leaving checkout funnel
       */ if (pathname.includes('/checkout') || pathname.includes('/express-checkout')) setIsLineItemClickable(false);
        }
    }, []);
    const labels = React.useMemo(()=>({
            quantity: getLabel('cart.item.quantity'),
            iconLeftEye: getLabel('products.contactLenses.leftEye'),
            iconRightEye: getLabel('products.contactLenses.rightEye'),
            power: getLabel('products.contactLenses.prescription.power'),
            monthlySubscription: getLabel('products.monthlySubscription'),
            deliveredEvery: getLabel('cart.delivery.frequency'),
            perBox: getLabel('products.perBox')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, lineItems.map((lineItem)=>{
        const sharedProps = {
            isLineItemClickable,
            key: lineItem.id,
            settings,
            labels
        };
        switch(lineItem.type){
            case 'prescription-glasses':
                return /*#__PURE__*/ React.createElement(PrescriptionGlassesLineItem, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem
                }));
            case 'non-prescription-bundle':
                return /*#__PURE__*/ React.createElement(NonPrescriptionBundleLineItem, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem
                }));
            case 'contact-lenses-bundle-benelux':
                return /*#__PURE__*/ React.createElement(ContactLensesBundleBenelux, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem
                }));
            case 'contact-lenses-subscription':
                return /*#__PURE__*/ React.createElement(ContactLensesSubscription, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem
                }));
            case 'contact-lenses':
                const { colorLens , eyeSide , packageQuantity , replacementFrequency , specs  } = lineItem;
                return /*#__PURE__*/ React.createElement(RegularLineItem, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem,
                    lineItemMediaIcon: {
                        iconName: `${eyeSide}-eye`,
                        iconLabel: eyeSide === 'left' ? labels.iconLeftEye : labels.iconRightEye
                    },
                    specsChild: /*#__PURE__*/ React.createElement(ContactLensSpecsContainer, {
                        colorLens: colorLens,
                        packageQuantity: packageQuantity,
                        replacementFrequency: replacementFrequency,
                        specs: specs
                    }),
                    quotationChild: Slots.Quotation ? /*#__PURE__*/ React.createElement(Slots.Quotation, {
                        type: "CONTACT_LENSES"
                    }) : null
                }));
            case 'solutions':
                return /*#__PURE__*/ React.createElement(RegularLineItem, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem,
                    specsChild: /*#__PURE__*/ React.createElement(SolutionSpecsContainer, {
                        type: lineItem.solutionType,
                        volume: lineItem.solutionVolume
                    })
                }));
            case 'ready-readers':
                return /*#__PURE__*/ React.createElement(ReadyReaders, {
                    key: lineItem.id,
                    lineItem: lineItem,
                    settings: settings,
                    labels: labels,
                    isLineItemClickable: isLineItemClickable
                });
            // 'sunglasses', accessories and 'frames' will default here
            default:
                return /*#__PURE__*/ React.createElement(RegularLineItem, swcHelpers.extends({}, sharedProps, {
                    lineItem: lineItem
                }));
        }
    }));
};

const LineItems = (props)=>/*#__PURE__*/ React.createElement(LineItemsContainer$1, swcHelpers.extends({}, props))
;

const Cart$1 = ({ lineItems , unavailableLineItems , readOnly , labels , deliveryResolver , policyItems  })=>{
    // This will be used when the basic variant of the DeliveryIndicator is enabled
    const availability = lineItems.map((item)=>item.availability
    );
    // This will be used if the full date variant of the DeliveryIndicator is enabled (feature toggle)
    const deliveryItems = lineItems.map((item)=>{
        if ('sku' in item) {
            return {
                sku: item.sku,
                quantity: item.quantity
            };
        }
    }).filter(isDefined);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('cart', {
            'cart--read-only': readOnly
        }),
        "data-t": "cart"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "medium",
        component: "h1"
    }, labels.cartTitle), /*#__PURE__*/ React__default.createElement("div", {
        className: "cart__content"
    }, !!lineItems.length && /*#__PURE__*/ React__default.createElement(DeliveryIndicator, {
        items: deliveryItems,
        availability: availability,
        deliveryResolver: deliveryResolver
    }), /*#__PURE__*/ React__default.createElement(LineItems, {
        lineItems: lineItems
    }), /*#__PURE__*/ React__default.createElement(UnavailableLineItemsContainer, {
        unavailableLineItems: unavailableLineItems,
        labels: labels
    })), /*#__PURE__*/ React__default.createElement(FooterContainer, {
        readOnly: readOnly,
        policyItems: policyItems,
        unavailableLineItems: unavailableLineItems
    }));
};

const CartContainer = ({ onEmptyCartButtonClick , policyItems  })=>{
    const { cart , loading , error  } = useCart();
    const settings = useCartSettings();
    const intl = useIntl();
    const cartExists = Boolean(cart);
    const labels = React.useMemo(()=>({
            cartTitle: intl.getLabel('cart.title'),
            unavailableTitle: intl.getLabel('cart.unavailableItems.title')
        })
    , [
        intl
    ]);
    React.useEffect(()=>{
        if (cart === null || cart === void 0 ? void 0 : cart.validationErrors) {
            const errorCodes1 = Object.entries(cart.validationErrors).flatMap(([, errorCodes])=>{
                return errorCodes.map((code)=>validationErrorLabelMap[code]
                );
            });
            new Set(errorCodes1).forEach((code)=>{
                toast.failed(`cart.${code}`);
            });
        }
    }, [
        cart === null || cart === void 0 ? void 0 : cart.validationErrors
    ]);
    if (loading) {
        return /*#__PURE__*/ React.createElement("div", {
            className: "cart",
            "data-t": "loading-cart"
        }, /*#__PURE__*/ React.createElement(Loader$1, null));
    }
    const lineItems = (cart === null || cart === void 0 ? void 0 : cart.lineItems.filter((lineItem)=>{
        var ref;
        return !(cart === null || cart === void 0 ? void 0 : (ref = cart.validationErrors) === null || ref === void 0 ? void 0 : ref[lineItem.id]);
    })) || [];
    const unavailableLineItems = (cart === null || cart === void 0 ? void 0 : cart.lineItems.filter((lineItem)=>{
        var ref;
        return cart === null || cart === void 0 ? void 0 : (ref = cart.validationErrors) === null || ref === void 0 ? void 0 : ref[lineItem.id];
    })) || [];
    const cartEmpty = lineItems.length < 1 && unavailableLineItems.length < 1;
    if (!cartExists || error || cartEmpty) {
        return /*#__PURE__*/ React.createElement(EmptyCart, {
            onClick: onEmptyCartButtonClick
        });
    }
    const deliveryResolver = settings.deliveryResolverFactory && settings.deliveryResolverFactory(lineItems);
    return /*#__PURE__*/ React.createElement(Cart$1, {
        labels: labels,
        lineItems: lineItems,
        unavailableLineItems: unavailableLineItems,
        readOnly: settings.readOnly,
        deliveryResolver: deliveryResolver,
        policyItems: policyItems
    });
};

const Cart = (props)=>/*#__PURE__*/ React.createElement(CartContainer, swcHelpers.extends({}, props))
;

const CartCounter = ({ count  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, count > 0 && /*#__PURE__*/ React.createElement("span", {
        className: "cart-counter",
        "data-t": "cart-counter"
    }, count))
;

const CartButtonContainer = ({ href , className , label , tabIndex  })=>{
    const intl = useIntl();
    const [cart, { setCartCount , reset  }] = useCartStorage();
    const [getTotalLineItems] = useLazyQuery(GET_TOTAL_LINE_ITEMS, {
        onCompleted: (data)=>{
            const unsolvableErrorCodes = getUnrecoverableValidationErrors(data.getCart.validationErrors);
            if (unsolvableErrorCodes.length) {
                // If there are unsolvable validation errors, reset the cart
                reset();
                setCartCount(0);
                return;
            }
            setCartCount(data.getCart.cart.numberOfItems);
        }
    });
    const count = useReactiveVar(cartCountVar);
    const ariaLabel = React__default.useMemo(()=>{
        return count === 0 ? intl.getLabel('cart.emptyCart.title') : Util.getCartTotalsTitle(count, intl);
    }, [
        count,
        intl
    ]);
    useEffect(()=>{
        if (cart.cartId) {
            getTotalLineItems({
                variables: {
                    id: cart.cartId
                }
            });
        }
    }, [
        cart.cartId,
        getTotalLineItems
    ]);
    return /*#__PURE__*/ React__default.createElement("a", {
        className: classnames('cart-button', className),
        href: href,
        title: label,
        tabIndex: tabIndex,
        "aria-label": tabIndex === -1 ? '' : ariaLabel
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "shopping-bag"
    }), /*#__PURE__*/ React__default.createElement(CartCounter, {
        count: count
    }), label && /*#__PURE__*/ React__default.createElement("span", {
        className: "cart-button__label"
    }, label));
};

const CartButton = (props)=>/*#__PURE__*/ React.createElement(CartButtonContainer, swcHelpers.extends({}, props))
;

const CartSlider = ({ title , cart , children  })=>{
    const [isOpen, setIsOpen] = React.useState(false);
    const scrollHeight = React.useRef(0);
    const { currency  } = useIntl();
    const cartPrice = cart ? cart.prices.totalPrice : 0;
    var ref;
    const itemCount = (ref = cart === null || cart === void 0 ? void 0 : cart.numberOfItems) !== null && ref !== void 0 ? ref : 0;
    const handleButtonClick = ()=>{
        if (isOpen) {
            const distance = scrollHeight.current;
            const scrollTo = distance > window.pageYOffset ? window.pageYOffset : distance;
            window.scrollTo(0, Math.floor(scrollTo));
        } else {
            scrollHeight.current = window.pageYOffset;
        }
        setIsOpen(!isOpen);
    };
    return /*#__PURE__*/ React.createElement(motion.div, {
        className: classnames('cart-slider', {
            'cart-slider--open': isOpen
        }),
        animate: isOpen ? 'open' : 'close',
        initial: "close",
        "data-t": "cart-slider"
    }, /*#__PURE__*/ React.createElement(motion.div, {
        className: "cart-slider__content",
        variants: posedContent,
        transition: {
            duration: itemCount
        }
    }, children), /*#__PURE__*/ React.createElement("button", {
        className: "cart-slider__button",
        onClick: handleButtonClick,
        name: title
    }, /*#__PURE__*/ React.createElement("span", {
        className: "cart-slider__counter"
    }, /*#__PURE__*/ React.createElement(CartCounter$1, {
        count: itemCount
    })), /*#__PURE__*/ React.createElement("span", {
        className: "cart-slider__cart-label"
    }, title), /*#__PURE__*/ React.createElement(motion.span, {
        variants: posedPrice,
        className: "cart-slider__button-price",
        transition: {
            duration: itemCount
        }
    }, /*#__PURE__*/ React.createElement(Price, {
        value: cartPrice,
        currency: currency
    })), /*#__PURE__*/ React.createElement(motion.span, {
        variants: posedIcon,
        className: "cart-slider__icon",
        transition: {
            duration: itemCount
        }
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-down",
        className: classnames('cart-slider__chevron', {
            'cart-slider__chevron--rotated': isOpen
        })
    }))));
};
const transition = {
    duration: 0.4,
    ease: [
        0.33,
        1,
        0.68,
        1
    ]
};
const posedContent = {
    close: {
        height: 0,
        transition
    },
    open: {
        height: 'auto',
        transition
    }
};
const posedIcon = {
    close: {
        rotate: 0,
        transition
    },
    open: {
        rotate: 180,
        transition
    }
};
const posedPrice = {
    close: {
        opacity: 1,
        transition
    },
    open: {
        opacity: 0,
        transition
    }
};

const CartSliderContainer = ({ children  })=>{
    const { getLabel  } = useIntl();
    const { cart  } = useCart();
    return /*#__PURE__*/ React.createElement(CartSlider, {
        cart: cart,
        title: getLabel('cart.title')
    }, children);
};

const classPrefix$2 = 'mini-cart-v2';
const ActionButtons = ({ buttonsRef , labels , links , showStickyButtons ,  })=>{
    const Buttons = /*#__PURE__*/ React.createElement(React.Fragment, null, links.cart ? /*#__PURE__*/ React.createElement(Button, {
        "aria-label": labels.toCart,
        caption: labels.toCart,
        className: `${classPrefix$2}__action-button`,
        href: links.cart,
        size: "small"
    }) : null, links.checkout ? /*#__PURE__*/ React.createElement(Button, {
        "aria-label": labels.toCheckout,
        caption: labels.toCheckout,
        className: `${classPrefix$2}__action-button`,
        href: links.checkout,
        size: "small",
        variant: "outline"
    }) : null);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix$2}__action-buttons`,
        ref: buttonsRef
    }, Buttons), showStickyButtons ? /*#__PURE__*/ React.createElement(StickyCallToAction, {
        button: Buttons
    }) : null);
};

const CartTotals = ({ isMiniCart  })=>/*#__PURE__*/ React.createElement(Totals, {
        className: "mini-cart-v2__totals",
        showSubTotal: true,
        showTotalDiscount: true,
        hideSubscriptionExplainer: true,
        isMiniCart: isMiniCart
    })
;

const Product$1 = ({ body , imageUrl , price , title , quotation  })=>{
    return /*#__PURE__*/ React.createElement(LineItem, {
        className: "mini-cart-v2__product"
    }, /*#__PURE__*/ React.createElement(LineItemMedia, {
        className: "mini-cart-v2__product--image",
        type: "image",
        imageUrl: imageUrl,
        imageAlt: title
    }), /*#__PURE__*/ React.createElement(LineItemContent, {
        title: title,
        body: body
    }), /*#__PURE__*/ React.createElement(LineItemAside, {
        currency: price.currency,
        price: price.originalPrice,
        discountPrice: price.discountedPrice
    }), quotation.isEnabled && /*#__PURE__*/ React.createElement(Text, {
        className: "mini-cart-v2__product--quotation",
        component: "p",
        variant: "body"
    }, quotation.text));
};

const Title = ({ value  })=>{
    return /*#__PURE__*/ React.createElement(Heading, {
        className: "mini-cart-v2__title",
        variant: "smallest"
    }, value);
};

const ActionButtonsContainer = ({ links  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            toCart: getLabel('cart.actions.goToCart'),
            toCheckout: getLabel('cart.actions.goToCheckout')
        })
    , [
        getLabel
    ]);
    const [buttonsRef, buttonsInview] = useInView();
    const showStickyButtons = isSmallScreen && !buttonsInview;
    return /*#__PURE__*/ React.createElement(ActionButtons, {
        buttonsRef: buttonsRef,
        isSmallScreen: isSmallScreen,
        labels: labels,
        links: links,
        showStickyButtons: showStickyButtons
    });
};

const getLineItemBody = (lineItem, labels)=>{
    if (lineItem.type === 'contact-lenses') {
        const eyeSideLabel = lineItem.eyeSide === 'left' ? labels.leftEye : labels.rightEye;
        return `${labels.quantity}: ${lineItem.quantity} | ${eyeSideLabel}`;
    }
    if (lineItem.type === 'contact-lenses-bundle-benelux') {
        return labels.monthlySubscription;
    }
    if (lineItem.type === 'sunglasses' || lineItem.type === 'ready-readers' || lineItem.type === 'accessories') {
        const { model , modelCode  } = lineItem.brand;
        return `${model}${modelCode && modelCode !== model ? ` ${modelCode}` : ''}${lineItem.type === 'ready-readers' && (lineItem === null || lineItem === void 0 ? void 0 : lineItem.power) ? ` | ${labels.power}: ${lineItem.power}` : ''}`;
    }
    if (lineItem.type === 'solutions') {
        return `${lineItem.solutionType}, ${lineItem.solutionVolume}${labels.unitMilli}`;
    }
    // TODO: Add body text for prescription glasses
    return '';
};

const LineItemsContainer = ({ lineItems  })=>{
    const { getLabel  } = useIntl();
    const { isDownloadQuotationEnabled  } = useFeatureToggles();
    const labels = React.useMemo(()=>({
            monthlySubscription: getLabel('products.monthlySubscription'),
            quantity: getLabel('cart.item.quantity'),
            unitMilli: getLabel('products.unit.metric.liter.milli.abbr'),
            leftEye: getLabel('products.contactLenses.leftEye'),
            rightEye: getLabel('products.contactLenses.rightEye'),
            quotation: getLabel('products.contactLenses.quotationMiniCart'),
            power: getLabel('products.contactLenses.prescription.power')
        })
    , [
        getLabel
    ]);
    const products = React.useMemo(()=>lineItems.map((lineItem)=>({
                sku: lineItem.type === 'prescription-glasses' ? lineItem.frame.sku : lineItem.sku,
                imageUrl: lineItem.imageUrl,
                price: {
                    discountedPrice: lineItem.price.total.discount ? lineItem.price.total.gross : undefined,
                    originalPrice: lineItem.price.total.original,
                    currency: lineItem.currency
                },
                title: lineItem.name,
                quotation: {
                    isEnabled: lineItem.type === 'contact-lenses' && isDownloadQuotationEnabled,
                    text: labels.quotation
                },
                body: /*#__PURE__*/ React.createElement(Text, {
                    component: "span",
                    variant: "body"
                }, getLineItemBody(lineItem, labels))
            })
        )
    , [
        lineItems,
        isDownloadQuotationEnabled,
        labels
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, products.map((product)=>/*#__PURE__*/ React.createElement(Product$1, swcHelpers.extends({
            key: product.sku
        }, product))
    ));
};

const SuccessNotificationContainer = ()=>{
    const intl = useIntl();
    const labels = React.useMemo(()=>({
            successNotification: intl.getLabel('cart.success.notification')
        })
    , [
        intl
    ]);
    return /*#__PURE__*/ React.createElement(Alert, {
        icon: "checkmark",
        type: "success",
        message: labels.successNotification
    });
};

const TitleContainer = ()=>{
    const intl = useIntl();
    const { data  } = useGetCart();
    var _numberOfItems;
    const numberOfItems = (_numberOfItems = data === null || data === void 0 ? void 0 : data.getCart.cart.numberOfItems) !== null && _numberOfItems !== void 0 ? _numberOfItems : 0;
    const value = React.useMemo(()=>{
        return Util.getCartTotalsTitle(numberOfItems, intl);
    }, [
        numberOfItems,
        intl
    ]);
    return /*#__PURE__*/ React.createElement(Title, {
        value: value
    });
};

const classPrefix$1 = 'mini-cart-v2';
const MiniCart = ({ alerts =[] , children , components ={} , links , lineItems ,  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const Components = React.useMemo(()=>({
            ActionButtons: components.ActionButtons || ActionButtonsContainer,
            CartTotals: components.CartTotals || CartTotals,
            LineItems: components.LineItems || LineItemsContainer,
            SuccessNotification: components.SuccessNotification || SuccessNotificationContainer,
            Title: components.Title || TitleContainer
        })
    , [
        components
    ]);
    const HorizontalDivider = /*#__PURE__*/ React.createElement(Divider, {
        className: `${classPrefix$1}__divider`,
        orientation: "horizontal",
        theme: "light"
    });
    const childrenRenderResult = children && isFunction(children) ? children(lineItems) : children;
    if (isSmallScreen) {
        return /*#__PURE__*/ React.createElement(CartProvider, null, /*#__PURE__*/ React.createElement(Components.Title, null), HorizontalDivider, /*#__PURE__*/ React.createElement(Components.SuccessNotification, null), /*#__PURE__*/ React.createElement(Components.LineItems, {
            lineItems: lineItems
        }), HorizontalDivider, /*#__PURE__*/ React.createElement(Components.CartTotals, {
            isMiniCart: true
        }), /*#__PURE__*/ React.createElement(Components.ActionButtons, {
            links: links
        }), alerts.length ? /*#__PURE__*/ React.createElement("div", {
            className: `${classPrefix$1}__alerts`
        }, alerts) : null, childrenRenderResult);
    }
    return /*#__PURE__*/ React.createElement(CartProvider, null, /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix$1}__box`
    }, /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix$1}__added-item`
    }, /*#__PURE__*/ React.createElement(Components.SuccessNotification, null), /*#__PURE__*/ React.createElement(Components.LineItems, {
        lineItems: lineItems
    }))), /*#__PURE__*/ React.createElement(Divider, {
        className: `${classPrefix$1}__divider--vertical`,
        orientation: "vertical",
        theme: "light"
    }), /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix$1}__box`
    }, /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix$1}__basket`
    }, /*#__PURE__*/ React.createElement(Components.Title, null), HorizontalDivider, /*#__PURE__*/ React.createElement(Components.CartTotals, {
        isMiniCart: true
    }), HorizontalDivider, /*#__PURE__*/ React.createElement(Components.ActionButtons, {
        links: links
    }))), childrenRenderResult && /*#__PURE__*/ React.createElement(Divider, {
        className: `${classPrefix$1}__divider`,
        orientation: "horizontal",
        theme: "light"
    }), alerts.length ? /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix$1}__alerts`
    }, alerts) : null, childrenRenderResult);
};

var ModalContainer = (_param)=>{
    var { modal  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "modal"
    ]);
    const [lineItems, setLineItems] = React.useState(undefined);
    useMiniCartListener((event)=>{
        if (!lineItems || event.detail.type === 'updated') {
            setLineItems(event.detail.lineItems);
        }
    });
    const handleClose = ()=>{
        setLineItems(undefined);
        (modal === null || modal === void 0 ? void 0 : modal.onModalClose) && modal.onModalClose();
    };
    var ref, ref1;
    return /*#__PURE__*/ React.createElement(Modal, swcHelpers.extends({}, modal, {
        className: classnames('mini-cart-v2', modal === null || modal === void 0 ? void 0 : modal.className),
        isOpen: (ref = !!lineItems || (modal === null || modal === void 0 ? void 0 : modal.isOpen)) !== null && ref !== void 0 ? ref : false,
        onModalClose: handleClose,
        showIcon: (ref1 = modal === null || modal === void 0 ? void 0 : modal.showIcon) !== null && ref1 !== void 0 ? ref1 : true
    }), lineItems ? /*#__PURE__*/ React.createElement(MiniCart, swcHelpers.extends({
        lineItems: lineItems
    }, rest)) : null);
};

const MiniCartModal = (props)=>{
    return /*#__PURE__*/ React.createElement(ModalContainer, swcHelpers.extends({}, props));
};

const useReplaceLineItemContactLensesBundleBenelux = ()=>{
    const intl = useIntl();
    const settings = useCartSettings();
    const [cart] = useCartStorage();
    const [replaceCartLineItem, replaceCartLineItemMutation] = useMutation(REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM);
    const handleOnCompleted = React__default.useCallback((replacedLineItem, data, onSuccess, onValidationError, onError)=>{
        const result = data.replaceContactLensBundleBeneluxCartLineItem;
        if (!result.success) {
            const validationErrors = getValidationErrorCodes(result.validationErrors);
            onValidationError && onValidationError(validationErrors);
            return;
        }
        if (!result.cart) {
            if (onError) {
                onError(errorsMapping.CART_IS_EMPTY);
            }
            return;
        }
        const lineItemsInCart = result.cart.lineItems;
        const addedLineItems = lineItemsInCart.filter(({ sku  })=>sku === replacedLineItem.newSku
        );
        const addedLineItemsMappedToViewModel = lineItemDataMapper(addedLineItems, settings, intl);
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.add, addedLineItemsMappedToViewModel, result.cart.id);
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
        dispatchMiniCartEvent('updated', addedLineItemsMappedToViewModel);
        const SdProductObjects = addedLineItemsMappedToViewModel.map(createProductObject);
        CartV2Actions.addToCartSdAction(SdProductObjects);
        onSuccess && onSuccess(addedLineItemsMappedToViewModel);
    }, [
        intl,
        settings
    ]);
    const replaceLineItem = React__default.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(lineItemDrafts, onSuccess, onValidationError, onError) {
            if (!cart.cartId) {
                if (onError) {
                    onError(errorsMapping.MISSING_CART_ID);
                }
                return;
            }
            try {
                const { data , errors  } = yield replaceCartLineItem({
                    variables: {
                        cartInput: {
                            cartId: cart.cartId,
                            lineItemToReplace: lineItemDrafts
                        }
                    }
                });
                if (errors) {
                    if (onError) {
                        onError(errorsMapping.SOMETHING_WENT_WRONG);
                    }
                }
                if (data) {
                    handleOnCompleted(lineItemDrafts, data, onSuccess, onValidationError, onError);
                }
            } catch (error) {
                if (onError) {
                    onError(errorsMapping.SOMETHING_WENT_WRONG);
                }
            }
        });
        return function(lineItemDrafts, onSuccess, onValidationError, onError) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart.cartId,
        handleOnCompleted,
        replaceCartLineItem
    ]);
    const { called , loading , error , data: data1  } = replaceCartLineItemMutation;
    var _cart;
    const newCart = (_cart = data1 === null || data1 === void 0 ? void 0 : data1.replaceContactLensBundleBeneluxCartLineItem.cart) !== null && _cart !== void 0 ? _cart : null;
    return [
        replaceLineItem,
        {
            called,
            loading,
            error,
            cart: newCart
        }
    ];
};

const baseClassName = 'clbb-solution-picker';

const Loader = ()=>/*#__PURE__*/ React.createElement(Loader$1, {
        className: `${baseClassName}__loader`
    })
;

const Description = ({ value  })=>/*#__PURE__*/ React.createElement("p", {
        className: `${baseClassName}__description`
    }, value)
;

const DescriptionContainer = ({ description  })=>{
    const { locale  } = useIntl();
    const value = description && getLocalizedValue(description, locale);
    if (!value) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Description, {
        value: value
    });
};

const className = `${baseClassName}-item`;
const Solution = ({ isChecked , isLoading , onChange , solution , title  })=>{
    const { bundleSku , bundlePrice , description , imageUrl  } = solution;
    return /*#__PURE__*/ React__default.createElement("div", {
        className: `${className}__wrapper`
    }, /*#__PURE__*/ React__default.createElement(RadioButton, {
        isChecked: isChecked,
        onChange: onChange,
        position: "bottom",
        value: bundleSku,
        disabled: isLoading,
        className: classnames(className, {
            [`${className}--empty`]: !description && !imageUrl
        })
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: `${className}__container`
    }, imageUrl && /*#__PURE__*/ React__default.createElement("div", {
        className: `${className}__image-holder`
    }, /*#__PURE__*/ React__default.createElement("img", {
        className: `${className}__image`,
        src: imageUrl,
        alt: title
    })), /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "small",
        className: `${className}__title`
    }, title), bundlePrice[0].value && /*#__PURE__*/ React__default.createElement("span", {
        className: `${className}__price`
    }, /*#__PURE__*/ React__default.createElement(Price, {
        value: bundlePrice[0].value.centAmount,
        currency: bundlePrice[0].value.currencyCode
    })))));
};

var SolutionContainer = (_param)=>{
    var { solution  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "solution"
    ]);
    const { getLabel , locale  } = useIntl();
    const { isBynderImagesEnabled  } = useFeatureToggles();
    const solutionProduct = React__default.useMemo(()=>{
        var ref;
        const imageUrl = isBynderImagesEnabled ? `${getFrontImage((ref = solution.media) === null || ref === void 0 ? void 0 : ref.images).url}?w=300&auto=format` : solution.imageUrl;
        return swcHelpers.objectSpread({}, solution, {
            imageUrl
        });
    }, [
        solution,
        isBynderImagesEnabled
    ]);
    const title = solutionProduct.name ? getLocalizedValue(solutionProduct.name, locale) : getLabel('products.contactLenses.solutions.missing');
    return /*#__PURE__*/ React__default.createElement(Solution, swcHelpers.extends({}, props, {
        solution: solutionProduct,
        title: title
    }));
};

const SolutionPicker = ({ activeSolution , handleClick , isLoading , solutions  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: baseClassName
    }, /*#__PURE__*/ React.createElement("div", {
        className: `${baseClassName}__list`
    }, solutions.map((solution)=>/*#__PURE__*/ React.createElement(SolutionContainer, {
            isChecked: activeSolution ? solution.bundleSku === activeSolution.bundleSku : false,
            isLoading: isLoading,
            key: solution.bundleSku,
            onChange: handleClick,
            solution: solution
        })
    )), /*#__PURE__*/ React.createElement(DescriptionContainer, {
        description: activeSolution === null || activeSolution === void 0 ? void 0 : activeSolution.description
    }));
};

const useFetchBundleSolutions = (sku)=>{
    const { productsUrl  } = useApiConfig();
    const params = React__default.useMemo(()=>({
            sku
        })
    , [
        sku
    ]);
    const [loading, data] = useApiClient(Products.getContactLensBundles, {}, productsUrl, params);
    return [
        loading,
        data === null || data === void 0 ? void 0 : data.solutions
    ];
};

const ContactLensesBundleBeneluxSolutionsContainer = ({ subscriptionProductSku  })=>{
    const [bundleSku, setBundleSku] = React.useState(subscriptionProductSku);
    const [isFetching, solutions] = useFetchBundleSolutions(subscriptionProductSku);
    const activeSolution = React.useMemo(()=>{
        return solutions === null || solutions === void 0 ? void 0 : solutions.find((solution)=>solution.bundleSku === bundleSku
        );
    }, [
        bundleSku,
        solutions
    ]);
    const [replaceLineItem, { loading  }] = useReplaceLineItemContactLensesBundleBenelux();
    const handleClick = React.useCallback((ev)=>{
        const newSku = ev.currentTarget.value;
        const existingSku = bundleSku;
        const draft = {
            newSku,
            existingSku
        };
        const onSuccess = ()=>{
            setBundleSku(newSku);
            toast.info('cart.notification.updatedCart');
        };
        const handleError = (message)=>{
            const errorMessages = isArray(message) ? message : [
                message
            ];
            errorMessages.forEach((errorMessage)=>toast.failed(`cart.${errorMessage}`)
            );
        };
        replaceLineItem(draft, onSuccess, handleError, handleError);
    }, [
        bundleSku,
        replaceLineItem
    ]);
    if (isFetching) {
        return /*#__PURE__*/ React.createElement(Loader, null);
    }
    if (!solutions) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(SolutionPicker, {
        activeSolution: activeSolution,
        handleClick: handleClick,
        isLoading: loading,
        solutions: solutions
    });
};

const ContactLensesBundleBeneluxSolutions = (props)=>{
    return /*#__PURE__*/ React.createElement(ContactLensesBundleBeneluxSolutionsContainer, swcHelpers.extends({}, props));
};

const AddButton = (props)=>/*#__PURE__*/ React.createElement(Button, swcHelpers.extends({
        size: "custom",
        "data-t": "add-button",
        className: "add-button",
        variant: "ghost",
        color: "primary"
    }, props))
;

const AddButtonSuccessMessage = ({ caption  })=>/*#__PURE__*/ React.createElement("div", {
        className: "add-button-success-message"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "add-button-success-message__text"
    }, caption))
;

const classPrefix = 'upsell-item-product';
const Product = ({ body , price , title , imageUrl , tag  })=>/*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix}`
    }, /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix}__header`
    }, tag && /*#__PURE__*/ React.createElement(ProductTag, {
        tag: tag
    })), /*#__PURE__*/ React.createElement("div", {
        className: `${classPrefix}__image-container`
    }, /*#__PURE__*/ React.createElement("img", {
        className: `${classPrefix}__image`,
        src: `${imageUrl}?w=300&auto=format`,
        alt: title
    })), /*#__PURE__*/ React.createElement(Text, {
        className: `${classPrefix}__title`,
        variant: "body-head"
    }, title), body && /*#__PURE__*/ React.createElement(Text, {
        className: `${classPrefix}__subtitle`,
        variant: "caption"
    }, body), /*#__PURE__*/ React.createElement(PriceTag, {
        className: `${classPrefix}__price`,
        originalPrice: price.originalPrice,
        discountedPrice: price.discountedPrice,
        currency: price.currency
    }))
;

const RemoveButton = (props)=>/*#__PURE__*/ React.createElement(Button, swcHelpers.extends({
        size: "custom",
        "data-t": "remove-button",
        className: "remove-button",
        variant: "ghost"
    }, props))
;

const UpsellLayout = (props)=>/*#__PURE__*/ React.createElement("div", {
        className: "upsell-layout"
    }, /*#__PURE__*/ React.createElement(Heading, {
        className: "upsell-layout__header",
        variant: "smallest",
        component: "h2"
    }, props.heading), props.children)
;

const RemoveButtonContainer = ({ lineItem , onSuccess  })=>{
    const [removeFromCart, { loading  }] = useRemoveFromCart(lineItem, onSuccess);
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            removeFromCart: getLabel('cart.actions.removeFromCart')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(RemoveButton, {
        isLoading: loading,
        caption: labels.removeFromCart,
        onClick: removeFromCart
    });
};

const AddButtonContainer = ({ lineItemDrafts , onSuccess  })=>{
    const { getLabel  } = useIntl();
    const [addToCart, { loading  }] = useAddToCart();
    const labels = React.useMemo(()=>({
            addToCart: getLabel('cart.actions.addToCart')
        })
    , [
        getLabel
    ]);
    const handOnClick = React.useCallback(()=>addToCart(lineItemDrafts, onSuccess)
    , [
        lineItemDrafts,
        onSuccess, 
    ]);
    return /*#__PURE__*/ React.createElement(AddButton, {
        isLoading: loading,
        caption: labels.addToCart,
        onClick: handOnClick
    });
};

const AddButtonSuccessMessageContainer = ()=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            addedToCart: getLabel('cart.notification.addedToCart')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(AddButtonSuccessMessage, {
        caption: labels.addedToCart
    });
};

const UpsellItemContainer = (item)=>{
    const [addedLineItem, setAddedItem] = React.useState(null);
    const [successMessage, setSuccessMessage] = React.useState(false);
    const itemDraft = {
        sku: item.sku,
        quantity: 1,
        type: 'regular'
    };
    const onAddedSuccess = React.useCallback(([lineItem])=>{
        setSuccessMessage(true);
        setTimeout(()=>{
            setSuccessMessage(false);
        }, 2000);
        setAddedItem(lineItem);
    }, [
        setSuccessMessage,
        setAddedItem
    ]);
    const onRemovedSuccess = React.useCallback(()=>{
        setAddedItem(null);
    }, [
        setAddedItem
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('upsell-item', {
            'upsell-item--is-added': addedLineItem
        })
    }, addedLineItem && /*#__PURE__*/ React.createElement(Icon, {
        name: "checkmark-circle",
        className: "upsell-item__checkmark"
    }), /*#__PURE__*/ React.createElement(Product, {
        body: item.body,
        price: item.price,
        title: item.title,
        imageUrl: item.imageUrl,
        tag: item.tag
    }), /*#__PURE__*/ React.createElement("div", {
        className: "upsell-item__action-button"
    }, !addedLineItem && /*#__PURE__*/ React.createElement(AddButtonContainer, {
        lineItemDrafts: [
            itemDraft
        ],
        onSuccess: onAddedSuccess
    }), addedLineItem && !successMessage && /*#__PURE__*/ React.createElement(RemoveButtonContainer, {
        lineItem: addedLineItem,
        onSuccess: onRemovedSuccess
    }), successMessage && /*#__PURE__*/ React.createElement(AddButtonSuccessMessageContainer, null)));
};

function getProductTag(tag, locale) {
    if (!tag) return null;
    return {
        key: tag.key,
        value: typeof tag.value == 'string' ? tag.value : tag.value[locale]
    };
}

const getPrice = (prices)=>{
    if (!Array.isArray(prices) || !prices.length) {
        return undefined;
    }
    const price = prices[0];
    if (!price.discounted) {
        return {
            originalPrice: price.value.centAmount,
            currency: Currencies[price.value.currencyCode]
        };
    }
    const discounted = price.discounted;
    return {
        currency: Currencies[price.value.currencyCode],
        originalPrice: price.value.centAmount,
        discountedPrice: discounted.value.centAmount
    };
};

/**
 * From the API returned upsell data, create objects that can be processed
 * by the Upsell components
 */ const parseCTUpsellData = (upsellProducts, locale, staticsUrl, getLabel, isBynderImagesEnabled, isProductLabelEnabled)=>{
    if (!upsellProducts || upsellProducts.length === 0) {
        return [];
    }
    return upsellProducts.reduce((storedUpsellProducts, solution, index)=>{
        var ref;
        const { masterVariant , name  } = solution;
        const price = getPrice(masterVariant.prices);
        if (price === undefined || index > 3) {
            return storedUpsellProducts;
        }
        const title = getLocalizedValue(name, locale);
        var _sku;
        const sku = (_sku = masterVariant.sku) !== null && _sku !== void 0 ? _sku : '';
        const imageUrl = getLineItemImageUrl(masterVariant, staticsUrl, isBynderImagesEnabled);
        const solutionType = getAttrValue(masterVariant, 'solutionType');
        const volume = getAttrValue(masterVariant, 'volume');
        const tag = !isProductLabelEnabled ? getProductTag((ref = masterVariant.tags) === null || ref === void 0 ? void 0 : ref[0], locale) : undefined;
        const availability = masterVariant.availability;
        const channelsMap = (availability === null || availability === void 0 ? void 0 : availability.channels) ? Object.entries(availability.channels) : [];
        const hasAvailableQuantity = channelsMap.some(([, channel])=>channel.availableQuantity
        );
        // When no availablity on product variant this item should still be orderable
        const isAvailable = !availability ? true : hasAvailableQuantity;
        if (!isAvailable) {
            return storedUpsellProducts;
        }
        let body;
        if (solutionType && volume) {
            body = `${solutionType}, ${volume}${getLabel('products.unit.metric.liter.milli.abbr')}`;
        }
        const product = {
            title,
            body,
            imageUrl,
            sku,
            price,
            tag
        };
        return [
            ...storedUpsellProducts,
            product
        ];
    }, []);
};

const UpsellCategoryContainer = ({ categoryKey  })=>{
    const { productsUrl  } = useApiConfig();
    const { staticsUrl  } = useCartSettings();
    const { getLabel , locale  } = useIntl();
    const { isBynderImagesEnabled , isProductLabelEnabled  } = useFeatureToggles();
    const [upsellProducts, setUpsellProducts] = React.useState([]);
    /**
   * Fetch and parse upsell products for a given category key.
   */ let cancelled = false;
    React.useEffect(()=>{
        const fetchUpsellProducts = function() {
            var _ref = swcHelpers.asyncToGenerator(function*() {
                try {
                    const { results  } = yield Products.query(productsUrl, {
                        categoryType: categoryKey
                    });
                    if (cancelled) {
                        return;
                    }
                    const parsedProducts = parseCTUpsellData(results, locale, staticsUrl, getLabel, isBynderImagesEnabled, isProductLabelEnabled);
                    setUpsellProducts(parsedProducts);
                } catch (e) {
                    console.error(`Error fetching upsell products ${e.message}`);
                }
            });
            return function fetchUpsellProducts() {
                return _ref.apply(this, arguments);
            };
        }();
        fetchUpsellProducts();
        return ()=>{
            cancelled = true;
        };
    }, [
        productsUrl,
        categoryKey,
        setUpsellProducts
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, upsellProducts.map((product)=>/*#__PURE__*/ React.createElement(UpsellItemContainer, swcHelpers.extends({
            key: product.sku
        }, product))
    ));
};

const UpsellCategory = ({ recommendedCategoryKey  })=>/*#__PURE__*/ React.createElement("div", {
        className: "upsell"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "upsell__content"
    }, /*#__PURE__*/ React.createElement(UpsellCategoryContainer, {
        categoryKey: recommendedCategoryKey
    })))
;

const UpsellProducts = ({ products  })=>/*#__PURE__*/ React.createElement("div", {
        className: "upsell"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "upsell__content"
    }, products.map((product)=>/*#__PURE__*/ React.createElement(UpsellItemContainer, swcHelpers.extends({
            key: product.sku
        }, product))
    )))
;

// FYI: Many of these labels haven't been implemented in Bloomreach yet
const productTypeSpecificMessageLabelMapping = {
    'contact-lenses-bundle-benelux': 'cart.notification.upsell.contactLensesBundleBenelux.message',
    'contact-lenses': 'cart.notification.upsell.contactLenses.message',
    'contact-lenses-subscription': 'cart.notification.upsell.contactLensesSubscription.message',
    'prescription-glasses': 'cart.notification.upsell.prescriptionGlasses.message',
    'non-prescription-bundle': 'cart.notification.upsell.nonRxBundle.message',
    frames: 'cart.notification.upsell.frames.message',
    solutions: 'cart.notification.upsell.solution.message',
    sunglasses: 'cart.notification.upsell.sunglasses.message',
    'ready-readers': 'cart.notification.upsell.readyReaders.message',
    accessories: 'cart.notification.upsell.accessories.message'
};
const ProductTypeMessages = ({ lineItemTypes  })=>{
    const { getLabel  } = useIntl();
    const messages = lineItemTypes.map((t)=>getLabel(productTypeSpecificMessageLabelMapping[t], {
            hideLabelKey: true
        })
    ).filter(Boolean);
    if (messages.length <= 0) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Alert, {
        message: [
            ...messages
        ]
    });
};
const UpsellContainer = ({ lineItems , products , recommendedCategoryKey , subscriptionProductSku ,  })=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            upsellCategoryHeading: getLabel('cart.notification.productAdded'),
            clbbCategoryHeading: getLabel('products.contactLenses.solutions.title')
        })
    , [
        getLabel
    ]);
    const lineItemTypes = [
        ...new Set(lineItems.map((l)=>l.type
        ))
    ];
    if (subscriptionProductSku && lineItems.some((lineItem)=>lineItem.type === 'contact-lenses-bundle-benelux'
    )) {
        return /*#__PURE__*/ React.createElement(UpsellLayout, {
            heading: labels.clbbCategoryHeading
        }, /*#__PURE__*/ React.createElement(ProductTypeMessages, {
            lineItemTypes: lineItemTypes
        }), /*#__PURE__*/ React.createElement(ContactLensesBundleBeneluxSolutions, {
            subscriptionProductSku: subscriptionProductSku
        }));
    }
    if (recommendedCategoryKey) {
        return /*#__PURE__*/ React.createElement(UpsellLayout, {
            heading: labels.upsellCategoryHeading
        }, /*#__PURE__*/ React.createElement(ProductTypeMessages, {
            lineItemTypes: lineItemTypes
        }), /*#__PURE__*/ React.createElement(UpsellCategory, {
            recommendedCategoryKey: recommendedCategoryKey
        }));
    }
    if (products) {
        return /*#__PURE__*/ React.createElement(UpsellLayout, {
            heading: labels.upsellCategoryHeading
        }, /*#__PURE__*/ React.createElement(ProductTypeMessages, {
            lineItemTypes: lineItemTypes
        }), /*#__PURE__*/ React.createElement(UpsellProducts, {
            products: products
        }));
    }
    return null;
};

const Upsell = (props)=>/*#__PURE__*/ React.createElement(UpsellContainer, swcHelpers.extends({}, props))
;

const Quotation$1 = ({ url , labels , showLink =false  })=>/*#__PURE__*/ React__default.createElement("div", {
        className: "line-item-quotation"
    }, showLink ? /*#__PURE__*/ React__default.createElement("a", {
        className: "line-item-quotation__link",
        href: url
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "printer"
    }), /*#__PURE__*/ React__default.createElement("span", null, labels.print)) : /*#__PURE__*/ React__default.createElement("p", {
        className: "line-item-quotation__label"
    }, labels.caption))
;

const Quotation = ({ type , showLink  })=>{
    const { cart  } = useCart();
    const { locale , getLabel  } = useIntl();
    const { apiUrl  } = useApiConfig();
    if (!apiUrl || !(cart === null || cart === void 0 ? void 0 : cart.id)) {
        return null;
    }
    const labels = {
        caption: getLabel('products.contactLenses.quotation'),
        print: getLabel('products.contactLenses.quotationPrint')
    };
    const downloadPdfUrl = `${apiUrl}/quotes/carts/${cart.id}?productTypes=${type}&locale=${locale}`;
    return /*#__PURE__*/ React__default.createElement(Quotation$1, {
        labels: labels,
        url: downloadPdfUrl,
        showLink: showLink
    });
};

export { AddToCart, Cart, CartButton, CartSliderContainer as CartSlider, CartSlotsProvider, index$2 as Context, EmptyCart, index$1 as Hooks, LineItems, index as Mappers, MiniCart, MiniCartModal, index$3 as Models, index$4 as Operations, Quotation, TYPE_DETAILS_EYE_SIDE, Upsell, appendSanitizationItems, cartCountVar, cartIdVar, createContainsHelper, extractSkuQuantityPairsFromLineItems, getActiveVoucherCodes, getAddedLineItems, getAddress, getAttribute, getCalculatedMaximumQuantity, getCartPrices, getContactLensSpecs, getCustomerDetails, getDiscountDescriptions, getDisplayType, getLineItemAvailability, getLineItemDiscount, getLineItemImageUrl, getLineItemName, getLineItemPrice, getLocalizedAttribute, getProductUrl, getShippingMethod, getSolvableValidationErrors, getSubscriptionDetails, getUnrecoverableValidationErrors, getValidationErrorCodes, hasLineItemTypes, isContactLensesSubscriptionLineItem, lineItemDataMapper, validationErrorLabelMap };
//# sourceMappingURL=index.js.map
